import type { ImportedCogoXyzApp } from "./cogo-xyz-import";
import { getStudioAccessToken } from "@/services/auth-provider";
import {
  buildFigmaTemplateLiveSourceMappingProof,
  type FigmaTemplateLiveSourceMappingReport,
} from "@cogo/headless-core/figma-template-live-source-mapping";
import {
  buildFigmaTemplateReferenceParity,
  type FigmaTemplateCompiledNode,
  type FigmaTemplateReferenceParityReport,
} from "@cogo/headless-core/figma-template-reference-parity";
import type { FigmaTemplateQualityReadiness } from "@cogo/headless-core/figma-template-ontology-benchmark";
import {
  figmaApiNodeToTemplateNode,
  type FigmaApiNode,
  type FigmaApiNodesResponse,
} from "@cogo/headless-core/figma-template-live-ingest";
import {
  buildFigmaTemplateEnforcementContract,
  buildFigmaTemplateSearchRepresentation,
  chooseLayoutGenerationBoundary,
  scoreFigmaTemplateMatch,
  type FigmaTemplateMatchQuery,
} from "./figma-template-ontology-matching";
import { canonicalFigmaSourceSha256 } from "../../../shared/figma-source-integrity";

type CogoElementRecord = Record<string, unknown>;
type CogoInlineStyle = Record<string, unknown>;
type FigmaDesignTokenBinding = {
  tokenRef: string;
  resolvedValue: string | number;
};
type FigmaDesignTokenBindings = Record<string, FigmaDesignTokenBinding>;
type ComponentSlotBinding = Record<string, unknown>;
type ComponentSlotBindings = Record<string, ComponentSlotBinding>;

const FIGMA_TEMPLATE_APPLICATION_SCHEMA_VERSION =
  "cogo.figma-frame-design-template-application.v1";
const FIGMA_TEMPLATE_ONTOLOGY_TRACE_SCHEMA_VERSION =
  "studio.figma-template-ontology-retrieval-trace.v1";
const FIGMA_TEMPLATE_ONTOLOGY_PLAN_SCHEMA_VERSION =
  "studio.figma-template-ontology-matching-plan.v1";
const FIGMA_TEMPLATE_COMPILER_CONTRACT_SCHEMA_VERSION =
  "studio.figma-template-ontology-compiler-contract.v1";
const FIGMA_TEXT_BASELINE_OFFSET_PX: number = 1;

export interface FigmaFrameImportInput {
  figmaFileId: string;
  figmaNodeId: string;
  figmaAccessToken?: string;
  figmaTokenRef?: string;
  workspaceId?: string;
  projectId?: string;
  figmaUrl?: string;
  audience?: string;
  tags?: string[];
  templateSelection?: FigmaTemplateMatchQuery & {
    userIntent: string;
    scope: NonNullable<FigmaTemplateMatchQuery["scope"]>;
  };
  selectionConfirmation?: {
    schemaVersion: "cogo.human-template-selection-confirmation.v1";
    actorId: string;
    confirmationId: string;
    approvedAt: string;
    tool: "cogo.studio.import_figma_frame";
    projectId: string;
  };
  sourceRevision?: string;
  sourceSha256?: string;
  // When provided, skip the live Figma API call and convert from this already-fetched
  // `/v1/files/:key/nodes` response. Enables offline/CI conversion of real Figma trees
  // (fixtures) without re-hitting the rate-limited API. Default undefined = fetch live.
  prefetchedNodesResponse?: FigmaApiNodesResponse;
}

interface FigmaOntologyReadbackResult {
  schemaVersion: "cogo.figma-ontology-readback-result.v1";
  status: "hit";
  source: "cloudflare-d1-r2-derived-readback" | "static-studio-asset";
  fallbackReason?: string;
  job: {
    jobId: string;
    tenantId?: string;
    projectId?: string;
    templateId: string;
    figmaFileKey: string;
    figmaNodeId: string;
    sourceRevision?: string;
    rawSha256?: string;
    sourceSha256?: string;
    artifactSetSha256?: string;
    r2Prefix: string;
    pageCount: number;
    chunkCount: number;
    updatedAt?: string;
  };
  artifacts: Array<{
    kind: "raw-figma-json" | "cogo-intermediate" | "ontology-seed";
    r2Key: string;
    sha256?: string;
    byteLength?: number;
  }>;
  cogoIntermediate: Record<string, unknown>;
  ontologySeed: Record<string, unknown>;
}

export interface FigmaSourceOntologyStorage {
  schemaVersion: "cogo.figma-source-ontology-storage.v1";
  status: "readback-hit" | "persisted" | "dry-run" | "skipped" | "failed";
  reused: boolean;
  source:
    | "cloudflare-d1-r2-derived-readback"
    | "static-studio-asset"
    | "ontology-import-api";
  jobId?: string;
  tenantId?: string;
  projectId?: string;
  templateId?: string;
  figmaFileKey?: string;
  figmaNodeId?: string;
  sourceRevision?: string;
  rawSha256?: string;
  sourceSha256?: string;
  artifactSetSha256?: string;
  r2Prefix?: string;
  chunkCount?: number;
  artifactCount?: number;
  reason?: string;
  error?: string;
}

export interface FigmaSourceFrameSummary {
  schemaVersion: "cogo.figma-source-frame.v1";
  platform: "mobile" | "web";
  width: number;
  height: number;
  figmaFileId: string;
  figmaNodeId: string;
  frameName: string;
}

export interface FigmaSourceFrameSelectionSummary {
  schemaVersion: "cogo.figma-source-frame-selection.v1";
  requestedNodeId: string;
  selectedNodeId: string;
  selectedFrameName: string;
  reason: "requested-node" | "mobile-frame-inside-section";
  candidateCount: number;
}

interface CachedVisualLockPage {
  pageIndex: number;
  name?: string;
  sourceNodeId?: string;
  route?: string;
  width: number;
  height: number;
  figmaWidth?: number;
  figmaHeight?: number;
  dataUrl?: string;
  imageUrl?: string;
  source?: string;
  editableComponents?: Array<Record<string, unknown>>;
}

interface CompiledVisualLockSummary {
  source: string;
  pageCount: number;
  appliedPage: Omit<CachedVisualLockPage, "dataUrl" | "imageUrl"> & {
    imageUrl?: string;
  };
}

interface EditableCompilerProof {
  schemaVersion: "cogo.figma-editable-compiler-proof.v1";
  status: "pass" | "warn" | "fail";
  source: string;
  editableNodeCount: number;
  textNodeCount: number;
  interactiveNodeCount: number;
  componentKinds: string[];
  visualLockRequired: boolean;
  thresholds: {
    passEditableNodeCount: number;
    passTextNodeCount: number;
    passInteractiveNodeCount: number;
  };
  reasons: string[];
}

interface VisualDiffGateProof {
  schemaVersion: "cogo.figma-source-visual-diff-gate.v1";
  status: "pass" | "warn" | "fail";
  mode:
    | "visual-lock-raster-authority"
    | "pending-rendered-component-diff"
    | "raster-backed-editable-overlay-diff";
  diffRatio: number | null;
  rawDiffPixels?: number;
  rawDiffRatio?: number;
  threshold: number;
  sourceRasterImageUrl?: string;
  generatedRasterImageUrl?: string;
  width?: number;
  height?: number;
  measuredBy: string;
  reasons: string[];
}

export type VisibleCogoCompilerRenderMode =
  | "visual-lock"
  | "visible-cogo-candidate"
  | "raster-backed-editable-overlay";

type VisibleCogoCompilerPrimaryAuthority =
  | "visual-lock"
  | "visible-cogo-components"
  | "raster-backed-editable-overlay";

type EditableOntologyIdentityStatus = "pass" | "blocked";
type DesignSystemTokenCategory =
  | "color"
  | "type"
  | "radius"
  | "shadow"
  | "spacing"
  | "density";

interface TokenCategoryCoverage {
  total: number;
  tokenRef: number;
  raw: number;
  coverageRatio: number;
}

interface RawStyleViolation {
  nodeId?: string;
  nodeName?: string;
  cogoKind?: string;
  category: DesignSystemTokenCategory;
  property: string;
  value: string;
  requiredTokenPrefix: string;
  violation:
    | "missing-token-binding"
    | "token-category-mismatch"
    | "resolved-value-mismatch";
  tokenRef?: string;
  resolvedValue?: string;
}

interface DesignSystemIdentityProof {
  schemaVersion: "cogo.figma-design-system-identity-proof.v1";
  status: EditableOntologyIdentityStatus;
  selectedDesignSystemId?: string;
  nodeCount: number;
  tokenCoverageByCategory: Record<
    DesignSystemTokenCategory,
    TokenCategoryCoverage
  >;
  rawStyleViolationCount: number;
  rawStyleViolations: RawStyleViolation[];
  reasons: string[];
}

interface ComponentIdentityViolation {
  nodeId?: string;
  nodeName?: string;
  cogoKind?: string;
  missing: Array<"componentOntologyId" | "variantId" | "slotBindings">;
}

interface ComponentIdentityProof {
  schemaVersion: "cogo.figma-component-identity-proof.v1";
  status: EditableOntologyIdentityStatus;
  componentNodeCount: number;
  authoritativeNodeCount: number;
  cogoKindOnlyNodeCount: number;
  missingIdentityCount: number;
  requiredFields: ["componentOntologyId", "variantId", "slotBindings"];
  missingIdentitySamples: ComponentIdentityViolation[];
  reasons: string[];
}

interface VisibleCogoCompilerProof {
  schemaVersion: "cogo.figma-visible-cogo-compiler-proof.v1";
  stage: "stage3-visible-cogo-ui-compiler";
  status: "pass" | "warn" | "fail";
  renderMode: VisibleCogoCompilerRenderMode;
  primaryRenderAuthority: VisibleCogoCompilerPrimaryAuthority;
  commercializationReady: boolean;
  commercialPreviewReady: boolean;
  editablePrimaryPromotionReady: boolean;
  candidateNodeCount: number;
  visibleTextNodeCount: number;
  styledNodeCount: number;
  interactiveNodeCount: number;
  styleCoverageRatio: number;
  editableToVisibleRatio: number;
  visualDiffMeasured: boolean;
  editableOntologyIdentityStatus: EditableOntologyIdentityStatus;
  designSystemIdentityProof: DesignSystemIdentityProof;
  componentIdentityProof: ComponentIdentityProof;
  thresholds: {
    minCandidateNodeCount: number;
    minVisibleTextNodeCount: number;
    minInteractiveNodeCount: number;
    minStyleCoverageRatio: number;
    maxPrimaryVisualDiffRatio: number;
  };
  promotionBlockedBy: string[];
  editablePrimaryPromotionBlockedBy: string[];
  ontologyIdentityBlockedBy: string[];
  reasons: string[];
}

export interface CompileCachedIntermediateRootOptions {
  renderMode?: VisibleCogoCompilerRenderMode;
  measuredVisualDiffGate?: VisualDiffGateProof;
}

async function importCogoJsonAppInputLazy(input: {
  title: string;
  input: string;
}): Promise<ImportedCogoXyzApp> {
  const { importCogoJsonAppInput } = await import("./cogo-xyz-import");
  return importCogoJsonAppInput(input);
}

function editableSeedComponent(input: {
  id: string;
  name: string;
  kind: string;
  text?: string;
  left: number;
  top: number;
  width: number;
  height: number;
  fill?: string;
  stroke?: string;
  radius?: number;
  fontSize?: number;
  fontWeight?: number;
  gradient?: string;
  shadow?: string;
  transform?: string;
  visualPrimitive?: Record<string, unknown>;
}): Record<string, unknown> {
  return {
    id: `editable:${input.id}`,
    sourceNodeId: `editable:${input.id}`,
    name: input.name,
    figmaType: input.kind === "text" ? "TEXT" : "FRAME",
    cogoKind: input.kind,
    ...(input.text ? { text: input.text } : {}),
    bbox: {
      x: input.left,
      y: input.top,
      width: input.width,
      height: input.height,
    },
    relativeLayout: {
      position: "absolute",
      left: input.left,
      top: input.top,
      width: input.width,
      height: input.height,
    },
    styleFacts: {
      fillTokens: input.fill ? [input.fill] : [],
      strokeTokens: input.stroke ? [input.stroke] : [],
      ...(input.gradient ? { gradient: input.gradient } : {}),
      ...(input.shadow ? { shadow: input.shadow } : {}),
      ...(input.transform ? { transform: input.transform } : {}),
      ...(input.visualPrimitive
        ? { visualPrimitive: input.visualPrimitive }
        : {}),
      ...(input.radius !== undefined ? { radius: input.radius } : {}),
      typography: {
        fontFamily: "Inter",
        ...(input.fontSize !== undefined ? { fontSize: input.fontSize } : {}),
        ...(input.fontWeight !== undefined
          ? { fontWeight: input.fontWeight }
          : {}),
        ...(input.fontSize !== undefined
          ? { lineHeightPx: Math.round(input.fontSize * 1.25) }
          : {}),
      },
    },
  };
}

const STATIC_FIGMA_VISUAL_LOCKS: Record<
  string,
  {
    source: "static-studio-asset";
    status: "pass" | "warn";
    pages: CachedVisualLockPage[];
  }
> = {
  "bike-group-24": {
    source: "static-studio-asset",
    status: "pass",
    pages: [
      {
        pageIndex: 0,
        name: "Discover",
        sourceNodeId: "1:42",
        route: "/",
        width: 394,
        height: 844,
        figmaWidth: 394,
        figmaHeight: 844,
        imageUrl: "/figma-visual-lock/bike-group-24/discover.png",
        source: "static-studio-asset",
        editableComponents: [
          editableSeedComponent({
            id: "bike-status-bar",
            name: "Status bar",
            kind: "nav",
            left: 28,
            top: 0,
            width: 338,
            height: 42,
            visualPrimitive: {
              kind: "mobile-status-bar",
              foreground: "#f8fafc",
            },
          }),
          editableSeedComponent({
            id: "bike-title",
            name: "Choose Your Bike",
            kind: "text",
            text: "Choose Your Bike",
            left: 21,
            top: 72,
            width: 220,
            height: 34,
            fill: "#f8fafc",
            fontSize: 22,
            fontWeight: 700,
          }),
          editableSeedComponent({
            id: "bike-search",
            name: "Search",
            kind: "button",
            left: 326,
            top: 61,
            width: 51,
            height: 51,
            fill: "#49a6ff",
            radius: 12,
            shadow: "0 10px 22px rgba(59, 130, 246, 0.35)",
            visualPrimitive: { kind: "search-icon", foreground: "#f8fafc" },
          }),
          editableSeedComponent({
            id: "bike-blue-glow-surface",
            name: "Blue glow surface",
            kind: "surface",
            left: 170,
            top: 292,
            width: 250,
            height: 540,
            fill: "rgba(37,99,235,0.16)",
            radius: 82,
            gradient:
              "linear-gradient(150deg, rgba(96,165,250,0.62), rgba(37,99,235,0.18) 48%, rgba(15,23,42,0) 82%)",
            shadow: "0 0 80px rgba(59, 130, 246, 0.26)",
            transform: "rotate(12deg)",
          }),
          editableSeedComponent({
            id: "bike-promo-card",
            name: "Promotion card",
            kind: "card",
            left: 21,
            top: 139,
            width: 328,
            height: 204,
            fill: "#334155",
            stroke: "#475569",
            radius: 18,
            gradient:
              "linear-gradient(135deg, rgba(51,65,85,0.96), rgba(38,54,78,0.86))",
            shadow: "0 18px 42px rgba(15, 23, 42, 0.34)",
          }),
          editableSeedComponent({
            id: "bike-hero-product-image",
            name: "Hero bike image",
            kind: "image",
            left: 78,
            top: 176,
            width: 220,
            height: 96,
            fill: "rgba(0,0,0,0)",
            stroke: "rgba(0,0,0,0)",
            radius: 10,
            visualPrimitive: {
              kind: "bike-line-art",
              foreground: "#cbd5e1",
              accent: "#0f172a",
            },
          }),
          editableSeedComponent({
            id: "bike-promo-copy",
            name: "30% Off",
            kind: "text",
            text: "30% Off",
            left: 40,
            top: 309,
            width: 128,
            height: 38,
            fill: "#f8fafc",
            fontSize: 28,
            fontWeight: 800,
          }),
          editableSeedComponent({
            id: "bike-category-tabs",
            name: "Category tabs",
            kind: "tabs",
            left: 34,
            top: 366,
            width: 306,
            height: 58,
            fill: "rgba(0,0,0,0)",
            stroke: "rgba(0,0,0,0)",
            visualPrimitive: {
              kind: "bike-category-tabs",
              foreground: "#e2e8f0",
              selected: "#4aa8ff",
              surface: "#273244",
            },
          }),
          editableSeedComponent({
            id: "bike-product-card-left",
            name: "Road Bike card",
            kind: "card",
            left: 21,
            top: 474,
            width: 165,
            height: 225,
            fill: "#2f3a4c",
            stroke: "#475569",
            radius: 18,
            shadow: "0 16px 34px rgba(15, 23, 42, 0.28)",
          }),
          editableSeedComponent({
            id: "bike-product-card-right",
            name: "Helmet card",
            kind: "card",
            left: 212,
            top: 430,
            width: 165,
            height: 225,
            fill: "#3b4b68",
            stroke: "#60a5fa",
            radius: 18,
            shadow: "0 16px 34px rgba(37, 99, 235, 0.22)",
          }),
          editableSeedComponent({
            id: "bike-product-image-left",
            name: "Road bike product image",
            kind: "image",
            left: 53,
            top: 520,
            width: 98,
            height: 62,
            fill: "rgba(0,0,0,0)",
            stroke: "rgba(0,0,0,0)",
            radius: 8,
            visualPrimitive: {
              kind: "bike-line-art",
              foreground: "#cbd5e1",
              accent: "#0f172a",
            },
          }),
          editableSeedComponent({
            id: "bike-product-image-right",
            name: "Helmet product image",
            kind: "image",
            left: 258,
            top: 470,
            width: 72,
            height: 78,
            fill: "rgba(0,0,0,0)",
            stroke: "rgba(0,0,0,0)",
            radius: 16,
            visualPrimitive: {
              kind: "helmet-line-art",
              foreground: "#e2e8f0",
              accent: "#0f172a",
            },
          }),
          editableSeedComponent({
            id: "bike-product-title-left",
            name: "Road Bike title",
            kind: "text",
            text: "Road Bike\nPEUGEOT - LR01",
            left: 40,
            top: 596,
            width: 132,
            height: 48,
            fill: "#f8fafc",
            fontSize: 13,
            fontWeight: 700,
          }),
          editableSeedComponent({
            id: "bike-product-price-left",
            name: "Road Bike price",
            kind: "text",
            text: "$ 1,299.99",
            left: 40,
            top: 654,
            width: 116,
            height: 22,
            fill: "#94a3b8",
            fontSize: 12,
            fontWeight: 500,
          }),
          editableSeedComponent({
            id: "bike-product-title-right",
            name: "Helmet title",
            kind: "text",
            text: "Road Helmet\nSMITH - Trade",
            left: 230,
            top: 566,
            width: 124,
            height: 42,
            fill: "#f8fafc",
            fontSize: 12,
            fontWeight: 700,
          }),
          editableSeedComponent({
            id: "bike-product-price-right",
            name: "Helmet price",
            kind: "text",
            text: "$ 120",
            left: 230,
            top: 620,
            width: 90,
            height: 22,
            fill: "#94a3b8",
            fontSize: 12,
            fontWeight: 500,
          }),
          editableSeedComponent({
            id: "bike-bottom-nav",
            name: "Bottom navigation",
            kind: "nav",
            left: 43,
            top: 778,
            width: 306,
            height: 66,
            fill: "#1f2937",
            stroke: "#334155",
            radius: 18,
            shadow: "0 -10px 30px rgba(15, 23, 42, 0.26)",
            visualPrimitive: {
              kind: "bike-bottom-nav",
              foreground: "#dbeafe",
              selected: "#49a6ff",
            },
          }),
        ],
      },
      {
        pageIndex: 1,
        name: "Detail",
        sourceNodeId: "1:180",
        route: "/peugeot-lr01",
        width: 394,
        height: 844,
        figmaWidth: 394,
        figmaHeight: 844,
        imageUrl: "/figma-visual-lock/bike-group-24/detail.png",
        source: "static-studio-asset",
      },
      {
        pageIndex: 2,
        name: "Cart",
        sourceNodeId: "1:214",
        route: "/my-shopping-cart",
        width: 394,
        height: 844,
        figmaWidth: 394,
        figmaHeight: 844,
        imageUrl: "/figma-visual-lock/bike-group-24/cart.png",
        source: "static-studio-asset",
      },
    ],
  },
  "ibank-design-canvas": {
    source: "static-studio-asset",
    status: "pass",
    pages: [
      {
        pageIndex: 0,
        name: "Sign in",
        sourceNodeId: "54:21278",
        route: "/",
        width: 375,
        height: 812,
        figmaWidth: 375,
        figmaHeight: 812,
        imageUrl: "/figma-visual-lock/ibank-design-canvas/sign-in.png",
        source: "static-studio-asset",
        editableComponents: [
          editableSeedComponent({
            id: "ibank-header-surface",
            name: "Header surface",
            kind: "surface",
            left: 0,
            top: 0,
            width: 375,
            height: 118,
            fill: "#3f2bbd",
          }),
          editableSeedComponent({
            id: "ibank-content-sheet",
            name: "Content sheet",
            kind: "surface",
            left: 0,
            top: 108,
            width: 375,
            height: 704,
            fill: "#ffffff",
            radius: 28,
            shadow: "0 -8px 24px rgba(15, 23, 42, 0.08)",
          }),
          editableSeedComponent({
            id: "ibank-status-bar",
            name: "Status bar",
            kind: "nav",
            left: 24,
            top: 0,
            width: 327,
            height: 40,
            visualPrimitive: {
              kind: "mobile-status-bar",
              foreground: "#f8fafc",
            },
          }),
          editableSeedComponent({
            id: "ibank-back",
            name: "Back",
            kind: "button",
            left: 23,
            top: 64,
            width: 32,
            height: 32,
            visualPrimitive: { kind: "back-chevron", foreground: "#ffffff" },
          }),
          editableSeedComponent({
            id: "ibank-sign-in-title",
            name: "Sign in title",
            kind: "text",
            text: "Sign in",
            left: 57,
            top: 63,
            width: 92,
            height: 36,
            fill: "#ffffff",
            fontSize: 24,
            fontWeight: 700,
          }),
          editableSeedComponent({
            id: "ibank-welcome",
            name: "Welcome copy",
            kind: "text",
            text: "Welcome Back\nHello there, sign in to continue",
            left: 24,
            top: 133,
            width: 284,
            height: 58,
            fill: "#111827",
            fontSize: 26,
            fontWeight: 700,
          }),
          editableSeedComponent({
            id: "ibank-hero-illustration",
            name: "Lock illustration",
            kind: "image",
            left: 108,
            top: 255,
            width: 160,
            height: 160,
            fill: "#e0f2fe",
            stroke: "#38bdf8",
            radius: 80,
            gradient:
              "radial-gradient(circle at 50% 42%, #eff6ff 0%, #dbeafe 68%, #bfdbfe 100%)",
            visualPrimitive: {
              kind: "bank-lock-illustration",
              foreground: "#2563eb",
              accent: "#0f172a",
            },
          }),
          editableSeedComponent({
            id: "ibank-text-input",
            name: "Text input",
            kind: "input",
            left: 24,
            top: 410,
            width: 327,
            height: 45,
            fill: "#f8fafc",
            stroke: "#e5e7eb",
            radius: 14,
            shadow: "0 6px 14px rgba(15, 23, 42, 0.04)",
          }),
          editableSeedComponent({
            id: "ibank-password-input",
            name: "Password",
            kind: "input",
            left: 24,
            top: 475,
            width: 327,
            height: 45,
            fill: "#f8fafc",
            stroke: "#e5e7eb",
            radius: 14,
            shadow: "0 6px 14px rgba(15, 23, 42, 0.04)",
          }),
          editableSeedComponent({
            id: "ibank-sign-in-button",
            name: "Sign in",
            kind: "button",
            text: "Sign in",
            left: 24,
            top: 585,
            width: 327,
            height: 45,
            fill: "#2563eb",
            radius: 14,
            shadow: "0 12px 24px rgba(37, 99, 235, 0.18)",
          }),
          editableSeedComponent({
            id: "ibank-biometric",
            name: "Fingerprint",
            kind: "button",
            left: 153,
            top: 674,
            width: 70,
            height: 70,
            fill: "#eff6ff",
            stroke: "#60a5fa",
            radius: 35,
            visualPrimitive: { kind: "fingerprint", foreground: "#2563eb" },
          }),
          editableSeedComponent({
            id: "ibank-sign-up-link",
            name: "Sign Up",
            kind: "text",
            text: "Sign Up",
            left: 235,
            top: 762,
            width: 64,
            height: 26,
            fill: "#2563eb",
          }),
        ],
      },
      {
        pageIndex: 1,
        name: "Sign up #1",
        sourceNodeId: "54:21277",
        route: "/sign-up-1",
        width: 375,
        height: 812,
        figmaWidth: 375,
        figmaHeight: 812,
        imageUrl: "/figma-visual-lock/ibank-design-canvas/sign-up-1.png",
        source: "static-studio-asset",
      },
      {
        pageIndex: 2,
        name: "Home",
        sourceNodeId: "2:22909",
        route: "/home",
        width: 375,
        height: 844,
        figmaWidth: 375,
        figmaHeight: 844,
        imageUrl: "/figma-visual-lock/ibank-design-canvas/home.png",
        source: "static-studio-asset",
      },
      {
        pageIndex: 3,
        name: "Account & card #1",
        sourceNodeId: "290:4931",
        route: "/account-card-1",
        width: 375,
        height: 812,
        figmaWidth: 375,
        figmaHeight: 812,
        imageUrl: "/figma-visual-lock/ibank-design-canvas/account-card-1.png",
        source: "static-studio-asset",
      },
      {
        pageIndex: 4,
        name: "Transfer #5 - Successfully",
        sourceNodeId: "587:29634",
        route: "/transfer-successfully",
        width: 375,
        height: 812,
        figmaWidth: 375,
        figmaHeight: 812,
        imageUrl:
          "/figma-visual-lock/ibank-design-canvas/transfer-successfully.png",
        source: "static-studio-asset",
      },
      {
        pageIndex: 5,
        name: "Exchange rate",
        sourceNodeId: "61:895",
        route: "/exchange-rate",
        width: 375,
        height: 812,
        figmaWidth: 375,
        figmaHeight: 812,
        imageUrl: "/figma-visual-lock/ibank-design-canvas/exchange-rate.png",
        source: "static-studio-asset",
      },
      {
        pageIndex: 6,
        name: "Transaction report #2",
        sourceNodeId: "143:601",
        route: "/transaction-report-2",
        width: 375,
        height: 815,
        figmaWidth: 375,
        figmaHeight: 815,
        imageUrl:
          "/figma-visual-lock/ibank-design-canvas/transaction-report-2.png",
        source: "static-studio-asset",
      },
      {
        pageIndex: 7,
        name: "Message #1",
        sourceNodeId: "104:2499",
        route: "/message-1",
        width: 375,
        height: 812,
        figmaWidth: 375,
        figmaHeight: 812,
        imageUrl: "/figma-visual-lock/ibank-design-canvas/message-1.png",
        source: "static-studio-asset",
      },
    ],
  },
};

const STATIC_FIGMA_TEMPLATE_BY_FILE_KEY: Record<string, string> = {
  BuX9uUXDcoDfLIZF1vVvSi: "bike-group-24",
  Ij2IoVfWtLAduApU1dBtd1: "bike-group-24",
  "4bQxHpFtpc1xgqEngy4fWL": "ibank-design-canvas",
  vHrRFbinwzNfApucoPH2dC: "ibank-design-canvas",
};

const STATIC_FIGMA_IMPORT_ROUTE_TARGETS: Record<
  string,
  {
    appId: string;
    sourceName: string;
    input: FigmaFrameImportInput;
  }
> = {
  "import-figma-discover": {
    appId: "figma-discover",
    sourceName: "Online Bike Shopping App Discover.cogo-app.json",
    input: {
      figmaFileId: "Ij2IoVfWtLAduApU1dBtd1",
      figmaNodeId: "1:41",
      figmaUrl:
        "https://www.figma.com/design/Ij2IoVfWtLAduApU1dBtd1/Online-Bike-Shopping-App--Community-?node-id=1-41&p=f",
      audience: "mobile commerce application users",
      tags: ["figma-community-template", "mobile", "commerce", "bike"],
    },
  },
  "import-figma-sign-in": {
    appId: "figma-sign-in",
    sourceName: "iBank Sign In.cogo-app.json",
    input: {
      figmaFileId: "vHrRFbinwzNfApucoPH2dC",
      figmaNodeId: "54:21278",
      figmaUrl:
        "https://www.figma.com/design/vHrRFbinwzNfApucoPH2dC/iBank---Banking---E-Money-Management-App-%7C-FinPay-%7C-Digital-%7C-Finance-Mobile-Banking-App-Ui-Kit--Community-?node-id=54-21278&p=f",
      audience: "mobile banking application users",
      tags: ["figma-community-template", "mobile", "finance", "banking"],
    },
  },
};

export interface FigmaFrameImportResult extends ImportedCogoXyzApp {
  frameName: string;
  sourceFrame?: FigmaSourceFrameSummary;
  sourceFrames?: FigmaSourceFrameSummary[];
  pageCount?: number;
  actionFlowCount?: number;
  sourceOntologyStorage?: FigmaSourceOntologyStorage;
  mapping: {
    matchedNodes: number;
    unsupportedNodes: string[];
    extractedTokens: string[];
    matchedComponents: string[];
    componentMatchRate: number;
    unsupportedNodeCount: number;
    textNodeCount: number;
    nodeCount: number;
    sourceFrameSelection?: FigmaSourceFrameSelectionSummary;
    ontologyValidation: FigmaFrameOntologyValidationSummary;
    referenceParity: FigmaTemplateReferenceParityReport;
    designTemplateApplication: FigmaFrameDesignTemplateApplicationSummary;
  };
}

type FigmaFrameImportMapping = FigmaFrameImportResult["mapping"];

export interface FigmaFrameCogoBundleBuildResult {
  frameName: string;
  sourceFrame?: FigmaSourceFrameSummary;
  mapping: FigmaFrameImportMapping;
  bundle: Record<string, unknown>;
}

export interface StaticFigmaImportedCogoApplicationSnapshot {
  projectId: string;
  sourceName: string;
  bundle: Record<string, unknown>;
  frameName: string;
  figmaFileId: string;
  figmaNodeId: string;
  mapping: FigmaFrameImportMapping;
}

export interface FigmaFrameOntologyValidationSummary {
  schemaVersion: FigmaTemplateLiveSourceMappingReport["schemaVersion"];
  status: FigmaTemplateLiveSourceMappingReport["status"];
  sourceCount: number;
  admittedSourceCount: number;
  blockedSourceCount: number;
  admittedCaseCount: number;
  sourceEvidence: FigmaTemplateLiveSourceMappingReport["sourceEvidence"];
  mappingCaseCount: number;
  nodeMappingCount: number;
  requirementMappingCount: number;
  chunkMappingCount: number;
  tokenMappingCount: number;
  actionMappingCount: number;
  dataInputMappingCount: number;
  hardConstraints: string[];
  qualityReadiness?: FigmaTemplateQualityReadiness;
}

export interface FigmaFrameDesignTemplateApplicationSummary {
  schemaVersion: typeof FIGMA_TEMPLATE_APPLICATION_SCHEMA_VERSION;
  status: "applied" | "partial" | "blocked";
  source: "figma-template";
  enforcement: "tool-compiler";
  templateId?: string;
  designSystemId?: string;
  hardConstraints: string[];
  tokenRefs: string[];
  appliedNodeCount: number;
  styledNodeCount: number;
  textStyleNodeCount: number;
  componentStyleNodeCount: number;
  styleSignature: {
    backgroundColor?: string;
    primaryColor?: string;
    foregroundColor?: string;
    primaryForegroundColor?: string;
    radius?: string;
    shadow?: string;
    fontFamily?: string;
    fontWeight?: number;
    fontSize?: string;
    lineHeight?: string;
  };
  qualityReadiness?: FigmaFrameOntologyValidationSummary["qualityReadiness"];
  referenceParity?: FigmaTemplateReferenceParityReport;
  ontologyMatchingPlan: Record<string, unknown>;
  ontologyRetrievalTrace: Record<string, unknown>;
  ontologyCompilerContract: Record<string, unknown>;
  reasons: string[];
}

interface FigmaPaint {
  type?: string;
  visible?: boolean;
  opacity?: number;
  color?: { r?: number; g?: number; b?: number; a?: number };
  // GRADIENT_LINEAR / GRADIENT_RADIAL / GRADIENT_ANGULAR
  gradientStops?: Array<{
    position?: number;
    color?: { r?: number; g?: number; b?: number; a?: number };
  }>;
  gradientHandlePositions?: Array<{ x?: number; y?: number }>;
  // IMAGE (imageRef resolves to a URL via the /v1/images endpoint — not yet wired)
  imageRef?: string;
  scaleMode?: string;
  gifRef?: string;
  tileType?: string;
  patternTileType?: string;
  scalingFactor?: number;
  spacing?: number;
  alignment?: string;
  shaderType?: string;
  uniforms?: Record<string, unknown>;
}

interface FigmaEffect {
  type?: string;
  visible?: boolean;
  radius?: number;
  offset?: { x?: number; y?: number };
  color?: FigmaPaint["color"];
  density?: number;
  shaderType?: string;
  uniforms?: Record<string, unknown>;
}

interface FigmaNode {
  id?: string;
  name?: string;
  type?: string;
  visible?: boolean;
  absoluteBoundingBox?: {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
  };
  absoluteRenderBounds?: {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
  } | null;
  relativeTransform?: number[][];
  opacity?: number;
  blendMode?: string;
  clipsContent?: boolean;
  constraints?: {
    horizontal?: string;
    vertical?: string;
  };
  targetAspectRatio?: number;
  layoutMode?: string;
  primaryAxisSizingMode?: string;
  counterAxisSizingMode?: string;
  primaryAxisAlignItems?: string;
  counterAxisAlignItems?: string;
  paddingLeft?: number;
  paddingRight?: number;
  paddingTop?: number;
  paddingBottom?: number;
  itemSpacing?: number;
  layoutWrap?: string;
  gridRowCount?: number;
  gridColumnCount?: number;
  gridRowGap?: number;
  gridColumnGap?: number;
  gridRowsSizing?: unknown[];
  gridColumnsSizing?: unknown[];
  layoutAlign?: string;
  layoutGrow?: number;
  layoutPositioning?: string;
  layoutGrids?: unknown[];
  overflowDirection?: string;
  isMask?: boolean;
  maskType?: string;
  cornerSmoothing?: number;
  characterStyleOverrides?: number[];
  styleOverrideTable?: Record<string, unknown>;
  textRangeFills?: unknown[];
  lineTypes?: string[];
  lineIndentations?: number[];
  componentPropertyDefinitions?: Record<string, unknown>;
  componentPropertyReferences?: Record<string, unknown>;
  componentProperties?: Record<string, unknown>;
  boundVariables?: Record<string, unknown>;
  resolvedVariableModes?: Record<string, unknown>;
  explicitVariableModes?: Record<string, unknown>;
  inferredVariables?: Record<string, unknown>;
  styles?: Record<string, unknown>;
  interactions?: unknown[];
  transitionNodeID?: string;
  flowStartingPoints?: unknown[];
  prototypeDevice?: Record<string, unknown>;
  animations?: unknown[];
  manualKeyframeTracks?: unknown[];
  timelines?: unknown[];
  devStatus?: unknown;
  annotations?: unknown[];
  measurements?: unknown[];
  documentationLinks?: unknown[];
  exportSettings?: unknown[];
  complexStrokeProperties?: Record<string, unknown>;
  variableWidthPoints?: unknown[];
  fillGeometry?: Array<{ path?: string; data?: string; windingRule?: string }>;
  strokeGeometry?: Array<{
    path?: string;
    data?: string;
    windingRule?: string;
  }>;
  strokeCap?: string;
  strokeJoin?: string;
  strokeDashes?: number[];
  strokeWeight?: number;
  strokeAlign?: string;
  strokeMiterAngle?: number;
  booleanOperation?: string;
  characters?: string;
  textPathStartData?: Record<string, unknown>;
  textPath?: Record<string, unknown>;
  fills?: FigmaPaint[];
  strokes?: FigmaPaint[];
  cornerRadius?: number;
  effects?: FigmaEffect[];
  style?: {
    fontFamily?: string;
    fontPostScriptName?: string;
    fontWeight?: number;
    fontSize?: number;
    lineHeightPx?: number;
    letterSpacing?: number;
    textAutoResize?: string;
    textTruncation?: string;
    maxLines?: number;
  };
  children?: FigmaNode[];
}

function readRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function readString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function readNumber(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value)
    ? value
    : undefined;
}

function readBoolean(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

function readArray<T = unknown>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

function stableFigmaJson(value: unknown): string {
  return `${JSON.stringify(value, null, 2)}\n`;
}

async function exactFigmaSourceIdentity(
  payload: Record<string, unknown>,
): Promise<{ sourceRevision: string; sourceSha256: string }> {
  const sourceSha256 = await canonicalFigmaSourceSha256(payload);
  const sourceRevision =
    readString(payload.version) ??
    readString(payload.lastModified) ??
    `sha256:${sourceSha256}`;
  return { sourceRevision, sourceSha256 };
}

function withDefinedRecordEntries(
  entries: Record<string, unknown>,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(entries).filter(([, value]) => {
      if (value === undefined || value === null) return false;
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === "object") {
        return Object.keys(readRecord(value)).length > 0;
      }
      return value !== "";
    }),
  );
}

function safeId(value: string): string {
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 48) || "figma"
  );
}

function stableHash(value: string): string {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
}

function safeElementId(node: FigmaNode, path: string): string {
  const raw = node.id ?? `${path}-${node.name ?? node.type ?? "figma-node"}`;
  const base = safeId(raw).slice(0, 40) || "figma-node";
  return `${base}-${stableHash(`${raw}:${path}`)}`;
}

function colorTokenFromPaint(paint: FigmaPaint): string | null {
  if (paint.type !== "SOLID" || paint.visible === false || !paint.color)
    return null;
  const r = Math.round((paint.color.r ?? 0) * 255);
  const g = Math.round((paint.color.g ?? 0) * 255);
  const b = Math.round((paint.color.b ?? 0) * 255);
  const a = (paint.color.a ?? 1) * (paint.opacity ?? 1);
  return a < 1
    ? `rgba(${r}, ${g}, ${b}, ${Number(a.toFixed(3))})`
    : `rgb(${r}, ${g}, ${b})`;
}

function cssColorFromPaints(
  paints: FigmaPaint[] | undefined,
  fallback?: string,
): string | undefined {
  const solids =
    paints?.filter(
      (paint) =>
        paint.type === "SOLID" &&
        paint.visible !== false &&
        paint.opacity !== 0 &&
        (paint.color?.a ?? 1) > 0 &&
        paint.color,
    ) ?? [];
  if (solids.length === 0) return fallback;
  let outR = 0;
  let outG = 0;
  let outB = 0;
  let outA = 0;
  for (const paint of solids) {
    const srcA = Math.max(
      0,
      Math.min(1, (paint.color?.a ?? 1) * (paint.opacity ?? 1)),
    );
    const nextA = srcA + outA * (1 - srcA);
    const srcR = paint.color?.r ?? 0;
    const srcG = paint.color?.g ?? 0;
    const srcB = paint.color?.b ?? 0;
    if (nextA > 0) {
      outR = (srcR * srcA + outR * outA * (1 - srcA)) / nextA;
      outG = (srcG * srcA + outG * outA * (1 - srcA)) / nextA;
      outB = (srcB * srcA + outB * outA * (1 - srcA)) / nextA;
    }
    outA = nextA;
  }
  const r = Math.round(outR * 255);
  const g = Math.round(outG * 255);
  const b = Math.round(outB * 255);
  const a = Number(outA.toFixed(3));
  return a < 1 ? `rgba(${r}, ${g}, ${b}, ${a})` : `rgb(${r}, ${g}, ${b})`;
}

function firstVisibleSolidPaint(paints?: FigmaPaint[]): FigmaPaint | undefined {
  return paints?.find(
    (paint) =>
      paint.type === "SOLID" &&
      paint.visible !== false &&
      paint.opacity !== 0 &&
      (paint.color?.a ?? 1) > 0 &&
      paint.color,
  );
}

function rgbaFromFigmaColor(
  color?: { r?: number; g?: number; b?: number; a?: number },
  paintOpacity = 1,
): string {
  const r = Math.round((color?.r ?? 0) * 255);
  const g = Math.round((color?.g ?? 0) * 255);
  const b = Math.round((color?.b ?? 0) * 255);
  const a = Number(((color?.a ?? 1) * paintOpacity).toFixed(3));
  return a < 1 ? `rgba(${r}, ${g}, ${b}, ${a})` : `rgb(${r}, ${g}, ${b})`;
}

export function isGradientPaintType(type?: string): boolean {
  return (
    type === "GRADIENT_LINEAR" ||
    type === "GRADIENT_RADIAL" ||
    type === "GRADIENT_ANGULAR" ||
    type === "GRADIENT_DIAMOND"
  );
}

export function firstVisibleGradientPaint(
  paints?: FigmaPaint[],
): FigmaPaint | undefined {
  return paints?.find(
    (paint) =>
      isGradientPaintType(paint.type) &&
      paint.visible !== false &&
      (paint.gradientStops?.length ?? 0) > 0,
  );
}

/**
 * Convert a Figma gradient paint to a CSS gradient string.
 * Figma gradient handles are normalized (0..1) within the node box, y-axis DOWN.
 * CSS linear-gradient angle: 0deg points up, increases clockwise (90deg = left->right).
 */
export function cssGradientFromPaint(paint: FigmaPaint): string | undefined {
  const stops = paint.gradientStops ?? [];
  if (stops.length === 0) return undefined;
  const stopCss = stops
    .map((stop) => {
      const pos = Math.round((stop.position ?? 0) * 100);
      return `${rgbaFromFigmaColor(stop.color, paint.opacity ?? 1)} ${pos}%`;
    })
    .join(", ");
  if (paint.type === "GRADIENT_RADIAL" || paint.type === "GRADIENT_DIAMOND") {
    return `radial-gradient(circle, ${stopCss})`;
  }
  if (paint.type === "GRADIENT_ANGULAR") {
    return `conic-gradient(${stopCss})`;
  }
  // GRADIENT_LINEAR
  const handles = paint.gradientHandlePositions ?? [];
  const start = handles[0];
  const end = handles[1];
  let angleDeg = 180; // default top->bottom
  if (start && end) {
    const dx = (end.x ?? 0) - (start.x ?? 0);
    const dy = (end.y ?? 0) - (start.y ?? 0);
    if (dx !== 0 || dy !== 0) {
      angleDeg = Math.round(
        ((Math.atan2(dx, -dy) * 180) / Math.PI + 360) % 360,
      );
    }
  }
  return `linear-gradient(${angleDeg}deg, ${stopCss})`;
}

// The top-most visible (solid or gradient) fill. Figma fills are ordered bottom->top, so
// the last visible paint is the one rendered on top (matching this file's existing
// convention of treating the first solid as the bottom backgroundColor).
// A paint that actually puts pixels on screen: visible, non-zero opacity, and (for solids)
// a non-zero alpha. A `visible: true` paint with opacity 0 / color.a 0 is effectively
// invisible and must not block a fill below it.
function isPaintRenderable(paint: FigmaPaint): boolean {
  if (paint.visible === false) return false;
  if (paint.opacity === 0) return false;
  if (paint.type === "SOLID" && (paint.color?.a ?? 1) === 0) return false;
  return true;
}

function topVisibleFill(paints?: FigmaPaint[]): FigmaPaint | undefined {
  if (!paints) return undefined;
  // The first RENDERABLE paint from the top, REGARDLESS of type. An unsupported renderable
  // paint (e.g. an image/tint layer) sitting above a gradient must BLOCK the gradient rather
  // than be skipped — otherwise a covered gradient would be rendered. Fully transparent
  // paints are skipped so they don't block a visible fill below. Callers decide what to do
  // based on the returned paint's type.
  for (let i = paints.length - 1; i >= 0; i -= 1) {
    if (isPaintRenderable(paints[i])) return paints[i];
  }
  return undefined;
}

// CSS gradient for a node's fill, but ONLY when the gradient is the top-most visible fill.
// CSS paints background-image over background-color, so applying a gradient unconditionally
// would render it above a solid that is actually the top/tint layer in Figma. When the top
// fill is a solid (or there is no fill), this returns undefined and the solid backgroundColor
// wins.
export function gradientBackgroundIfTop(node: FigmaNode): string | undefined {
  const top = topVisibleFill(node.fills);
  if (!top || !isGradientPaintType(top.type)) return undefined;
  return cssGradientFromPaint(top);
}

function px(value: number | undefined): string | undefined {
  return typeof value === "number" && Number.isFinite(value)
    ? `${Number(value.toFixed(2))}px`.replace(".00px", "px")
    : undefined;
}

function pct(value: number | undefined): string | undefined {
  return typeof value === "number" && Number.isFinite(value)
    ? `${Number((value * 100).toFixed(3))}%`
    : undefined;
}

function hasUsableBox(node: FigmaNode): boolean {
  return (
    typeof node.absoluteBoundingBox?.width === "number" &&
    node.absoluteBoundingBox.width > 0 &&
    typeof node.absoluteBoundingBox?.height === "number" &&
    node.absoluteBoundingBox.height > 0
  );
}

function layoutStyleForNode(
  node: FigmaNode,
  parentNode?: FigmaNode,
): CogoInlineStyle {
  const box = node.absoluteBoundingBox;
  if (!box || !hasUsableBox(node)) return {};
  const width = px(box.width);
  const height = px(box.height);
  if (!parentNode?.absoluteBoundingBox) {
    return {
      position: "relative",
      width: "100%",
      maxWidth: width ?? "100%",
      ...(width && height
        ? { aspectRatio: `${box.width} / ${box.height}` }
        : {}),
      overflow: "hidden",
    };
  }
  const parentBox = parentNode.absoluteBoundingBox;
  const parentWidth =
    typeof parentBox.width === "number" && parentBox.width > 0
      ? parentBox.width
      : undefined;
  const parentHeight =
    typeof parentBox.height === "number" && parentBox.height > 0
      ? parentBox.height
      : undefined;
  const style: CogoInlineStyle = {
    position: "absolute",
    left:
      parentWidth && typeof box.x === "number"
        ? (pct((box.x - (parentBox.x ?? 0)) / parentWidth) ?? "0px")
        : (px((box.x ?? 0) - (parentBox.x ?? 0)) ?? "0px"),
    top:
      parentHeight && typeof box.y === "number"
        ? (pct((box.y - (parentBox.y ?? 0)) / parentHeight) ?? "0px")
        : (px((box.y ?? 0) - (parentBox.y ?? 0)) ?? "0px"),
    width:
      parentWidth && typeof box.width === "number"
        ? (pct(box.width / parentWidth) ?? "auto")
        : (width ?? "auto"),
  };
  if (parentHeight && typeof box.height === "number") {
    style.height = pct(box.height / parentHeight) ?? height ?? "auto";
  } else if (height) {
    style.minHeight = height;
  }
  return style;
}

function shadowCssFromEffect(effect: FigmaEffect): string | undefined {
  if (!effect || effect.visible === false) return undefined;
  if (effect.type !== "DROP_SHADOW" && effect.type !== "INNER_SHADOW")
    return undefined;
  const x = effect.offset?.x ?? 0;
  const y = effect.offset?.y ?? 0;
  const blur = effect.radius ?? 0;
  const color =
    colorTokenFromPaint({
      type: "SOLID",
      color: effect.color ?? { r: 0, g: 0, b: 0, a: 0.18 },
    }) ?? "rgba(0, 0, 0, 0.18)";
  const inset = effect.type === "INNER_SHADOW" ? "inset " : "";
  return `${inset}${x}px ${y}px ${blur}px ${color}`;
}

function firstShadowCss(node: FigmaNode): string | undefined {
  return (node.effects ?? [])
    .map(shadowCssFromEffect)
    .find((value): value is string => Boolean(value));
}

function contrastTextForColor(color: string | undefined): string {
  if (!color) return "rgb(255, 255, 255)";
  const match = /rgba?\((\d+),\s*(\d+),\s*(\d+)/.exec(color);
  if (!match) return "rgb(255, 255, 255)";
  const r = Number(match[1]);
  const g = Number(match[2]);
  const b = Number(match[3]);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.62 ? "rgb(17, 24, 39)" : "rgb(255, 255, 255)";
}

function typographyTokens(node: FigmaNode): string[] {
  const style = node.style;
  if (!style) return [];
  return [
    style.fontFamily ? `font:${style.fontFamily}` : null,
    typeof style.fontWeight === "number"
      ? `fontWeight:${style.fontWeight}`
      : null,
    typeof style.fontSize === "number" ? `fontSize:${style.fontSize}` : null,
    typeof style.lineHeightPx === "number"
      ? `lineHeight:${style.lineHeightPx}`
      : null,
  ].filter((token): token is string => Boolean(token));
}

function radiusTokens(node: FigmaNode): string[] {
  return typeof node.cornerRadius === "number"
    ? [`radius:${node.cornerRadius}`]
    : [];
}

function shadowTokens(node: FigmaNode): string[] {
  return (node.effects ?? [])
    .filter(
      (effect) =>
        effect.visible !== false &&
        (effect.type === "DROP_SHADOW" || effect.type === "INNER_SHADOW"),
    )
    .map(
      (effect) =>
        `${effect.type?.toLowerCase() ?? "shadow"}:${effect.offset?.x ?? 0},${effect.offset?.y ?? 0},${effect.radius ?? 0}`,
    );
}

function componentKindForNode(node: FigmaNode): string | null {
  const haystack =
    `${node.id ?? ""} ${node.name ?? ""} ${node.type ?? ""}`.toLowerCase();
  if (/\b(button|cta)\b/.test(haystack)) return "button";
  if (/\b(input|textfield|text field|search|email|password)\b/.test(haystack))
    return "input";
  if (/\b(card|tile)\b/.test(haystack)) return "card";
  if (/\b(tab|tabs)\b/.test(haystack)) return "tabs";
  if (/\b(table|grid)\b/.test(haystack)) return "table";
  if (/\b(nav|navbar|navigation|menu)\b/.test(haystack)) return "nav";
  if (/\bmodal|dialog\b/.test(haystack)) return "modal";
  return null;
}

function collectNodes(node: FigmaNode): FigmaNode[] {
  return [node, ...(node.children ?? []).flatMap(collectNodes)];
}

function firstTemplateId(
  summary: FigmaFrameOntologyValidationSummary,
): string | undefined {
  return summary.sourceEvidence.flatMap((entry) => entry.templateIds)[0];
}

function firstDesignSystemId(
  summary: FigmaFrameOntologyValidationSummary,
): string | undefined {
  return summary.sourceEvidence.flatMap((entry) => entry.designSystemIds)[0];
}

function hasStyleSource(node: FigmaNode): boolean {
  return Boolean(
    firstVisibleSolidPaint(node.fills) ||
    firstVisibleSolidPaint(node.strokes) ||
    typeof node.cornerRadius === "number" ||
    firstShadowCss(node) ||
    node.style,
  );
}

function buildFigmaTokenTheme(input: {
  document: FigmaNode;
  allNodes: FigmaNode[];
}): {
  tokens: Record<string, unknown>;
  designSystemTokens: Record<string, unknown>;
  tokenRefs: string[];
  styleSignature: FigmaFrameDesignTemplateApplicationSummary["styleSignature"];
} {
  const rootFill = cssColorFromPaints(
    input.document.fills,
    "rgb(255, 255, 255)",
  );
  const textColor = cssColorFromPaints(
    input.allNodes.find(
      (node) => node.type === "TEXT" && firstVisibleSolidPaint(node.fills),
    )?.fills,
    "rgb(17, 24, 39)",
  );
  const primaryNode =
    input.allNodes.find(
      (node) =>
        componentKindForNode(node) === "button" &&
        firstVisibleSolidPaint(node.fills),
    ) ??
    input.allNodes.find(
      (node) =>
        node !== input.document &&
        isShapeType(node.type ?? "") &&
        firstVisibleSolidPaint(node.fills),
    );
  const primaryColor = cssColorFromPaints(
    primaryNode?.fills,
    "rgb(37, 99, 235)",
  );
  const primaryForeground = contrastTextForColor(primaryColor);
  const borderColor =
    cssColorFromPaints(input.document.strokes) ??
    input.allNodes
      .map((node) => cssColorFromPaints(node.strokes))
      .find((value): value is string => Boolean(value)) ??
    "rgba(15, 23, 42, 0.12)";
  const radius =
    px(input.document.cornerRadius) ??
    input.allNodes.map((node) => px(node.cornerRadius)).find(Boolean) ??
    "8px";
  const shadow =
    firstShadowCss(input.document) ??
    input.allNodes.map(firstShadowCss).find(Boolean) ??
    "0 1px 2px rgba(15, 23, 42, 0.08)";
  const headingNode = input.allNodes
    .filter((node) => node.type === "TEXT" && node.style)
    .sort((a, b) => (b.style?.fontSize ?? 0) - (a.style?.fontSize ?? 0))[0];
  const fontFamily = headingNode?.style?.fontFamily ?? "Inter";
  const fontWeight = headingNode?.style?.fontWeight ?? 600;
  const fontSize = px(headingNode?.style?.fontSize) ?? "32px";
  const lineHeight = px(headingNode?.style?.lineHeightPx) ?? "40px";
  const tokenRefs = [
    "$color.background",
    "$color.foreground",
    "$color.primary",
    "$color.primaryForeground",
    "$color.border",
    "$radius.lg",
    "$type.heading",
    "$type.body",
    "$shadow.template",
  ];
  const tokens = {
    colors: {
      background: rootFill,
      foreground: textColor,
      primary: primaryColor,
      primaryForeground,
      border: borderColor,
      card: rootFill,
    },
    radius: {
      lg: radius,
      md: radius,
    },
    typography: {
      heading: {
        family: fontFamily,
        weight: fontWeight,
        size: fontSize,
        lineHeight,
      },
      body: {
        family: fontFamily,
        weight: 400,
        size: "16px",
        lineHeight: "24px",
      },
    },
    shadow: {
      template: shadow,
      lg: shadow,
    },
    fonts: {
      sans: `"${fontFamily}", system-ui, sans-serif`,
      heading: `"${fontFamily}", system-ui, sans-serif`,
    },
    fontFamily,
  };
  return {
    tokens,
    designSystemTokens: tokens,
    tokenRefs,
    styleSignature: {
      backgroundColor: rootFill,
      foregroundColor: textColor,
      primaryColor,
      primaryForegroundColor: primaryForeground,
      radius,
      shadow,
      fontFamily,
      fontWeight,
      fontSize,
      lineHeight,
    },
  };
}

function buildOntologyEnforcementArtifacts(input: {
  figmaFileId: string;
  figmaNodeId: string;
  frameName: string;
  ontologyValidation: FigmaFrameOntologyValidationSummary;
  unsupportedNodeCount: number;
  styleSignature: FigmaFrameDesignTemplateApplicationSummary["styleSignature"];
  tokenRefs: string[];
  referenceParity?: FigmaTemplateReferenceParityReport;
}): {
  ontologyMatchingPlan: Record<string, unknown>;
  ontologyRetrievalTrace: Record<string, unknown>;
  ontologyCompilerContract: Record<string, unknown>;
} {
  const templateId = firstTemplateId(input.ontologyValidation);
  const designSystemId = firstDesignSystemId(input.ontologyValidation);
  const evidenceRefs = [
    "figma-api:nodes",
    "figma-template-live-source-mapping",
    "figma-template-design-token-compiler",
    ...(input.referenceParity ? ["figma-template-reference-parity"] : []),
  ];
  const hardConstraints = Array.from(
    new Set(input.ontologyValidation.hardConstraints),
  );
  const ontologyMatchingPlan = {
    schemaVersion: FIGMA_TEMPLATE_ONTOLOGY_PLAN_SCHEMA_VERSION,
    status: input.ontologyValidation.status,
    source: "figma-template-live-source-mapping",
    templateId,
    designSystemId,
    hardConstraints,
    selectedAuthority: {
      templateId,
      designSystemId,
      source: "figma-template",
      enforcement: "tool-compiler",
    },
    searchWindow: [
      {
        source: "figma-frame",
        figmaFileId: input.figmaFileId,
        figmaNodeId: input.figmaNodeId,
        frameName: input.frameName,
        tokenRefs: input.tokenRefs,
      },
    ],
    decisions: [
      {
        kind: "design-system",
        requirementId: designSystemId ?? "figma-derived-design-system",
        selectedAssetId: designSystemId ?? "figma-derived-design-system",
        decision: "use_authority",
        generationAllowed: true,
        evidenceRefs,
      },
      {
        kind: "template",
        requirementId: templateId ?? "figma-derived-template",
        selectedAssetId: templateId ?? "figma-derived-template",
        decision: "use_authority",
        generationAllowed: true,
        evidenceRefs,
      },
    ],
    actionFlowImpact: {
      required: false,
      requiredValidation: [],
    },
    referenceParity: input.referenceParity,
    evidenceRefs,
  };
  const ontologyCompilerContract = {
    schemaVersion: FIGMA_TEMPLATE_COMPILER_CONTRACT_SCHEMA_VERSION,
    status:
      input.ontologyValidation.status === "pass" &&
      input.ontologyValidation.qualityReadiness?.status !== "fail" &&
      input.referenceParity?.status !== "fail" &&
      input.unsupportedNodeCount === 0
        ? "ready"
        : "blocked",
    authority: "tool",
    source: "cogo-xyz-figma-import",
    templateId,
    designSystemId,
    hardConstraints,
    tokenRefs: input.tokenRefs,
    styleSignature: input.styleSignature,
    qualityReadiness: input.ontologyValidation.qualityReadiness,
    referenceParity: input.referenceParity,
    entrypoints: [
      {
        entrypoint: "figma-template-design-system",
        enforcement: "tool-compiler",
        evidenceRefs,
      },
      {
        entrypoint: "figma-template-page-block-component",
        enforcement: "tool-compiler",
        evidenceRefs,
      },
    ],
    evidenceRefs,
    reasons: [
      ...(input.ontologyValidation.status === "pass"
        ? []
        : [
            `Figma ontology validation status is ${input.ontologyValidation.status}.`,
          ]),
      ...(input.ontologyValidation.qualityReadiness?.status === "fail"
        ? [
            `Figma template quality readiness score ${input.ontologyValidation.qualityReadiness.score} is below ${input.ontologyValidation.qualityReadiness.threshold}.`,
          ]
        : []),
      ...(input.referenceParity?.status === "fail"
        ? [
            `Figma template reference parity score ${input.referenceParity.score} is below ${input.referenceParity.threshold}.`,
            ...input.referenceParity.reasons,
          ]
        : []),
      ...(input.unsupportedNodeCount === 0
        ? []
        : [
            `${input.unsupportedNodeCount} unsupported Figma node(s) block template-quality application.`,
          ]),
    ],
  };
  const ontologyRetrievalTrace = {
    schemaVersion: FIGMA_TEMPLATE_ONTOLOGY_TRACE_SCHEMA_VERSION,
    status: input.ontologyValidation.status,
    source: "figma-template-live-source-mapping",
    figmaFileId: input.figmaFileId,
    figmaNodeId: input.figmaNodeId,
    frameName: input.frameName,
    ontologyMatchingPlan,
    ontologyCompilerContract,
    qualityReadiness: input.ontologyValidation.qualityReadiness,
    referenceParity: input.referenceParity,
    unsupportedNodeCount: input.unsupportedNodeCount,
    evidenceRefs,
  };
  return {
    ontologyMatchingPlan,
    ontologyRetrievalTrace,
    ontologyCompilerContract,
  };
}

function buildDesignTemplateApplication(input: {
  figmaFileId: string;
  figmaNodeId: string;
  frameName: string;
  document: FigmaNode;
  allNodes: FigmaNode[];
  ontologyValidation: FigmaFrameOntologyValidationSummary;
  unsupportedNodeCount: number;
  tokenRefs: string[];
  styleSignature: FigmaFrameDesignTemplateApplicationSummary["styleSignature"];
  referenceParity?: FigmaTemplateReferenceParityReport;
}): FigmaFrameDesignTemplateApplicationSummary {
  const styledNodes = input.allNodes.filter(hasStyleSource);
  const textStyleNodeCount = input.allNodes.filter(
    (node) => node.type === "TEXT" && node.style,
  ).length;
  const componentStyleNodeCount = input.allNodes.filter(
    (node) => Boolean(componentKindForNode(node)) && hasStyleSource(node),
  ).length;
  const {
    ontologyMatchingPlan,
    ontologyRetrievalTrace,
    ontologyCompilerContract,
  } = buildOntologyEnforcementArtifacts({
    figmaFileId: input.figmaFileId,
    figmaNodeId: input.figmaNodeId,
    frameName: input.frameName,
    ontologyValidation: input.ontologyValidation,
    unsupportedNodeCount: input.unsupportedNodeCount,
    styleSignature: input.styleSignature,
    tokenRefs: input.tokenRefs,
    referenceParity: input.referenceParity,
  });
  const hasTemplate = Boolean(firstTemplateId(input.ontologyValidation));
  const hasDesignSystem = Boolean(
    firstDesignSystemId(input.ontologyValidation),
  );
  const qualityReady =
    input.ontologyValidation.qualityReadiness?.status !== "fail";
  const referenceParityReady = input.referenceParity?.status !== "fail";
  const status =
    input.ontologyValidation.status !== "pass" ||
    !qualityReady ||
    !referenceParityReady ||
    input.unsupportedNodeCount > 0
      ? "blocked"
      : styledNodes.length === 0 || !hasTemplate || !hasDesignSystem
        ? "partial"
        : "applied";
  return {
    schemaVersion: FIGMA_TEMPLATE_APPLICATION_SCHEMA_VERSION,
    status,
    source: "figma-template",
    enforcement: "tool-compiler",
    templateId: firstTemplateId(input.ontologyValidation),
    designSystemId: firstDesignSystemId(input.ontologyValidation),
    hardConstraints: Array.from(
      new Set(input.ontologyValidation.hardConstraints),
    ),
    tokenRefs: input.tokenRefs,
    appliedNodeCount: input.allNodes.length,
    styledNodeCount: styledNodes.length,
    textStyleNodeCount,
    componentStyleNodeCount,
    styleSignature: input.styleSignature,
    qualityReadiness: input.ontologyValidation.qualityReadiness,
    referenceParity: input.referenceParity,
    ontologyMatchingPlan,
    ontologyRetrievalTrace,
    ontologyCompilerContract,
    reasons: [
      ...(hasTemplate
        ? []
        : ["Figma template id was not resolved from ontology mapping."]),
      ...(hasDesignSystem
        ? []
        : ["Figma design system id was not resolved from ontology mapping."]),
      ...(styledNodes.length > 0
        ? []
        : ["Figma source did not expose style-bearing nodes."]),
      ...(input.ontologyValidation.status === "pass"
        ? []
        : [
            `Figma ontology validation status is ${input.ontologyValidation.status}.`,
          ]),
      ...(qualityReady
        ? []
        : [
            `Figma template quality readiness score ${input.ontologyValidation.qualityReadiness?.score} is below ${input.ontologyValidation.qualityReadiness?.threshold}.`,
          ]),
      ...(referenceParityReady
        ? []
        : [
            `Figma template reference parity score ${input.referenceParity?.score} is below ${input.referenceParity?.threshold}.`,
            ...(input.referenceParity?.reasons ?? []),
          ]),
      ...(input.unsupportedNodeCount === 0
        ? []
        : [
            `${input.unsupportedNodeCount} unsupported Figma node(s) block template-quality application.`,
          ]),
    ],
  };
}

function isContainerType(type: string): boolean {
  return [
    "DOCUMENT",
    "CANVAS",
    "FRAME",
    "GROUP",
    "SECTION",
    "COMPONENT",
    "INSTANCE",
  ].includes(type);
}

function isShapeType(type: string): boolean {
  return [
    "RECTANGLE",
    "ELLIPSE",
    "POLYGON",
    "STAR",
    "VECTOR",
    "LINE",
    "BOOLEAN_OPERATION",
  ].includes(type);
}

function unsupportedNodeRef(node: FigmaNode, path: string): string {
  return `${node.id ?? path}:${node.type ?? "UNKNOWN"}:${node.name ?? node.type ?? "Figma node"}`;
}

function isUnsupportedNode(node: FigmaNode): boolean {
  const type = node.type ?? "UNKNOWN";
  return type !== "TEXT" && !isContainerType(type) && !isShapeType(type);
}

function collectUnsupportedNodeRefs(
  node: FigmaNode,
  path = "figma-root",
): string[] {
  return [
    ...(isUnsupportedNode(node) ? [unsupportedNodeRef(node, path)] : []),
    ...(node.children ?? []).flatMap((child, index) =>
      collectUnsupportedNodeRefs(child, `${path}-${index}`),
    ),
  ];
}

function figmaNodeLayoutFacts(node: FigmaNode): Record<string, unknown> {
  const constraints = withDefinedRecordEntries({
    horizontal: node.constraints?.horizontal,
    vertical: node.constraints?.vertical,
    targetAspectRatio: node.targetAspectRatio,
  });
  const autoLayout = withDefinedRecordEntries({
    mode: node.layoutMode,
    primaryAxisSizingMode: node.primaryAxisSizingMode,
    counterAxisSizingMode: node.counterAxisSizingMode,
    primaryAxisAlignItems: node.primaryAxisAlignItems,
    counterAxisAlignItems: node.counterAxisAlignItems,
    paddingLeft: node.paddingLeft,
    paddingRight: node.paddingRight,
    paddingTop: node.paddingTop,
    paddingBottom: node.paddingBottom,
    itemSpacing: node.itemSpacing,
    layoutWrap: node.layoutWrap,
  });
  const childLayout = withDefinedRecordEntries({
    layoutAlign: node.layoutAlign,
    layoutGrow: node.layoutGrow,
    layoutPositioning: node.layoutPositioning,
  });
  const grid = withDefinedRecordEntries({
    layoutMode: node.layoutMode === "GRID" ? node.layoutMode : undefined,
    gridRowCount: node.gridRowCount,
    gridColumnCount: node.gridColumnCount,
    gridRowGap: node.gridRowGap,
    gridColumnGap: node.gridColumnGap,
    gridRowsSizing: node.gridRowsSizing,
    gridColumnsSizing: node.gridColumnsSizing,
  });
  return withDefinedRecordEntries({
    constraints,
    autoLayout,
    grid,
    childLayout,
    layoutGrids: node.layoutGrids,
    overflowDirection: node.overflowDirection,
  });
}

function figmaPaintFeatureFacts(paint: FigmaPaint): Record<string, unknown> {
  return withDefinedRecordEntries({
    type: paint.type,
    visible: paint.visible,
    opacity: paint.opacity,
    imageRef: paint.imageRef,
    gifRef: paint.gifRef,
    scaleMode: paint.scaleMode,
    tileType: paint.tileType ?? paint.patternTileType,
    scalingFactor: paint.scalingFactor,
    spacing: paint.spacing,
    alignment: paint.alignment,
    shaderType: paint.shaderType,
    uniforms: paint.uniforms,
  });
}

function figmaEffectFeatureFacts(effect: FigmaEffect): Record<string, unknown> {
  return withDefinedRecordEntries({
    type: effect.type,
    visible: effect.visible,
    radius: effect.radius,
    offset: effect.offset,
    color: effect.color,
    density: effect.density,
    shaderType: effect.shaderType,
    uniforms: effect.uniforms,
  });
}

function isVectorContractType(node: FigmaNode): boolean {
  return ["VECTOR", "LINE", "BOOLEAN_OPERATION", "POLYGON", "STAR"].includes(
    node.type ?? "",
  );
}

function visiblePaintTypes(node: FigmaNode): string[] {
  return [
    ...readArray<FigmaPaint>(node.fills),
    ...readArray<FigmaPaint>(node.strokes),
  ]
    .filter((paint) => paint.visible !== false && readString(paint.type))
    .map((paint) => readString(paint.type) as string);
}

function complexStrokeFacts(node: FigmaNode): Record<string, unknown> {
  const rawProperties = readRecord(node.complexStrokeProperties);
  const nonDefaultProperties = withDefinedRecordEntries(
    Object.fromEntries(
      Object.entries(rawProperties).filter(
        ([key, value]) => !(key === "strokeType" && value === "BASIC"),
      ),
    ),
  );
  return withDefinedRecordEntries({
    complexStrokeProperties: nonDefaultProperties,
    variableWidthPoints: node.variableWidthPoints,
  });
}

function figmaNodeFeatureFacts(node: FigmaNode): Record<string, unknown> {
  const textRanges = withDefinedRecordEntries({
    characterStyleOverrides: node.characterStyleOverrides,
    styleOverrideTable: node.styleOverrideTable,
    textRangeFills: node.textRangeFills,
    lineTypes: node.lineTypes,
    lineIndentations: node.lineIndentations,
  });
  const component = withDefinedRecordEntries({
    componentPropertyDefinitions: node.componentPropertyDefinitions,
    componentPropertyReferences: node.componentPropertyReferences,
    componentProperties: node.componentProperties,
  });
  const variables = withDefinedRecordEntries({
    boundVariables: node.boundVariables,
    resolvedVariableModes: node.resolvedVariableModes,
    explicitVariableModes: node.explicitVariableModes,
    inferredVariables: node.inferredVariables,
  });
  const prototype = withDefinedRecordEntries({
    interactions: node.interactions,
    transitionNodeID: node.transitionNodeID,
    flowStartingPoints: node.flowStartingPoints,
    prototypeDevice: node.prototypeDevice,
  });
  const motion = withDefinedRecordEntries({
    animations: node.animations,
    manualKeyframeTracks: node.manualKeyframeTracks,
    timelines: node.timelines,
  });
  const devMode = withDefinedRecordEntries({
    devStatus: node.devStatus,
    annotations: node.annotations,
    measurements: node.measurements,
    documentationLinks: node.documentationLinks,
  });
  const effects = readArray<Record<string, unknown>>(node.effects).filter(
    (effect) => effect.visible !== false,
  );
  const blurEffects = effects.filter((effect) =>
    ["LAYER_BLUR", "BACKGROUND_BLUR"].includes(readString(effect.type) ?? ""),
  );
  const textureEffects = effects.filter(
    (effect) => readString(effect.type) === "TEXTURE",
  );
  const noiseEffects = effects.filter(
    (effect) => readString(effect.type) === "NOISE",
  );
  const shaderEffects = effects.filter(
    (effect) => readString(effect.type) === "SHADER",
  );
  const paints = [
    ...readArray<FigmaPaint>(node.fills),
    ...readArray<FigmaPaint>(node.strokes),
  ];
  const patternPaints = paints
    .filter((paint) => paint.visible !== false && paint.type === "PATTERN")
    .map(figmaPaintFeatureFacts);
  const shaderPaints = paints
    .filter((paint) => paint.visible !== false && paint.type === "SHADER")
    .map(figmaPaintFeatureFacts);
  const geometryPaths = [
    ...readArray(node.fillGeometry),
    ...readArray(node.strokeGeometry),
  ];
  const paintTypes = visiblePaintTypes(node);
  const hasVisibleStroke = readArray<FigmaPaint>(node.strokes).some(
    (paint) => paint.visible !== false && readString(paint.type),
  );
  const complexStroke = complexStrokeFacts(node);
  const vector =
    isVectorContractType(node) || geometryPaths.length > 0
      ? withDefinedRecordEntries({
          fillGeometry: node.fillGeometry,
          strokeGeometry: node.strokeGeometry,
          fillColor: cssColorFromPaints(node.fills),
          strokeColor: cssColorFromPaints(node.strokes),
          ...readRecord(complexStroke),
          ...(hasVisibleStroke
            ? {
                strokeCap: node.strokeCap,
                strokeJoin: node.strokeJoin,
                strokeDashes: node.strokeDashes,
                strokeWeight: node.strokeWeight,
                strokeAlign: node.strokeAlign,
                strokeMiterAngle: node.strokeMiterAngle,
              }
            : {}),
          booleanOperation: node.booleanOperation,
          visiblePaintTypes: paintTypes,
        })
      : {};
  const textPathSource = withDefinedRecordEntries({
    textPathStartData: node.textPathStartData,
    textPath: node.textPath,
  });
  const unsupportedPaintTypes = paints
    .map((paint) => paint.type)
    .filter((type): type is string =>
      Boolean(type && ["PATTERN", "EMOJI", "VIDEO", "SHADER"].includes(type)),
    );
  return withDefinedRecordEntries({
    mask:
      node.isMask === true || node.maskType
        ? withDefinedRecordEntries({
            isMask: node.isMask,
            maskType: node.maskType,
          })
        : undefined,
    cornerSmoothing: node.cornerSmoothing,
    textRanges,
    component,
    variables,
    styles: node.styles,
    prototype,
    motion,
    devMode,
    exportSettings: node.exportSettings,
    complexStroke,
    pattern:
      patternPaints.length > 0
        ? withDefinedRecordEntries({
            source: "figma-pattern-paint",
            paints: patternPaints,
            tileType: readString(patternPaints[0]?.tileType),
            spacing: readNumber(patternPaints[0]?.spacing),
            scalingFactor: readNumber(patternPaints[0]?.scalingFactor),
            alignment: readString(patternPaints[0]?.alignment),
          })
        : undefined,
    vector:
      hasRecordEntries(vector) || geometryPaths.length > 0
        ? withDefinedRecordEntries({
            source:
              geometryPaths.length > 0
                ? "figma-vector-geometry"
                : "figma-vector-style",
            ...vector,
          })
        : undefined,
    textPath:
      node.type === "TEXT_PATH" || hasRecordEntries(textPathSource)
        ? withDefinedRecordEntries({
            source: "figma-text-path",
            characters: node.characters,
            ...textPathSource,
          })
        : undefined,
    effects: withDefinedRecordEntries({
      blur: blurEffects.map((effect) =>
        figmaEffectFeatureFacts(effect as FigmaEffect),
      ),
      texture: textureEffects.map((effect) =>
        figmaEffectFeatureFacts(effect as FigmaEffect),
      ),
      noise: noiseEffects.map((effect) =>
        figmaEffectFeatureFacts(effect as FigmaEffect),
      ),
      shader: [
        ...shaderEffects.map((effect) =>
          figmaEffectFeatureFacts(effect as FigmaEffect),
        ),
        ...shaderPaints,
      ],
    }),
    unsupportedPaintTypes: Array.from(new Set(unsupportedPaintTypes)),
  });
}

function sourceDesignMetadata(
  node: FigmaNode,
  application: FigmaFrameDesignTemplateApplicationSummary,
  tokenRefs: string[],
  designTokenBindings: FigmaDesignTokenBindings = {},
): Record<string, unknown> {
  const layoutFacts = figmaNodeLayoutFacts(node);
  const featureFacts = figmaNodeFeatureFacts(node);
  const componentProperties = withDefinedRecordEntries({
    definitions: node.componentPropertyDefinitions,
    references: node.componentPropertyReferences,
    values: node.componentProperties,
  });
  const allTokenRefs = Array.from(
    new Set([
      ...tokenRefs,
      ...Object.values(designTokenBindings).map((binding) => binding.tokenRef),
    ]),
  );
  return {
    options: {
      sourceDerived: true,
      sourceKind: "figma-template",
      sourceTemplateId: application.templateId,
      sourceDesignSystemId: application.designSystemId,
      sourceEnforcement: application.enforcement,
    },
    sourceDesign: {
      source: "figma-template",
      figmaNodeId: node.id,
      figmaNodeName: node.name,
      figmaNodeType: node.type,
      templateId: application.templateId,
      designSystemId: application.designSystemId,
      tokenRefs: allTokenRefs,
      ...(Object.keys(designTokenBindings).length > 0
        ? { designTokenBindings }
        : {}),
      ...(hasRecordEntries(layoutFacts) ? { layoutFacts } : {}),
      ...(hasRecordEntries(featureFacts) ? { featureFacts } : {}),
      ...(hasRecordEntries(componentProperties)
        ? {
            componentProperties,
            slotConstraints: {
              variantDependencies: componentProperties,
              replacementPolicy: "merge",
            },
          }
        : {}),
      enforcement: application.enforcement,
    },
    dataAttributes: {
      figmaNodeId: node.id,
      figmaTemplateApplied:
        application.status === "applied" || application.status === "partial",
    },
  };
}

function figmaNodeSlotSource(node: FigmaNode): string {
  return node.id ?? node.name ?? "figma-node";
}

function figmaTextSlotKind(node: FigmaNode): "text" | "heading" {
  const name = readString(node.name)?.toLowerCase() ?? "";
  return /(^|[\s_-])(heading|headline|title|h[1-6])($|[\s_-])/.test(name)
    ? "heading"
    : "text";
}

function componentSlotBinding(input: {
  slotId: string;
  nodeId: string;
  kind: string;
  sourceRole?: string;
  acceptedTypes?: string[];
  required?: boolean;
  editable?: boolean;
  visibility?: string;
  cardinality?: "one" | "many" | "optional";
  featureFacts?: Record<string, unknown>;
  slotConstraints?: Record<string, unknown>;
}): ComponentSlotBinding {
  return {
    slotId: input.slotId,
    nodeId: input.nodeId,
    kind: input.kind,
    sourceRole: input.sourceRole ?? `figma-${input.slotId}`,
    ...(input.acceptedTypes ? { acceptedTypes: input.acceptedTypes } : {}),
    required: input.required ?? input.slotId === "root",
    editable: input.editable ?? input.slotId !== "root",
    visibility:
      input.visibility ??
      (input.slotId === "root" ? "visible" : "transparent-overlay"),
    cardinality: input.cardinality ?? "one",
    ...(input.featureFacts ? { featureFacts: input.featureFacts } : {}),
    ...(input.slotConstraints
      ? { slotConstraints: input.slotConstraints }
      : {}),
  };
}

function rootSlotBinding(
  source: string,
  componentKind: string,
): ComponentSlotBinding {
  return componentSlotBinding({
    slotId: "root",
    nodeId: source,
    kind: componentKind,
    sourceRole: "component-root",
    acceptedTypes: [componentKind],
    required: true,
    editable: false,
    visibility: "visible",
  });
}

function figmaNodeComponentIdentityContract(
  node: FigmaNode,
  componentKind: string | null,
  children: readonly CogoElementRecord[] = [],
): {
  componentOntologyId?: string;
  variantId?: string;
  slotBindings?: ComponentSlotBindings;
} {
  if (node.type === "TEXT" || componentKind === "text") {
    const sourceNodeId = readString(node.id);
    if (!sourceNodeId) return {};
    const slotKind = figmaTextSlotKind(node);
    return {
      slotBindings: {
        content: componentSlotBinding({
          slotId: "content",
          nodeId: sourceNodeId,
          kind: slotKind,
          sourceRole: `editable-${slotKind}`,
          acceptedTypes: ["text", "heading"],
          required: true,
          editable: true,
          visibility: "visible",
        }),
      },
    };
  }
  if (!componentKind || !REAL_COMPONENT_KINDS.has(componentKind)) return {};
  const haystack = `${node.name ?? ""} ${node.type ?? ""}`.toLowerCase();
  const source = figmaNodeSlotSource(node);
  const slots: ComponentSlotBindings = {
    root: rootSlotBinding(source, componentKind),
  };
  const childText = children.find((child) => typeof child.text === "string");
  if (childText || node.type === "TEXT") {
    slots.label = componentSlotBinding({
      slotId: "label",
      nodeId: source,
      kind: "text",
      sourceRole: "editable-label",
      acceptedTypes: ["text", "heading"],
      required: Boolean(childText || node.type === "TEXT"),
      editable: true,
    });
  }

  if (componentKind === "button") {
    return {
      componentOntologyId: "cogo.component.button",
      variantId: childText
        ? "button.primary-label"
        : /\b(search|magnifier)\b/.test(haystack)
          ? "button.icon-search"
          : /\b(back|chevron|arrow)\b/.test(haystack)
            ? "button.icon-back"
            : /\b(fingerprint|biometric)\b/.test(haystack)
              ? "button.icon-biometric"
              : "button.default",
      slotBindings: slots,
    };
  }

  if (componentKind === "input") {
    return {
      componentOntologyId: "cogo.component.input",
      variantId: /\b(password|passcode|secure)\b/.test(haystack)
        ? "input.password-field"
        : /\b(search)\b/.test(haystack)
          ? "input.search-field"
          : "input.text-field",
      slotBindings: {
        ...slots,
        field: componentSlotBinding({
          slotId: "field",
          nodeId: source,
          kind: "input-field",
          sourceRole: "editable-field",
          acceptedTypes: ["input"],
          required: true,
          editable: false,
        }),
      },
    };
  }

  if (componentKind === "card") {
    return {
      componentOntologyId: "cogo.component.card",
      variantId: /\b(product|bike|helmet|road)\b/.test(haystack)
        ? "card.product-tile"
        : /\b(promo|promotion|offer|discount)\b/.test(haystack)
          ? "card.promotional-hero"
          : "card.surface",
      slotBindings: slots,
    };
  }

  if (componentKind === "tabs") {
    return {
      componentOntologyId: "cogo.component.segmented-tabs",
      variantId: "segmented-tabs.category-filter",
      slotBindings: {
        ...slots,
        items: componentSlotBinding({
          slotId: "items",
          nodeId: source,
          kind: "collection",
          sourceRole: "tab-items",
          acceptedTypes: ["icon", "button"],
          required: true,
          editable: false,
          cardinality: "many",
        }),
        selectedItem: componentSlotBinding({
          slotId: "selectedItem",
          nodeId: source,
          kind: "state",
          sourceRole: "selected-tab",
          required: false,
          editable: true,
          cardinality: "optional",
        }),
      },
    };
  }

  if (componentKind === "nav") {
    return {
      componentOntologyId: /\b(status bar|mobile status|system status)\b/.test(
        haystack,
      )
        ? "cogo.component.mobile-status-bar"
        : "cogo.component.bottom-tabs",
      variantId: /\b(status bar|mobile status|system status)\b/.test(haystack)
        ? "mobile-status-bar.default"
        : "bottom-tabs.mobile",
      slotBindings: {
        ...slots,
        items: componentSlotBinding({
          slotId: "items",
          nodeId: source,
          kind: "collection",
          sourceRole: "navigation-items",
          acceptedTypes: ["icon", "button"],
          required: true,
          editable: false,
          cardinality: "many",
        }),
        activeItem: componentSlotBinding({
          slotId: "activeItem",
          nodeId: source,
          kind: "state",
          sourceRole: "active-navigation-item",
          required: false,
          editable: true,
          cardinality: "optional",
        }),
      },
    };
  }

  return {
    componentOntologyId: `cogo.component.${componentKind}`,
    variantId: `${componentKind}.default`,
    slotBindings: slots,
  };
}

function withFigmaNodeIdentity(
  node: FigmaNode,
  componentKind: string | null,
  element: CogoElementRecord,
  children: readonly CogoElementRecord[] = [],
): CogoElementRecord {
  const identity = figmaNodeComponentIdentityContract(
    node,
    componentKind,
    children,
  );
  if (!identity.slotBindings) return element;
  return {
    ...element,
    ...(identity.componentOntologyId
      ? { componentOntologyId: identity.componentOntologyId }
      : {}),
    ...(identity.variantId ? { variantId: identity.variantId } : {}),
    slotBindings: identity.slotBindings,
    sourceDesign: {
      ...readRecord(element.sourceDesign),
      ...(identity.componentOntologyId
        ? { componentOntologyId: identity.componentOntologyId }
        : {}),
      ...(identity.variantId ? { variantId: identity.variantId } : {}),
      slotBindings: identity.slotBindings,
    },
    ...(identity.componentOntologyId && identity.variantId
      ? {
          dataAttributes: {
            ...readRecord(element.dataAttributes),
            figmaComponentOntologyId: identity.componentOntologyId,
            figmaComponentVariantId: identity.variantId,
          },
        }
      : {}),
  };
}

function containerStyleForNode(
  node: FigmaNode,
  options: { isRoot?: boolean } = {},
): {
  style: CogoInlineStyle;
  tokenRefs: string[];
} {
  const tokenRefs: string[] = [];
  const style: CogoInlineStyle = {};
  const fillColor = cssColorFromPaints(node.fills);
  if (fillColor) {
    style.backgroundColor = options.isRoot ? "$color.background" : fillColor;
    tokenRefs.push("$color.background");
  }
  const gradientCss = gradientBackgroundIfTop(node);
  if (gradientCss) {
    style.backgroundImage = gradientCss;
    tokenRefs.push("$color.gradient");
  }
  if (typeof node.cornerRadius === "number") {
    style.borderRadius = "$radius.lg";
    tokenRefs.push("$radius.lg");
  }
  if (firstShadowCss(node)) {
    style.boxShadow = "$shadow.template";
    tokenRefs.push("$shadow.template");
  }
  if (firstVisibleSolidPaint(node.strokes)) {
    style.borderColor = "$color.border";
    style.borderWidth = 1;
    style.borderStyle = "solid";
    tokenRefs.push("$color.border");
  }
  return { style, tokenRefs };
}

function textStyleForNode(node: FigmaNode): {
  style: CogoInlineStyle;
  tokenRefs: string[];
} {
  const tokenRefs: string[] = ["$color.foreground"];
  const style: CogoInlineStyle = {
    color: cssColorFromPaints(node.fills) ?? "$color.foreground",
  };
  if (node.style?.fontFamily) {
    style.fontFamily = `"${node.style.fontFamily}", system-ui, sans-serif`;
    tokenRefs.push("$type.heading");
  }
  if (typeof node.style?.fontWeight === "number") {
    style.fontWeight = node.style.fontWeight;
    tokenRefs.push("$type.heading");
  }
  if (typeof node.style?.fontSize === "number") {
    style.fontSize = px(node.style.fontSize) ?? `${node.style.fontSize}px`;
    tokenRefs.push("$type.heading");
  }
  if (typeof node.style?.lineHeightPx === "number") {
    style.lineHeight =
      px(node.style.lineHeightPx) ?? `${node.style.lineHeightPx}px`;
    tokenRefs.push("$type.heading");
  }
  if (FIGMA_TEXT_BASELINE_OFFSET_PX !== 0) {
    style.transform = `translateY(${FIGMA_TEXT_BASELINE_OFFSET_PX}px)`;
  }
  if (node.characters?.includes("\n")) {
    style.whiteSpace = "pre-line";
  }
  if (node.style?.textTruncation === "ENDING" || node.style?.maxLines === 1) {
    style.whiteSpace = "nowrap";
    style.overflow = "hidden";
    style.textOverflow = "ellipsis";
  }
  return { style, tokenRefs: Array.from(new Set(tokenRefs)) };
}

function compactBindings(
  entries: Array<[string, FigmaDesignTokenBinding | undefined]>,
): FigmaDesignTokenBindings {
  return Object.fromEntries(
    entries.filter((entry): entry is [string, FigmaDesignTokenBinding] =>
      Boolean(entry[1]),
    ),
  );
}

function buttonStyleForNode(node: FigmaNode): {
  style: CogoInlineStyle;
  tokenRefs: string[];
  designTokenBindings?: FigmaDesignTokenBindings;
} {
  const haystack = `${node.name ?? ""} ${node.type ?? ""}`.toLowerCase();
  const solidFill = cssColorFromPaints(node.fills);
  const isLink = /\blink\b/.test(haystack);
  const isDisabled = /\b(disabled|disable|dissable)\b/.test(haystack);
  const fallbackBackground = isLink
    ? "transparent"
    : isDisabled
      ? "rgb(239, 237, 247)"
      : "transparent";
  const backgroundColor = solidFill ?? fallbackBackground;
  const foregroundColor = isLink
    ? "rgb(54, 41, 183)"
    : solidFill
      ? "$color.primaryForeground"
      : isDisabled
        ? "rgb(255, 255, 255)"
        : "$color.primaryForeground";
  const radius =
    typeof node.cornerRadius === "number"
      ? (px(node.cornerRadius) ?? "$radius.lg")
      : "$radius.lg";
  const tokenRefs: string[] = [
    ...(isTokenReference(backgroundColor)
      ? [backgroundColor]
      : backgroundColor === "transparent"
        ? []
        : [tokenRef("color", backgroundColor)].filter(
            (value): value is string => Boolean(value),
          )),
    ...(isTokenReference(foregroundColor)
      ? [foregroundColor]
      : [tokenRef("color", foregroundColor)].filter((value): value is string =>
          Boolean(value),
        )),
    ...(isTokenReference(radius)
      ? [radius]
      : [tokenRef("radius", radius)].filter((value): value is string =>
          Boolean(value),
        )),
  ];
  const designTokenBindings = compactBindings([
    [
      "backgroundColor",
      solidFill
        ? {
            tokenRef: tokenRef("color", solidFill) ?? "$color.primary",
            resolvedValue: solidFill,
          }
        : backgroundColor === "transparent"
          ? undefined
          : designTokenBinding("color", backgroundColor),
    ],
    [
      "color",
      solidFill
        ? {
            tokenRef: "$color.primaryForeground",
            resolvedValue: contrastTextForColor(solidFill),
          }
        : designTokenBinding("color", foregroundColor),
    ],
    ["borderRadius", designTokenBinding("radius", radius)],
  ]);
  const style: CogoInlineStyle = {
    backgroundColor,
    color: foregroundColor,
    borderRadius: radius,
  };
  // A real Figma CTA/button can carry a gradient fill (this branch runs before
  // containerStyleForNode would); apply it only when it is the top-most visible fill.
  const gradientCss = gradientBackgroundIfTop(node);
  if (gradientCss) {
    style.backgroundImage = gradientCss;
    const binding = designTokenBinding("color.gradient", gradientCss);
    if (binding) {
      designTokenBindings.backgroundImage = binding;
      tokenRefs.push(binding.tokenRef);
    }
  }
  if (firstShadowCss(node)) {
    const shadow = firstShadowCss(node);
    if (shadow) {
      style.boxShadow = shadow;
      const binding = designTokenBinding("shadow", shadow);
      if (binding) {
        designTokenBindings.boxShadow = binding;
        tokenRefs.push(binding.tokenRef);
      }
    }
  }
  return {
    style,
    tokenRefs: Array.from(new Set(tokenRefs)),
    designTokenBindings,
  };
}

function inputStyleForNode(node: FigmaNode): {
  style: CogoInlineStyle;
  tokenRefs: string[];
  designTokenBindings: FigmaDesignTokenBindings;
} {
  const solidFill = cssColorFromPaints(node.fills) ?? "rgb(255, 255, 255)";
  const stroke = cssColorFromPaints(node.strokes) ?? "rgb(229, 229, 229)";
  const radius =
    typeof node.cornerRadius === "number"
      ? (px(node.cornerRadius) ?? "15px")
      : "15px";
  const style: CogoInlineStyle = {
    backgroundColor: solidFill,
    borderColor: stroke,
    borderStyle: "solid",
    borderWidth: 1,
    borderRadius: radius,
    color: "rgb(202, 202, 202)",
  };
  const designTokenBindings = compactBindings([
    ["backgroundColor", designTokenBinding("color", solidFill)],
    ["borderColor", designTokenBinding("color.border", stroke)],
    ["borderRadius", designTokenBinding("radius", radius)],
    ["color", designTokenBinding("color", style.color)],
  ]);
  return {
    style,
    tokenRefs: Array.from(
      new Set(
        Object.values(designTokenBindings).map((binding) => binding.tokenRef),
      ),
    ),
    designTokenBindings,
  };
}

function buttonTextForNode(
  children: readonly CogoElementRecord[] = [],
): string | undefined {
  const childText = firstMeaningfulText(children);
  if (
    typeof childText === "string" &&
    childText.trim() &&
    !isGenericFigmaLayerName(childText)
  ) {
    return childText;
  }
  return undefined;
}

function ctaIdentityForElement(element: CogoElementRecord): string {
  const dataAttributes = readRecord(element.dataAttributes);
  const sourceDesign = readRecord(element.sourceDesign);
  return [
    readString(element.id),
    readString(element.name),
    readString(dataAttributes.figmaNodeId),
    readString(sourceDesign.figmaNodeName),
    readString(sourceDesign.figmaNodeId),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function ctaTextForElement(element: CogoElementRecord): string | undefined {
  const text = readString(element.text);
  if (
    text &&
    /^(확인|완료|다음|계속|시작|네|예|동의|계좌\s*선택\s*완료|continue|next|done|yes|agree)$/i.test(
      text,
    )
  ) {
    return text;
  }
  return undefined;
}

function isCtaBackgroundElement(element: CogoElementRecord): boolean {
  const identity = ctaIdentityForElement(element);
  return /(^|[-_: ])cta($|[-_: ])|button|primary-action/.test(identity);
}

function ctaBoxForElement(
  element: CogoElementRecord,
): { left: number; top: number; width: number; height: number } | null {
  const style = readRecord(element.style);
  const left = cssNumber(style.left);
  const top = cssNumber(style.top);
  const width = cssNumber(style.width);
  const height = cssNumber(style.height);
  if (left === undefined || top === undefined || !width || !height) return null;
  return { left, top, width, height };
}

function cssNumber(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value !== "string") return undefined;
  const match = value.trim().match(/^-?\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : undefined;
}

function isLikelyCtaBackgroundForLabel(
  background: CogoElementRecord,
  label: CogoElementRecord,
): boolean {
  if (readString(background.type) === "text") return false;
  const backgroundBox = ctaBoxForElement(background);
  const labelBox = ctaBoxForElement(label);
  if (!backgroundBox || !labelBox) return false;
  if (backgroundBox.width < 44 || backgroundBox.height < 30) return false;
  const style = readRecord(background.style);
  const fill = [
    readString(style.background),
    readString(style.backgroundColor),
    readString(style.borderColor),
  ]
    .filter(Boolean)
    .join(" ");
  if (
    !fill ||
    /transparent|rgba\(\s*0\s*,\s*0\s*,\s*0\s*,\s*0\s*\)/i.test(fill)
  ) {
    return false;
  }
  const labelCenterX = labelBox.left + labelBox.width / 2;
  const labelCenterY = labelBox.top + labelBox.height / 2;
  return (
    labelCenterX >= backgroundBox.left - 2 &&
    labelCenterX <= backgroundBox.left + backgroundBox.width + 2 &&
    labelCenterY >= backgroundBox.top - 2 &&
    labelCenterY <= backgroundBox.top + backgroundBox.height + 2
  );
}

function mergeSiblingCtaElements(
  children: readonly CogoElementRecord[],
): CogoElementRecord[] {
  const labelByBackground = new Map<
    number,
    { labelIndex: number; label: CogoElementRecord; labelText: string }
  >();
  const usedLabels = new Set<number>();

  children.forEach((candidateLabel, labelIndex) => {
    const labelText = candidateLabel
      ? ctaTextForElement(candidateLabel)
      : undefined;
    if (!candidateLabel || !labelText) return;
    const backgroundIndex = children
      .map((candidate, candidateIndex) => ({ candidate, candidateIndex }))
      .filter(({ candidate, candidateIndex }) => {
        if (!candidate || candidateIndex === labelIndex) return false;
        if (labelByBackground.has(candidateIndex)) return false;
        if (Math.abs(candidateIndex - labelIndex) > 8) return false;
        return (
          isCtaBackgroundElement(candidate) ||
          isLikelyCtaBackgroundForLabel(candidate, candidateLabel)
        );
      })
      .sort(
        (a, b) =>
          Math.abs(a.candidateIndex - labelIndex) -
          Math.abs(b.candidateIndex - labelIndex),
      )[0]?.candidateIndex;
    if (backgroundIndex === undefined) return;
    labelByBackground.set(backgroundIndex, {
      labelIndex,
      label: candidateLabel,
      labelText,
    });
    usedLabels.add(labelIndex);
  });

  const merged: CogoElementRecord[] = [];
  for (let index = 0; index < children.length; index += 1) {
    if (usedLabels.has(index)) continue;
    const child = children[index];
    if (!child) continue;
    const mergedLabel = labelByBackground.get(index);
    if (mergedLabel) {
      const label = mergedLabel.label;
      const labelText = mergedLabel.labelText;
      const labelStyle = readRecord(label.style);
      const buttonStyle = {
        ...readRecord(child.style),
        color: labelStyle.color ?? "$color.primaryForeground",
        fontSize: labelStyle.fontSize,
        fontWeight: labelStyle.fontWeight,
        lineHeight: labelStyle.lineHeight,
        letterSpacing: labelStyle.letterSpacing,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        cursor: "pointer",
      };
      const button: CogoElementRecord = {
        ...child,
        type: "button",
        text: labelText,
        className:
          "inline-flex items-center justify-center gap-2 whitespace-nowrap border-0 px-5 py-3 font-medium shadow-none",
        style: buttonStyle,
        dataAttributes: {
          ...readRecord(child.dataAttributes),
          cogoMergedCtaLabelNodeId: readString(
            readRecord(label.dataAttributes).figmaNodeId,
          ),
        },
      };
      delete button.children;
      merged.push(button);
      continue;
    }
    merged.push(child);
  }
  return merged;
}

function firstMeaningfulText(
  children: readonly CogoElementRecord[] = [],
): string | undefined {
  for (const child of children) {
    if (
      typeof child.text === "string" &&
      child.text.trim() &&
      !isGenericFigmaLayerName(child.text)
    ) {
      return child.text;
    }
    const nested = firstMeaningfulText(
      Array.isArray(child.children) ? child.children : [],
    );
    if (nested) return nested;
  }
  return undefined;
}

function isGenericFigmaLayerName(value: string): boolean {
  return /^(text|label|button|icon|vector|rectangle|ellipse|shape|group|\d+)$/i.test(
    value.trim(),
  );
}

function isNonVisualBoundsLayer(
  node: FigmaNode,
  parentNode?: FigmaNode,
): boolean {
  if (node.type !== "RECTANGLE") return false;
  if (!/^(area|hit[-_\s]?area|bounds)$/i.test((node.name ?? "").trim())) {
    return false;
  }
  if (
    !parentNode ||
    !["GROUP", "FRAME", "INSTANCE"].includes(parentNode.type ?? "")
  ) {
    return false;
  }
  const nodeBox = node.absoluteBoundingBox;
  const parentBox = parentNode.absoluteBoundingBox;
  if (!nodeBox || !parentBox) return false;
  const sameBox =
    Math.abs((nodeBox.width ?? 0) - (parentBox.width ?? 0)) < 0.01 &&
    Math.abs((nodeBox.height ?? 0) - (parentBox.height ?? 0)) < 0.01;
  if (!sameBox) return false;
  if ((node.strokes ?? []).some((paint) => isPaintRenderable(paint))) {
    return false;
  }
  if ((node.effects ?? []).some((effect) => effect.visible !== false)) {
    return false;
  }
  return node.absoluteRenderBounds == null;
}

function sourceVectorPayloadStyleForNode(
  node: FigmaNode,
  parentNode?: FigmaNode,
): CogoInlineStyle {
  const vectorPayload = renderAssemblyVectorFromFeatureFacts(
    figmaNodeFeatureFacts(node),
    node.id,
  );
  if (!vectorPayload) return {};
  const transformMatrix = sourceVectorTransformMatrix(node, parentNode);
  const paths = transformMatrix
    ? readArray<Record<string, unknown>>(vectorPayload.paths).map((path) => ({
        ...path,
        transform: { matrix: transformMatrix },
      }))
    : vectorPayload.paths;
  return {
    vectorPayload: withDefinedRecordEntries({
      ...vectorPayload,
      paths,
      width: node.absoluteBoundingBox?.width,
      height: node.absoluteBoundingBox?.height,
    }),
  };
}

function sourceVectorTransformMatrix(
  node: FigmaNode,
  parentNode?: FigmaNode,
): number[] | undefined {
  const matrix = node.relativeTransform;
  if (!Array.isArray(matrix) || matrix.length !== 2) return undefined;
  const row0 = matrix[0] ?? [];
  const row1 = matrix[1] ?? [];
  const a = readNumber(row0[0]);
  const b = readNumber(row0[1]);
  const e = readNumber(row0[2]);
  const c = readNumber(row1[0]);
  const d = readNumber(row1[1]);
  const f = readNumber(row1[2]);
  if ([a, b, c, d, e, f].some((value) => value === undefined)) {
    return undefined;
  }
  const isIdentity =
    Math.abs((a as number) - 1) < 0.0001 &&
    Math.abs(b as number) < 0.0001 &&
    Math.abs(c as number) < 0.0001 &&
    Math.abs((d as number) - 1) < 0.0001;
  if (isIdentity) return undefined;
  const parentBox = parentNode?.absoluteBoundingBox;
  const nodeBox = node.absoluteBoundingBox;
  const relativeLeft =
    nodeBox && parentBox ? (nodeBox.x ?? 0) - (parentBox.x ?? 0) : 0;
  const relativeTop =
    nodeBox && parentBox ? (nodeBox.y ?? 0) - (parentBox.y ?? 0) : 0;
  // Figma relativeTransform is [[a, b, tx], [c, d, ty]] in parent coordinates.
  // The element wrapper is already positioned at absoluteBoundingBox, so translate
  // the vector path back into that local viewBox.
  return [
    a as number,
    c as number,
    b as number,
    d as number,
    (e as number) - relativeLeft,
    (f as number) - relativeTop,
  ].map((value) => Number(value.toFixed(6)));
}

function convertNode(
  node: FigmaNode,
  path: string,
  unsupportedNodes: string[],
  designTemplateApplication: FigmaFrameDesignTemplateApplicationSummary,
  parentNode?: FigmaNode,
): CogoElementRecord | null {
  if (node.visible === false) return null;
  if (isNonVisualBoundsLayer(node, parentNode)) return null;
  const type = node.type ?? "UNKNOWN";
  const id = safeElementId(node, path);
  const name = node.name ?? type;
  const layoutStyle = layoutStyleForNode(node, parentNode);

  if (type === "TEXT") {
    const { style, tokenRefs } = textStyleForNode(node);
    return {
      id,
      type: "text",
      text: node.characters?.trim() || name,
      className: "block",
      style: { ...style, ...layoutStyle },
      ...sourceDesignMetadata(node, designTemplateApplication, tokenRefs),
    };
  }

  if (isContainerType(type)) {
    const componentKind = componentKindForNode(node);
    const children = mergeSiblingCtaElements(
      (node.children ?? [])
        .map((child, index) =>
          convertNode(
            child,
            `${id}-${index}`,
            unsupportedNodes,
            designTemplateApplication,
            node,
          ),
        )
        .filter((child): child is CogoElementRecord => Boolean(child)),
    );
    if (componentKind === "button") {
      const { style, tokenRefs, designTokenBindings } =
        buttonStyleForNode(node);
      const buttonText = buttonTextForNode(children);
      return withFigmaNodeIdentity(
        node,
        componentKind,
        {
          id,
          type: "button",
          ...(children.length > 0
            ? { children }
            : buttonText
              ? { text: buttonText }
              : {}),
          figmaType: type,
          name,
          className: "relative block overflow-hidden border-0 p-0 shadow-none",
          style: { ...style, ...layoutStyle },
          ...sourceDesignMetadata(
            node,
            designTemplateApplication,
            tokenRefs,
            designTokenBindings,
          ),
        },
        children,
      );
    }
    if (componentKind === "input") {
      const { style, tokenRefs, designTokenBindings } = inputStyleForNode(node);
      return withFigmaNodeIdentity(node, componentKind, {
        id,
        type: "input",
        placeholder: name,
        figmaType: type,
        name,
        style: { ...style, ...layoutStyle },
        ...sourceDesignMetadata(
          node,
          designTemplateApplication,
          tokenRefs,
          designTokenBindings,
        ),
      });
    }
    const { style, tokenRefs } = containerStyleForNode(node, {
      isRoot: !parentNode,
    });
    return withFigmaNodeIdentity(
      node,
      componentKind,
      {
        id,
        type: "container",
        figmaType: type,
        name,
        componentKind,
        className: hasUsableBox(node)
          ? "relative"
          : type === "FRAME"
            ? "space-y-4 p-6"
            : "space-y-3",
        style: { ...style, ...layoutStyle },
        ...sourceDesignMetadata(node, designTemplateApplication, tokenRefs),
        ...(children.length > 0 ? { children } : {}),
      },
      children,
    );
  }

  if (isShapeType(type)) {
    const componentKind = componentKindForNode(node);
    if (componentKind === "button") {
      const { style, tokenRefs, designTokenBindings } =
        buttonStyleForNode(node);
      const buttonText = buttonTextForNode();
      return withFigmaNodeIdentity(node, componentKind, {
        id,
        type: "button",
        ...(buttonText ? { text: buttonText } : {}),
        figmaType: type,
        name,
        className:
          "inline-flex items-center justify-center gap-2 whitespace-nowrap border-0 px-5 py-3 font-medium shadow-none",
        style: { ...style, ...layoutStyle },
        ...sourceDesignMetadata(
          node,
          designTemplateApplication,
          tokenRefs,
          designTokenBindings,
        ),
      });
    }
    const { style, tokenRefs } = containerStyleForNode(node);
    const vectorPayloadStyle = sourceVectorPayloadStyleForNode(
      node,
      parentNode,
    );
    return withFigmaNodeIdentity(node, componentKind, {
      id,
      type: "shape",
      figmaType: type,
      name,
      componentKind,
      ...(hasUsableBox(node) ? {} : { className: "min-h-10" }),
      style: { ...style, ...vectorPayloadStyle, ...layoutStyle },
      ...sourceDesignMetadata(node, designTemplateApplication, tokenRefs),
    });
  }

  const ref = unsupportedNodeRef(node, path);
  if (!unsupportedNodes.includes(ref)) unsupportedNodes.push(ref);
  return {
    id,
    type: "container",
    figmaType: type,
    name,
    className: "rounded-md border border-dashed p-3 text-muted-foreground",
    style: layoutStyle,
    ...sourceDesignMetadata(node, designTemplateApplication, []),
    children: [
      {
        id: `${id}-unsupported`,
        type: "text",
        text: `Unsupported Figma node: ${name}`,
      },
    ],
  };
}

function compactReferenceParitySummary(
  report: FigmaTemplateReferenceParityReport | undefined,
): Record<string, unknown> | undefined {
  if (!report) return undefined;
  return {
    schemaVersion: report.schemaVersion,
    status: report.status,
    score: report.score,
    threshold: report.threshold,
    sourceNodeCount: report.sourceNodeCount,
    matchedNodeCount: report.matchedNodeCount,
    missingSourceNodeCount: report.missingSourceNodeRefs.length,
    metricCount: report.metrics.length,
    evidenceRefCount: report.evidenceRefs.length,
    reasons: report.reasons.slice(0, 8),
  };
}

function compactQualityReadinessSummary(
  readiness: FigmaTemplateQualityReadiness | undefined,
): Record<string, unknown> | undefined {
  if (!readiness) return undefined;
  return {
    schemaVersion: readiness.schemaVersion,
    status: readiness.status,
    score: readiness.score,
    threshold: readiness.threshold,
    metricCount: readiness.metrics?.length ?? 0,
    failedMetricCount:
      readiness.metrics?.filter((metric) => metric.status === "fail").length ??
      0,
    evidenceLimits: readiness.evidenceLimits?.slice(0, 8) ?? [],
  };
}

function compactOntologyValidationSummary(
  summary: FigmaFrameOntologyValidationSummary,
): Record<string, unknown> {
  return {
    schemaVersion: summary.schemaVersion,
    status: summary.status,
    sourceCount: summary.sourceCount,
    admittedSourceCount: summary.admittedSourceCount,
    blockedSourceCount: summary.blockedSourceCount,
    admittedCaseCount: summary.admittedCaseCount,
    mappingCaseCount: summary.mappingCaseCount,
    nodeMappingCount: summary.nodeMappingCount,
    requirementMappingCount: summary.requirementMappingCount,
    chunkMappingCount: summary.chunkMappingCount,
    tokenMappingCount: summary.tokenMappingCount,
    actionMappingCount: summary.actionMappingCount,
    dataInputMappingCount: summary.dataInputMappingCount,
    hardConstraints: summary.hardConstraints.slice(0, 24),
    qualityReadiness: compactQualityReadinessSummary(summary.qualityReadiness),
  };
}

function compactDesignTemplateApplicationSummary(
  application: FigmaFrameDesignTemplateApplicationSummary,
): Record<string, unknown> {
  return {
    schemaVersion: application.schemaVersion,
    status: application.status,
    source: application.source,
    enforcement: application.enforcement,
    templateId: application.templateId,
    designSystemId: application.designSystemId,
    hardConstraints: application.hardConstraints.slice(0, 24),
    tokenRefs: application.tokenRefs.slice(0, 48),
    appliedNodeCount: application.appliedNodeCount,
    styledNodeCount: application.styledNodeCount,
    textStyleNodeCount: application.textStyleNodeCount,
    componentStyleNodeCount: application.componentStyleNodeCount,
    styleSignature: application.styleSignature,
    qualityReadiness: compactQualityReadinessSummary(
      application.qualityReadiness,
    ),
    referenceParity: compactReferenceParitySummary(application.referenceParity),
    reasons: application.reasons.slice(0, 8),
  };
}

function figmaBundle(params: {
  figmaFileId: string;
  figmaNodeId: string;
  frameName: string;
  sourceFrame?: FigmaSourceFrameSummary;
  root: CogoElementRecord;
  mapping: FigmaFrameImportResult["mapping"];
  tokens: Record<string, unknown>;
  designTemplateApplication: FigmaFrameDesignTemplateApplicationSummary;
}): Record<string, unknown> {
  const appId = safeId(`figma-${params.frameName}`);
  const root = uniqueCogoElementTreeIds(
    params.root,
    `${params.figmaFileId}:${params.figmaNodeId}:${params.frameName}`,
  );
  const sourceFrame = params.sourceFrame;
  const sourceFrameRecord = sourceFrame
    ? {
        schemaVersion: sourceFrame.schemaVersion,
        platform: sourceFrame.platform,
        width: sourceFrame.width,
        height: sourceFrame.height,
        figmaFileId: sourceFrame.figmaFileId,
        figmaNodeId: sourceFrame.figmaNodeId,
        frameName: sourceFrame.frameName,
      }
    : undefined;
  const mobileOnly =
    sourceFrame?.platform === "mobile" ||
    (typeof sourceFrame?.width === "number" && sourceFrame.width <= 520);
  const sourceConversion = {
    sourceType: "figma",
    figmaFileId: params.figmaFileId,
    figmaNodeId: params.figmaNodeId,
    frameName: params.frameName,
    ...(sourceFrameRecord ? { sourceFrame: sourceFrameRecord } : {}),
    ...(mobileOnly
      ? {
          mobileOnly: true,
          defaultDevice: "mobile",
        }
      : {}),
  };
  const studioViewport =
    sourceFrame && mobileOnly
      ? {
          defaultDevice: "mobile",
          defaultViewport: "mobile",
          viewport: {
            width: sourceFrame.width,
            height: sourceFrame.height,
          },
        }
      : undefined;
  const compactDesignTemplateApplication =
    compactDesignTemplateApplicationSummary(params.designTemplateApplication);
  const compactOntologyValidation = compactOntologyValidationSummary(
    params.mapping.ontologyValidation,
  );
  const compactReferenceParity = compactReferenceParitySummary(
    params.designTemplateApplication.referenceParity,
  );
  const generationEvidence = {
    schemaVersion: "cogo-xyz-figma-generation-evidence.v1",
    generatedApp: true,
    source: "figma-template",
    uiOntology: {
      status: params.mapping.ontologyValidation.status,
      designTemplateApplication: compactDesignTemplateApplication,
      referenceParity: compactReferenceParity,
    },
    ontologyValidation: compactOntologyValidation,
    referenceParity: compactReferenceParity,
  };
  return {
    schemaVersion: "cogo-application/v1",
    manifest: {
      appId,
      name: params.frameName,
      sourceConversion,
      ...(studioViewport ? { studio: studioViewport } : {}),
      pages: [
        {
          id: "home",
          route: "/",
          blocks: ["figma-frame"],
          ...(sourceFrameRecord ? { sourceFrame: sourceFrameRecord } : {}),
          ...(sourceFrame && mobileOnly
            ? {
                viewport: {
                  width: sourceFrame.width,
                  height: sourceFrame.height,
                },
                device: "mobile",
              }
            : {}),
        },
      ],
    },
    tokens: params.tokens,
    designSystem: {
      schemaVersion: "cogo.figma-derived-design-system.v1",
      source: "figma-template",
      templateId: params.designTemplateApplication.templateId,
      designSystemId: params.designTemplateApplication.designSystemId,
      enforcement: params.designTemplateApplication.enforcement,
      hardConstraints: params.designTemplateApplication.hardConstraints,
      tokenRefs: params.designTemplateApplication.tokenRefs,
      tokens: params.tokens,
      styleSignature: params.designTemplateApplication.styleSignature,
      qualityReadiness: compactQualityReadinessSummary(
        params.designTemplateApplication.qualityReadiness,
      ),
      referenceParity: compactReferenceParity,
    },
    blocks: {
      "figma-frame": {
        meta: {
          id: "figma-frame",
          name: params.frameName,
          sourceType: "figma_frame",
          designTemplateApplication: compactDesignTemplateApplication,
          referenceParity: compactReferenceParity,
        },
        ui: {
          schemaVersion: "3.0",
          root,
        },
      },
    },
    metadata: {
      cogoXyzFigmaImport: {
        figmaFileId: params.figmaFileId,
        figmaNodeId: params.figmaNodeId,
        frameName: params.frameName,
        ...(sourceFrameRecord ? { sourceFrame: sourceFrameRecord } : {}),
        matchedNodes: params.mapping.matchedNodes,
        unsupportedNodeCount: params.mapping.unsupportedNodeCount,
        unsupportedNodeSample: params.mapping.unsupportedNodes.slice(0, 50),
        extractedTokenCount: params.mapping.extractedTokens.length,
        extractedTokens: params.mapping.extractedTokens.slice(0, 64),
        matchedComponents: params.mapping.matchedComponents,
        componentMatchRate: params.mapping.componentMatchRate,
        textNodeCount: params.mapping.textNodeCount,
        nodeCount: params.mapping.nodeCount,
        sourceFrameSelection: params.mapping.sourceFrameSelection,
        ontologyValidation: compactOntologyValidation,
        referenceParity: compactReferenceParity,
        designTemplateApplication: compactDesignTemplateApplication,
      },
      generationEvidence,
      referenceParity: compactReferenceParity,
    },
    ontology: {
      figmaTemplate: {
        schemaVersion: FIGMA_TEMPLATE_APPLICATION_SCHEMA_VERSION,
        templateId: params.designTemplateApplication.templateId,
        designSystemId: params.designTemplateApplication.designSystemId,
        hardConstraints: params.designTemplateApplication.hardConstraints,
        tokenRefs: params.designTemplateApplication.tokenRefs,
        referenceParity: compactReferenceParity,
      },
    },
  };
}

function cloneJsonRecord(value: unknown): Record<string, unknown> {
  return readRecord(JSON.parse(JSON.stringify(value ?? {})));
}

function sourceFrameRecord(
  sourceFrame: FigmaSourceFrameSummary | undefined,
): Record<string, unknown> | undefined {
  if (!sourceFrame) return undefined;
  return {
    schemaVersion: sourceFrame.schemaVersion,
    platform: sourceFrame.platform,
    width: sourceFrame.width,
    height: sourceFrame.height,
    figmaFileId: sourceFrame.figmaFileId,
    figmaNodeId: sourceFrame.figmaNodeId,
    frameName: sourceFrame.frameName,
  };
}

function hasFilledBackground(element: Record<string, unknown>): boolean {
  const style = readRecord(element.style);
  const fill = [
    readString(style.background),
    readString(style.backgroundColor),
    readString(style.borderColor),
  ]
    .filter(Boolean)
    .join(" ");
  return Boolean(
    fill && !/transparent|rgba\(\s*0\s*,\s*0\s*,\s*0\s*,\s*0\s*\)/i.test(fill),
  );
}

function ctaArea(box: { width: number; height: number }): number {
  return box.width * box.height;
}

function sourceNodeIdForElement(
  element: Record<string, unknown>,
): string | undefined {
  const dataAttributes = readRecord(element.dataAttributes);
  const sourceDesign = readRecord(element.sourceDesign);
  return (
    readString(dataAttributes.figmaNodeId) ??
    readString(sourceDesign.figmaNodeId)
  );
}

function sourceNodeIdContainsDescendant(
  candidate: Record<string, unknown>,
  target: Record<string, unknown>,
): boolean {
  const candidateId = sourceNodeIdForElement(candidate);
  const targetId = sourceNodeIdForElement(target);
  return Boolean(
    candidateId &&
    targetId &&
    targetId !== candidateId &&
    (targetId.startsWith(`${candidateId};`) ||
      targetId.startsWith(`${candidateId}:`) ||
      targetId.startsWith(`${candidateId}/`)),
  );
}

function isFilledTapTargetBox(candidate: Record<string, unknown>): boolean {
  if (readString(candidate.type) === "text") return false;
  const box = ctaBoxForElement(candidate);
  return Boolean(
    box &&
    box.width >= 44 &&
    box.height >= 30 &&
    hasFilledBackground(candidate),
  );
}

function findCoveringTapTarget(
  target: Record<string, unknown>,
  candidates: Record<string, unknown>[],
): Record<string, unknown> | undefined {
  const targetBox = ctaBoxForElement(target);
  if (!targetBox) return undefined;
  const targetCenterX = targetBox.left + targetBox.width / 2;
  const targetCenterY = targetBox.top + targetBox.height / 2;
  return candidates
    .filter((candidate) => {
      if (candidate === target || !isFilledTapTargetBox(candidate))
        return false;
      const candidateBox = ctaBoxForElement(candidate);
      if (!candidateBox) return false;
      const sameSourceBranch = sourceNodeIdContainsDescendant(
        candidate,
        target,
      );
      const coversTextCenter =
        targetCenterX >= candidateBox.left - 2 &&
        targetCenterX <= candidateBox.left + candidateBox.width + 2 &&
        targetCenterY >= candidateBox.top - 2 &&
        targetCenterY <= candidateBox.top + candidateBox.height + 2;
      return sameSourceBranch || coversTextCenter;
    })
    .sort((a, b) => {
      const aPrefix = sourceNodeIdContainsDescendant(a, target) ? 0 : 1;
      const bPrefix = sourceNodeIdContainsDescendant(b, target) ? 0 : 1;
      if (aPrefix !== bPrefix) return aPrefix - bPrefix;
      return ctaArea(ctaBoxForElement(a)!) - ctaArea(ctaBoxForElement(b)!);
    })[0];
}

function disablePointerEventsForTapTargetDescendants(
  tapTarget: Record<string, unknown>,
  candidates: Record<string, unknown>[],
): void {
  for (const candidate of candidates) {
    if (candidate === tapTarget) continue;
    if (!sourceNodeIdContainsDescendant(tapTarget, candidate)) continue;
    candidate.style = {
      ...readRecord(candidate.style),
      pointerEvents: "none",
    };
    candidate.dataAttributes = {
      ...readRecord(candidate.dataAttributes),
      cogoPointerEventsDisabledForTapTarget:
        sourceNodeIdForElement(tapTarget) ??
        readString(tapTarget.id) ??
        "tap-target",
    };
  }
}

function findNextFrameTapTarget(
  target: Record<string, unknown>,
  ancestors: Record<string, unknown>[],
  candidates: Record<string, unknown>[],
): Record<string, unknown> {
  const ancestor = [...ancestors].reverse().find((candidate) => {
    return isFilledTapTargetBox(candidate);
  });
  return ancestor ?? findCoveringTapTarget(target, candidates) ?? target;
}

function collectCogoElements(root: unknown): Record<string, unknown>[] {
  const node = readRecord(root);
  if (Object.keys(node).length === 0) return [];
  return [
    node,
    ...readArray(node.children).flatMap((child) => collectCogoElements(child)),
  ];
}

function uniqueCogoElementTreeIds(
  root: CogoElementRecord,
  namespace: string,
): CogoElementRecord {
  const seen = new Map<string, number>();

  const visit = (value: unknown, path: string): CogoElementRecord => {
    const node = readRecord(value);
    const originalId = readString(node.id) ?? safeId(`${namespace}-${path}`);
    let nextId = originalId;
    const seenCount = seen.get(originalId) ?? 0;
    seen.set(originalId, seenCount + 1);

    if (seenCount > 0) {
      const suffix = stableHash(`${namespace}:${path}:${seenCount}`);
      nextId = `${originalId}-${suffix}`;
      while (seen.has(nextId)) {
        nextId = `${originalId}-${stableHash(`${nextId}:${path}`)}`;
      }
      seen.set(nextId, 1);
    }

    const children = readArray(node.children).map((child, index) =>
      visit(child, `${path}-${index}`),
    );

    return {
      ...node,
      id: nextId,
      ...(nextId !== originalId
        ? {
            sourceDesign: {
              ...readRecord(node.sourceDesign),
              cogoOriginalElementId: originalId,
            },
            dataAttributes: {
              ...readRecord(node.dataAttributes),
              cogoOriginalElementId: originalId,
              cogoElementIdDisambiguated: "true",
            },
          }
        : {}),
      ...(node.children !== undefined ? { children } : {}),
    };
  };

  return visit(root, "root");
}

function isLikelyNextFrameTarget(element: Record<string, unknown>): boolean {
  const dataAttributes = readRecord(element.dataAttributes);
  const sourceDesign = readRecord(element.sourceDesign);
  const identityHaystack = [
    readString(element.id),
    readString(element.name),
    readString(dataAttributes.figmaNodeId),
    readString(sourceDesign.figmaNodeName),
    readString(sourceDesign.figmaNodeId),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  if (/(^|[-_: ])cta($|[-_: ])|button|primary-action/.test(identityHaystack)) {
    return true;
  }
  const text = readString(element.text);
  return Boolean(
    text &&
    /^(확인|완료|다음|계속|시작|네|예|동의|계좌\s*선택\s*완료|continue|next|done|yes|agree)$/i.test(
      text,
    ),
  );
}

function attachNextFrameNavigation(
  block: Record<string, unknown>,
  nextRoute: string,
): number {
  const root = readRecord(readRecord(block.ui).root);
  const touched = new Set<Record<string, unknown>>();
  const candidates = collectCogoElements(root);

  const visit = (
    element: Record<string, unknown>,
    ancestors: Record<string, unknown>[],
  ): number => {
    let attached = 0;
    if (isLikelyNextFrameTarget(element)) {
      const tapTarget = findNextFrameTapTarget(element, ancestors, candidates);
      const tapTargetLabel = readString(element.text);
      if (
        tapTarget !== element &&
        tapTargetLabel &&
        !readString(tapTarget.ariaLabel)
      ) {
        tapTarget.ariaLabel = tapTargetLabel;
      }
      disablePointerEventsForTapTargetDescendants(tapTarget, candidates);
      if (!touched.has(tapTarget)) {
        touched.add(tapTarget);
        tapTarget.type =
          tapTarget === element && readString(tapTarget.type) === "text"
            ? tapTarget.type
            : "button";
        tapTarget.events = {
          ...readRecord(tapTarget.events),
          tap: { flow: `__navigate:${nextRoute}` },
        };
        tapTarget.style = {
          ...readRecord(tapTarget.style),
          cursor: "pointer",
        };
        tapTarget.dataAttributes = {
          ...readRecord(tapTarget.dataAttributes),
          cogoPromotedTapTargetFor: readString(element.id) ?? "confirm",
        };
        attached += 1;
      }
    }
    for (const child of readArray(element.children)) {
      const childRecord = readRecord(child);
      if (Object.keys(childRecord).length === 0) continue;
      attached += visit(childRecord, [...ancestors, element]);
    }
    return attached;
  };

  if (Object.keys(root).length === 0) return 0;
  return visit(root, []);
}

function aggregateFigmaFrameMappings(input: {
  builds: FigmaFrameCogoBundleBuildResult[];
  sourceFrameSelection?: FigmaSourceFrameSelectionSummary;
}): FigmaFrameImportMapping {
  const first = input.builds[0]?.mapping;
  if (!first) {
    throw new Error("Cannot aggregate an empty Figma frame build list.");
  }
  const mappings = input.builds.map((build) => build.mapping);
  const matchedNodes = mappings.reduce(
    (sum, mapping) => sum + mapping.matchedNodes,
    0,
  );
  const nodeCount = mappings.reduce(
    (sum, mapping) => sum + mapping.nodeCount,
    0,
  );
  const unsupportedNodes = Array.from(
    new Set(mappings.flatMap((mapping) => mapping.unsupportedNodes)),
  );
  const extractedTokens = Array.from(
    new Set(mappings.flatMap((mapping) => mapping.extractedTokens)),
  );
  const matchedComponents = Array.from(
    new Set(mappings.flatMap((mapping) => mapping.matchedComponents)),
  );
  const ontologyValidation = {
    ...first.ontologyValidation,
    sourceCount: mappings.reduce(
      (sum, mapping) => sum + mapping.ontologyValidation.sourceCount,
      0,
    ),
    admittedSourceCount: mappings.reduce(
      (sum, mapping) => sum + mapping.ontologyValidation.admittedSourceCount,
      0,
    ),
    blockedSourceCount: mappings.reduce(
      (sum, mapping) => sum + mapping.ontologyValidation.blockedSourceCount,
      0,
    ),
    admittedCaseCount: mappings.reduce(
      (sum, mapping) => sum + mapping.ontologyValidation.admittedCaseCount,
      0,
    ),
    mappingCaseCount: mappings.reduce(
      (sum, mapping) => sum + mapping.ontologyValidation.mappingCaseCount,
      0,
    ),
    nodeMappingCount: mappings.reduce(
      (sum, mapping) => sum + mapping.ontologyValidation.nodeMappingCount,
      0,
    ),
    requirementMappingCount: mappings.reduce(
      (sum, mapping) =>
        sum + mapping.ontologyValidation.requirementMappingCount,
      0,
    ),
    chunkMappingCount: mappings.reduce(
      (sum, mapping) => sum + mapping.ontologyValidation.chunkMappingCount,
      0,
    ),
    tokenMappingCount: mappings.reduce(
      (sum, mapping) => sum + mapping.ontologyValidation.tokenMappingCount,
      0,
    ),
    actionMappingCount: mappings.reduce(
      (sum, mapping) => sum + mapping.ontologyValidation.actionMappingCount,
      0,
    ),
    dataInputMappingCount: mappings.reduce(
      (sum, mapping) => sum + mapping.ontologyValidation.dataInputMappingCount,
      0,
    ),
    hardConstraints: Array.from(
      new Set(
        mappings.flatMap(
          (mapping) => mapping.ontologyValidation.hardConstraints,
        ),
      ),
    ),
  };
  const designTemplateApplication = {
    ...first.designTemplateApplication,
    appliedNodeCount: mappings.reduce(
      (sum, mapping) =>
        sum + mapping.designTemplateApplication.appliedNodeCount,
      0,
    ),
    styledNodeCount: mappings.reduce(
      (sum, mapping) => sum + mapping.designTemplateApplication.styledNodeCount,
      0,
    ),
    textStyleNodeCount: mappings.reduce(
      (sum, mapping) =>
        sum + mapping.designTemplateApplication.textStyleNodeCount,
      0,
    ),
    componentStyleNodeCount: mappings.reduce(
      (sum, mapping) =>
        sum + mapping.designTemplateApplication.componentStyleNodeCount,
      0,
    ),
    tokenRefs: Array.from(
      new Set(
        mappings.flatMap(
          (mapping) => mapping.designTemplateApplication.tokenRefs,
        ),
      ),
    ),
    hardConstraints: Array.from(
      new Set(
        mappings.flatMap(
          (mapping) => mapping.designTemplateApplication.hardConstraints,
        ),
      ),
    ),
  };

  return {
    matchedNodes,
    unsupportedNodes,
    extractedTokens,
    matchedComponents,
    componentMatchRate:
      nodeCount > 0 ? Number((matchedNodes / nodeCount).toFixed(3)) : 0,
    unsupportedNodeCount: unsupportedNodes.length,
    textNodeCount: mappings.reduce(
      (sum, mapping) => sum + mapping.textNodeCount,
      0,
    ),
    nodeCount,
    sourceFrameSelection: input.sourceFrameSelection,
    ontologyValidation,
    referenceParity: first.referenceParity,
    designTemplateApplication,
  };
}

function buildMultiFrameFigmaApplicationBundle(input: {
  sourceName: string;
  figmaFileId: string;
  requestedFigmaNodeId: string;
  frameBuilds: FigmaFrameCogoBundleBuildResult[];
  aggregateMapping: FigmaFrameImportMapping;
}): {
  bundle: Record<string, unknown>;
  sourceFrames: FigmaSourceFrameSummary[];
  actionFlowCount: number;
} {
  const blocks: Record<string, unknown> = {};
  const pages: Array<Record<string, unknown>> = [];
  const sourceFrames = input.frameBuilds
    .map((build) => build.sourceFrame)
    .filter((frame): frame is FigmaSourceFrameSummary => Boolean(frame));
  const mobileOnly = sourceFrames.some((frame) => frame.platform === "mobile");
  let actionFlowCount = 0;

  input.frameBuilds.forEach((build, index) => {
    const sourceBundle = readRecord(build.bundle);
    const manifest = readRecord(sourceBundle.manifest);
    const sourcePage =
      readArray<Record<string, unknown>>(manifest.pages)[0] ?? {};
    const sourceBlocks = readRecord(sourceBundle.blocks);
    const sourceBlockName =
      readArray<string>(sourcePage.blocks).find((name) =>
        Boolean(sourceBlocks[name]),
      ) ?? Object.keys(sourceBlocks)[0];
    const sourceBlock = cloneJsonRecord(sourceBlocks[sourceBlockName]);
    if (!sourceBlockName || Object.keys(sourceBlock).length === 0) return;

    const frameName =
      build.sourceFrame?.frameName ??
      readString(sourcePage.name) ??
      readString(sourcePage.title) ??
      build.frameName ??
      `Figma screen ${index + 1}`;
    const blockName = `figma-frame-${index + 1}-${safeId(frameName)}`;
    const route = index === 0 ? "/" : `/${safeId(`${index + 1}-${frameName}`)}`;
    const nextRoute =
      index < input.frameBuilds.length - 1
        ? `/${safeId(`${index + 2}-${input.frameBuilds[index + 1]?.frameName ?? "screen"}`)}`
        : undefined;
    const blockMeta = readRecord(sourceBlock.meta);
    const blockUi = readRecord(sourceBlock.ui);
    const block = {
      ...sourceBlock,
      blockType: blockName,
      meta: {
        ...blockMeta,
        id: blockName,
        name: frameName,
        namespace: blockName,
      },
      ui: {
        ...blockUi,
        blockType: blockName,
      },
    };
    if (nextRoute) {
      const attached = attachNextFrameNavigation(block, nextRoute);
      if (attached > 0) actionFlowCount += 1;
    }
    blocks[blockName] = block;

    const sourceFrameBase = sourceFrameRecord(build.sourceFrame);
    const sourceFrame: Record<string, unknown> | undefined = sourceFrameBase
      ? {
          ...sourceFrameBase,
          pageIndex: index,
        }
      : undefined;
    pages.push({
      id: `screen-${index + 1}-${safeId(frameName)}`,
      route,
      name: frameName,
      title: frameName,
      layout: "flow",
      blocks: [blockName],
      blockLayout: {
        [blockName]: { width: "full" },
      },
      ...(sourceFrame ? { sourceFrame } : {}),
      ...(sourceFrame && mobileOnly
        ? {
            device: "mobile",
            viewport: {
              width: readNumber(readRecord(sourceFrame).width) ?? 360,
              height: readNumber(readRecord(sourceFrame).height) ?? 800,
            },
          }
        : {}),
    });
  });

  const sourceFrameRecords = sourceFrames.map((frame, index) => ({
    ...sourceFrameRecord(frame),
    pageIndex: index,
  }));
  const firstSourceFrame = sourceFrames[0];
  const sourceConversion = {
    sourceType: "figma",
    figmaFileId: input.figmaFileId,
    figmaNodeId: input.requestedFigmaNodeId,
    frameName: input.sourceName,
    sourceFrameCount: sourceFrames.length,
    sourceFrames: sourceFrameRecords,
    actionFlowCount,
    ...(mobileOnly ? { mobileOnly: true, defaultDevice: "mobile" } : {}),
  };
  return {
    bundle: {
      schemaVersion: "cogo-application/v1",
      manifest: {
        appId: `${safeId(input.sourceName)}-figma-application`,
        name: input.sourceName,
        version: "1.0.0",
        schemaVersion: "3.0",
        sourceConversion,
        ...(mobileOnly
          ? {
              studio: {
                defaultDevice: "mobile",
                defaultViewport: "mobile",
                viewport:
                  firstSourceFrame?.width && firstSourceFrame.height
                    ? {
                        width: firstSourceFrame.width,
                        height: firstSourceFrame.height,
                      }
                    : undefined,
              },
            }
          : {}),
        pages,
        datasources: {},
        globalState: {},
      },
      blocks,
      flows: {},
      data: {
        sourceFrames: sourceFrameRecords,
      },
      tokens: readRecord(input.frameBuilds.at(-1)?.bundle).tokens,
      designSystem: readRecord(input.frameBuilds.at(-1)?.bundle).designSystem,
      metadata: {
        cogoXyzFigmaImport: {
          figmaFileId: input.figmaFileId,
          figmaNodeId: input.requestedFigmaNodeId,
          frameName: input.sourceName,
          sourceFrames: sourceFrameRecords,
          pageCount: pages.length,
          actionFlowCount,
          matchedNodes: input.aggregateMapping.matchedNodes,
          unsupportedNodeCount: input.aggregateMapping.unsupportedNodeCount,
          unsupportedNodeSample: input.aggregateMapping.unsupportedNodes.slice(
            0,
            50,
          ),
          extractedTokenCount: input.aggregateMapping.extractedTokens.length,
          extractedTokens: input.aggregateMapping.extractedTokens.slice(0, 64),
          matchedComponents: input.aggregateMapping.matchedComponents,
          componentMatchRate: input.aggregateMapping.componentMatchRate,
          textNodeCount: input.aggregateMapping.textNodeCount,
          nodeCount: input.aggregateMapping.nodeCount,
          sourceFrameSelection: input.aggregateMapping.sourceFrameSelection,
          ontologyValidation: compactOntologyValidationSummary(
            input.aggregateMapping.ontologyValidation,
          ),
          referenceParity: compactReferenceParitySummary(
            input.aggregateMapping.referenceParity,
          ),
          designTemplateApplication: compactDesignTemplateApplicationSummary(
            input.aggregateMapping.designTemplateApplication,
          ),
        },
        cogoXyzFigmaMultiFrameAssembly: {
          schemaVersion: "cogo.figma-multi-frame-application.v1",
          sourceFrameCount: sourceFrames.length,
          pageCount: pages.length,
          actionFlowCount,
          mobileOnly,
        },
      },
    },
    sourceFrames,
    actionFlowCount,
  };
}

function buildMultiFrameFigmaOntologyImportPayload(input: {
  sourceInput: FigmaFrameImportInput;
  payload: Record<string, unknown>;
  document: FigmaNode;
  frameName: string;
  mapping: FigmaFrameImportMapping;
  bundle: Record<string, unknown>;
  frameBuilds: FigmaFrameCogoBundleBuildResult[];
}): Record<string, unknown> {
  const basePayload = buildFigmaOntologyImportPayload({
    input: input.sourceInput,
    payload: input.payload,
    document: input.document,
    frameName: input.frameName,
    mapping: input.mapping,
    bundle: input.bundle,
  });
  const cogoIntermediate = {
    ...readRecord(basePayload.cogoIntermediate),
    selection: {
      ...readRecord(readRecord(basePayload.cogoIntermediate).selection),
      pageCount: input.frameBuilds.length,
      nodeCount: input.mapping.nodeCount,
      textNodeCount: input.mapping.textNodeCount,
    },
    pages: input.frameBuilds.map((build, index) => {
      const sourceNodeId = readString(build.sourceFrame?.figmaNodeId);
      const otherPageSourceNodeIds = new Set(
        input.frameBuilds
          .map((candidate) => readString(candidate.sourceFrame?.figmaNodeId))
          .filter((candidateId): candidateId is string =>
            Boolean(candidateId && candidateId !== sourceNodeId),
          ),
      );
      const sourceFrameNode = sourceNodeId
        ? collectNodes(input.document).find(
            (candidate) => readString(candidate.id) === sourceNodeId,
          )
        : undefined;
      const lockedSourceBox = sourceFrameNode
        ? figmaBox(sourceFrameNode)
        : undefined;
      if (
        !sourceNodeId ||
        !sourceFrameNode ||
        !lockedSourceBox ||
        (readNumber(lockedSourceBox.width) ?? 0) <= 0 ||
        (readNumber(lockedSourceBox.height) ?? 0) <= 0
      ) {
        throw new Error(
          `Multi-frame ontology page ${index + 1} is missing its exact source frame identity or box`,
        );
      }
      return {
        id: safeId(`page-${sourceNodeId ?? index}`),
        sourceNodeId,
        name: build.frameName,
        route:
          index === 0 ? "/" : `/${safeId(`${index + 1}-${build.frameName}`)}`,
        bbox: lockedSourceBox,
        components: collectCachedComponentRecords(
          sourceFrameNode,
          lockedSourceBox,
          undefined,
          otherPageSourceNodeIds,
        ),
      };
    }),
    bundle: input.bundle,
  };
  return {
    ...basePayload,
    cogoIntermediate,
  };
}

function aggregateFigmaSourceOntologyStorage(input: {
  rootStorage: FigmaSourceOntologyStorage;
  sourceFrames: FigmaSourceFrameSummary[];
}): FigmaSourceOntologyStorage {
  return {
    ...input.rootStorage,
    figmaNodeId: input.rootStorage.figmaNodeId,
    chunkCount: input.rootStorage.chunkCount,
    artifactCount: input.rootStorage.artifactCount,
    reason:
      input.rootStorage.reason ??
      `multi-frame-root-storage:${input.sourceFrames.length}`,
  };
}

function readStoredSourceFrames(
  bundle: Record<string, unknown>,
): FigmaSourceFrameSummary[] {
  const manifest = readRecord(bundle.manifest);
  const sourceConversion = readRecord(manifest.sourceConversion);
  return readArray<Record<string, unknown>>(sourceConversion.sourceFrames)
    .map((frame): FigmaSourceFrameSummary => {
      const platform: "mobile" | "web" =
        readString(frame.platform) === "web" ? "web" : "mobile";
      return {
        schemaVersion: "cogo.figma-source-frame.v1",
        platform,
        width: readNumber(frame.width) ?? 0,
        height: readNumber(frame.height) ?? 0,
        figmaFileId: readString(frame.figmaFileId) ?? "",
        figmaNodeId: readString(frame.figmaNodeId) ?? "",
        frameName: readString(frame.frameName) ?? "Figma frame",
      };
    })
    .filter((frame) => frame.width > 0 && frame.height > 0);
}

function readStoredSourceFrameSelection(
  value: unknown,
): FigmaSourceFrameSelectionSummary | undefined {
  const record = readRecord(value);
  const requestedNodeId = readString(record.requestedNodeId);
  const selectedNodeId = readString(record.selectedNodeId);
  const selectedFrameName = readString(record.selectedFrameName);
  const reason = readString(record.reason);
  if (!requestedNodeId || !selectedNodeId || !selectedFrameName || !reason) {
    return undefined;
  }
  return {
    schemaVersion: "cogo.figma-source-frame-selection.v1",
    requestedNodeId,
    selectedNodeId,
    selectedFrameName,
    reason:
      reason === "mobile-frame-inside-section"
        ? "mobile-frame-inside-section"
        : "requested-node",
    candidateCount: readNumber(record.candidateCount) ?? 1,
  };
}

function resolveFigmaToken(inputToken?: string): string | undefined {
  return inputToken?.trim() || undefined;
}

function shouldUseFigmaProxy(): boolean {
  const env = (import.meta as { env?: { MODE?: string } }).env;
  return (
    env?.MODE !== "test" &&
    typeof window !== "undefined" &&
    typeof document !== "undefined"
  );
}

async function fetchFigmaProxy(
  body: Record<string, unknown>,
  token?: string,
): Promise<Response> {
  const accessToken = await getStudioAccessToken();
  return fetch("/api/figma/proxy", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify({
      ...body,
      ...(token ? { figmaAccessToken: token } : {}),
    }),
  });
}

async function readFigmaApiError(response: Response): Promise<string> {
  try {
    const payload = readRecord(await response.json());
    const detail = payload.err ?? payload.message ?? payload.status;
    return typeof detail === "string" ? detail : response.statusText;
  } catch {
    return response.statusText;
  }
}

async function fetchFigmaNode(
  input: FigmaFrameImportInput,
  token: string | undefined,
): Promise<Response> {
  if (shouldUseFigmaProxy()) {
    return fetchFigmaProxy(
      {
        kind: "nodes",
        figmaFileId: input.figmaFileId,
        figmaNodeId: input.figmaNodeId,
        ...(input.workspaceId ? { workspaceId: input.workspaceId } : {}),
        ...(input.figmaTokenRef ? { figmaTokenRef: input.figmaTokenRef } : {}),
      },
      token,
    );
  }

  const url = new URL(
    `https://api.figma.com/v1/files/${encodeURIComponent(input.figmaFileId)}/nodes`,
  );
  url.searchParams.set("ids", input.figmaNodeId);
  const headers = {
    Accept: "application/json",
    ...(token ? { "X-Figma-Token": token } : {}),
  };
  let response = await fetch(url.toString(), { headers });
  if (token && (response.status === 401 || response.status === 403)) {
    response = await fetch(url.toString(), {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
  }
  return response;
}

async function requestFigmaOntologyReadback(params: {
  projectId?: string;
  figmaFileId: string;
  figmaNodeId: string;
  sourceRevision?: string;
  sourceSha256?: string;
  jobId?: string;
  templateId?: string;
  artifactSetSha256?: string;
  fallbackReason: string;
}): Promise<FigmaOntologyReadbackResult | undefined> {
  if (!shouldUseFigmaProxy()) return undefined;
  return postFigmaOntologyReadback(params);
}

async function postFigmaOntologyReadback(params: {
  projectId?: string;
  figmaFileId: string;
  figmaNodeId: string;
  sourceRevision?: string;
  sourceSha256?: string;
  jobId?: string;
  templateId?: string;
  artifactSetSha256?: string;
  fallbackReason: string;
}): Promise<FigmaOntologyReadbackResult | undefined> {
  const projectId = readString(params.projectId);
  if (!projectId) return undefined;
  try {
    const studioAccessToken = await getStudioAccessToken();
    if (!studioAccessToken) return undefined;
    const response = await fetch("/api/figma/ontology-readback", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${studioAccessToken}`,
      },
      body: JSON.stringify({
        projectId,
        figmaFileKey: params.figmaFileId,
        figmaNodeId: params.figmaNodeId,
        ...(params.sourceRevision
          ? { sourceRevision: params.sourceRevision }
          : {}),
        ...(params.sourceSha256 ? { sourceSha256: params.sourceSha256 } : {}),
        ...(params.jobId ? { jobId: params.jobId } : {}),
        ...(params.templateId ? { templateId: params.templateId } : {}),
        ...(params.artifactSetSha256
          ? { artifactSetSha256: params.artifactSetSha256 }
          : {}),
        fallbackReason: params.fallbackReason,
      }),
    });
    if (!response.ok) return undefined;
    const result = readRecord(await response.json());
    if (
      result.schemaVersion !== "cogo.figma-ontology-readback-result.v1" ||
      result.status !== "hit" ||
      result.source !== "cloudflare-d1-r2-derived-readback"
    ) {
      return undefined;
    }

    const job = readRecord(result.job);
    const tenantId = readString(job.tenantId);
    const responseProjectId = readString(job.projectId);
    const templateId = readString(job.templateId);
    const figmaFileKey = readString(job.figmaFileKey);
    const figmaNodeId = readString(job.figmaNodeId);
    const sourceRevision = readString(job.sourceRevision);
    const rawSha256 = readString(job.rawSha256);
    const sourceSha256 = readString(job.sourceSha256);
    const artifactSetSha256 = readString(job.artifactSetSha256);
    if (
      !tenantId ||
      responseProjectId !== projectId ||
      !templateId ||
      figmaFileKey !== params.figmaFileId ||
      figmaNodeId !== params.figmaNodeId ||
      !sourceRevision ||
      !/^[a-f0-9]{64}$/.test(rawSha256 ?? "") ||
      !/^[a-f0-9]{64}$/.test(sourceSha256 ?? "") ||
      !/^[a-f0-9]{64}$/.test(artifactSetSha256 ?? "") ||
      (params.sourceRevision !== undefined &&
        sourceRevision !== params.sourceRevision) ||
      (params.sourceSha256 !== undefined &&
        sourceSha256 !== params.sourceSha256) ||
      (params.artifactSetSha256 !== undefined &&
        artifactSetSha256 !== params.artifactSetSha256)
    ) {
      return undefined;
    }

    const encodedSegments = [
      tenantId,
      responseProjectId,
      figmaFileKey,
      figmaNodeId,
      templateId,
    ].map((value) => {
      const raw = value.trim();
      if (!raw || raw.length > 160)
        throw new Error("invalid authority segment");
      const encoded = encodeURIComponent(raw);
      if (new TextEncoder().encode(encoded).byteLength > 480) {
        throw new Error("invalid authority segment");
      }
      return encoded;
    });
    const sourceRevisionSegment = authoritativeFigmaSegment(
      sourceRevision,
      "sourceRevision",
    );
    const expectedR2Prefix = [
      "figma-ingest",
      ...encodedSegments,
      "revisions",
      sourceRevisionSegment,
      sourceSha256,
      artifactSetSha256,
    ].join("/");
    if (
      new TextEncoder().encode(expectedR2Prefix).byteLength > 900 ||
      readString(job.jobId) !==
        [
          "figma-import",
          ...encodedSegments,
          sourceRevisionSegment,
          sourceSha256,
          artifactSetSha256,
        ].join(":") ||
      readString(job.r2Prefix) !== expectedR2Prefix
    ) {
      return undefined;
    }

    const artifacts = readArray<Record<string, unknown>>(result.artifacts);
    if (artifacts.length !== 3) return undefined;
    const artifactsByKind = new Map<string, Record<string, unknown>>();
    for (const artifact of artifacts) {
      const kind = readString(artifact.kind);
      const key = readString(artifact.r2Key);
      if (!kind || !key || artifactsByKind.has(kind)) return undefined;
      artifactsByKind.set(kind, artifact);
    }
    const rawArtifact = artifactsByKind.get("raw-figma-json");
    const intermediateArtifact = artifactsByKind.get("cogo-intermediate");
    const seedArtifact = artifactsByKind.get("ontology-seed");
    const intermediateSha256 = readString(intermediateArtifact?.sha256);
    const seedSha256 = readString(seedArtifact?.sha256);
    if (
      readString(rawArtifact?.sha256) !== rawSha256 ||
      readString(rawArtifact?.r2Key) !==
        `${expectedR2Prefix}/source/figma-api-readback.${rawSha256}.json` ||
      !/^[a-f0-9]{64}$/.test(intermediateSha256 ?? "") ||
      !/^[a-f0-9]{64}$/.test(seedSha256 ?? "") ||
      readString(intermediateArtifact?.r2Key) !==
        `${expectedR2Prefix}/derived/cogo-intermediate.${intermediateSha256}.v1.json` ||
      readString(seedArtifact?.r2Key) !==
        `${expectedR2Prefix}/derived/ontology-seed.${seedSha256}.v1.json`
    ) {
      return undefined;
    }
    const intermediateSource = readRecord(
      readRecord(result.cogoIntermediate).source,
    );
    const ontologySeed = readRecord(result.ontologySeed);
    if (
      readString(intermediateSource.sourceRevision) !== sourceRevision ||
      readString(intermediateSource.sourceSha256) !== sourceSha256 ||
      readString(ontologySeed.sourceRevision) !== sourceRevision ||
      readString(ontologySeed.sourceSha256) !== sourceSha256
    ) {
      return undefined;
    }
    return result as unknown as FigmaOntologyReadbackResult;
  } catch {
    return undefined;
  }
}

function storageFromReadback(
  readback: FigmaOntologyReadbackResult,
): FigmaSourceOntologyStorage {
  if (
    readback.source !== "cloudflare-d1-r2-derived-readback" ||
    !readString(readback.job.tenantId) ||
    !readString(readback.job.projectId) ||
    !readback.job.r2Prefix.startsWith("figma-ingest/")
  ) {
    return {
      schemaVersion: "cogo.figma-source-ontology-storage.v1",
      status: "failed",
      reused: false,
      source: readback.source,
      jobId: readback.job.jobId,
      templateId: readback.job.templateId,
      figmaFileKey: readback.job.figmaFileKey,
      figmaNodeId: readback.job.figmaNodeId,
      error: "Static or legacy Figma readback is not authoritative",
    };
  }
  return {
    schemaVersion: "cogo.figma-source-ontology-storage.v1",
    status: "readback-hit",
    reused: true,
    source: readback.source,
    jobId: readback.job.jobId,
    templateId: readback.job.templateId,
    figmaFileKey: readback.job.figmaFileKey,
    figmaNodeId: readback.job.figmaNodeId,
    sourceRevision: readback.job.sourceRevision,
    rawSha256: readback.job.rawSha256,
    sourceSha256: readback.job.sourceSha256,
    artifactSetSha256: readback.job.artifactSetSha256,
    r2Prefix: readback.job.r2Prefix,
    chunkCount: readback.job.chunkCount,
    artifactCount: readback.artifacts.length,
    reason: readback.fallbackReason,
  };
}

function authoritativeFigmaSegment(value: string, label: string): string {
  const raw = value.trim();
  if (!raw || raw.length > 160) {
    throw new Error(`${label} exceeds the authoritative raw segment limit`);
  }
  let encoded: string;
  try {
    encoded = encodeURIComponent(raw);
  } catch {
    throw new Error(`${label} must be well-formed Unicode`);
  }
  if (new TextEncoder().encode(encoded).byteLength > 480) {
    throw new Error(`${label} exceeds the authoritative segment limit`);
  }
  return encoded;
}

function failedFigmaOntologyStorage(error: string): FigmaSourceOntologyStorage {
  return {
    schemaVersion: "cogo.figma-source-ontology-storage.v1",
    status: "failed",
    reused: false,
    source: "ontology-import-api",
    error,
  };
}

function assertFigmaOntologyStorageAcceptedBeforeMutation(
  storage: FigmaSourceOntologyStorage,
): void {
  const accepted =
    (storage.status === "readback-hit" &&
      storage.source === "cloudflare-d1-r2-derived-readback" &&
      Boolean(storage.sourceRevision) &&
      /^[a-f0-9]{64}$/.test(storage.rawSha256 ?? "") &&
      /^[a-f0-9]{64}$/.test(storage.sourceSha256 ?? "") &&
      /^[a-f0-9]{64}$/.test(storage.artifactSetSha256 ?? "")) ||
    (storage.status === "skipped" && import.meta.env.MODE === "test");
  if (!accepted) {
    throw new Error(
      storage.error ??
        "Figma ontology mutation requires an accepted persistence readback receipt",
    );
  }
}

function storageFromIngestResult(params: {
  result: Record<string, unknown>;
  projectId: string;
  importPayload: Record<string, unknown>;
}): FigmaSourceOntologyStorage {
  const { result, projectId, importPayload } = params;
  const tenantId = readString(result.tenantId);
  const resultProjectId = readString(result.projectId);
  const templateId = readString(importPayload.templateId);
  const figmaFileKey =
    readString(importPayload.figmaFileKey) ??
    readString(importPayload.figmaFileId);
  const figmaNodeId = readString(importPayload.figmaNodeId);
  const sourceRevision = readString(importPayload.sourceRevision);
  const sourceSha256 = readString(importPayload.sourceSha256);
  const resultRawSha256 = readString(result.rawSha256);
  const resultArtifactSetSha256 = readString(result.artifactSetSha256);
  if (
    result.schemaVersion !== "cogo.figma-ontology-ingest-result.v1" ||
    result.mode !== "persisted" ||
    !tenantId ||
    resultProjectId !== projectId ||
    !templateId ||
    !figmaFileKey ||
    !figmaNodeId ||
    !sourceRevision ||
    !/^[a-f0-9]{64}$/.test(resultRawSha256 ?? "") ||
    !/^[a-f0-9]{64}$/.test(sourceSha256 ?? "") ||
    !/^[a-f0-9]{64}$/.test(resultArtifactSetSha256 ?? "") ||
    readString(result.templateId) !== templateId ||
    readString(result.figmaFileKey) !== figmaFileKey ||
    readString(result.figmaNodeId) !== figmaNodeId ||
    readString(result.sourceRevision) !== sourceRevision ||
    readString(result.sourceSha256) !== sourceSha256
  ) {
    return failedFigmaOntologyStorage(
      "ontology import response did not match the persisted request identity",
    );
  }

  let expectedJobId: string;
  let expectedR2Prefix: string;
  try {
    const segments = [
      authoritativeFigmaSegment(tenantId, "tenantId"),
      authoritativeFigmaSegment(projectId, "projectId"),
      authoritativeFigmaSegment(figmaFileKey, "figmaFileKey"),
      authoritativeFigmaSegment(figmaNodeId, "figmaNodeId"),
      authoritativeFigmaSegment(templateId, "templateId"),
    ];
    const sourceRevisionSegment = authoritativeFigmaSegment(
      sourceRevision,
      "sourceRevision",
    );
    expectedJobId = `figma-import:${[
      ...segments,
      sourceRevisionSegment,
      sourceSha256,
      resultArtifactSetSha256,
    ].join(":")}`;
    expectedR2Prefix = `figma-ingest/${segments.join(
      "/",
    )}/revisions/${sourceRevisionSegment}/${sourceSha256}/${resultArtifactSetSha256}`;
    if (new TextEncoder().encode(expectedR2Prefix).byteLength > 900) {
      throw new Error("Figma ontology authoritative R2 prefix is too long");
    }
  } catch (error) {
    return failedFigmaOntologyStorage(
      error instanceof Error ? error.message : String(error),
    );
  }

  const r2Objects = readArray<Record<string, unknown>>(result.r2Objects);
  const expectedKinds = new Set([
    "raw-figma-json",
    "cogo-intermediate",
    "ontology-seed",
  ]);
  const seenKinds = new Set<string>();
  const validObjects =
    r2Objects.length === expectedKinds.size &&
    r2Objects.every((object) => {
      const kind = readString(object.kind);
      if (!kind || !expectedKinds.has(kind) || seenKinds.has(kind))
        return false;
      seenKinds.add(kind);
      const sha256 = readString(object.sha256);
      if (!/^[a-f0-9]{64}$/.test(sha256 ?? "")) return false;
      const expectedKey =
        kind === "raw-figma-json"
          ? `${expectedR2Prefix}/source/figma-api-readback.${resultRawSha256}.json`
          : kind === "cogo-intermediate"
            ? `${expectedR2Prefix}/derived/cogo-intermediate.${sha256}.v1.json`
            : `${expectedR2Prefix}/derived/ontology-seed.${sha256}.v1.json`;
      return (
        readString(object.key) === expectedKey &&
        (kind !== "raw-figma-json" ||
          readString(object.sha256) === resultRawSha256) &&
        new Set(["put", "already-in-r2"]).has(
          readString(object.action) ?? "",
        ) &&
        object.contentType === "application/json" &&
        Boolean(readString(object.sha256)) &&
        readNumber(object.byteLength) !== undefined
      );
    });
  const d1Rows = readRecord(result.d1Rows);
  const vectorize = readRecord(result.vectorize);
  const storageDecision = readRecord(result.storageDecision);
  const expectedChunkCount = readArray<Record<string, unknown>>(
    readRecord(importPayload.ontologySeed).chunks,
  ).filter((chunk) => Boolean(readString(chunk.id))).length;
  if (
    readString(result.jobId) !== expectedJobId ||
    readString(result.r2Prefix) !== expectedR2Prefix ||
    !validObjects ||
    readNumber(d1Rows.figma_ingest_jobs) !== 1 ||
    readNumber(d1Rows.figma_ingest_artifacts) !== r2Objects.length ||
    readNumber(d1Rows.figma_ontology_chunks) !== expectedChunkCount ||
    readNumber(vectorize.chunkCount) !== expectedChunkCount ||
    vectorize.upsertRequired !== expectedChunkCount > 0 ||
    vectorize.embeddingInputPolicy !== "semantic-text-only" ||
    storageDecision.rawJsonStoredInR2 !== true ||
    storageDecision.rawPayloadStoredInD1 !== false ||
    storageDecision.derivedJsonStoredInR2 !== true ||
    storageDecision.d1StoresOnlyManifestAndChunkIndex !== true
  ) {
    return failedFigmaOntologyStorage(
      "ontology import response failed canonical job, R2, or persistence validation",
    );
  }

  return {
    schemaVersion: "cogo.figma-source-ontology-storage.v1",
    status: "persisted",
    reused: false,
    source: "ontology-import-api",
    jobId: expectedJobId,
    tenantId,
    projectId,
    templateId,
    figmaFileKey,
    figmaNodeId,
    sourceRevision,
    rawSha256: resultRawSha256,
    sourceSha256,
    artifactSetSha256: resultArtifactSetSha256,
    r2Prefix: expectedR2Prefix,
    chunkCount: readNumber(vectorize.chunkCount),
    artifactCount: r2Objects.length,
  };
}

function figmaBox(node: FigmaNode): Record<string, unknown> | undefined {
  const box = node.absoluteBoundingBox;
  if (!box) return undefined;
  return withDefinedRecordEntries({
    x: readNumber(box.x) ?? 0,
    y: readNumber(box.y) ?? 0,
    width: readNumber(box.width),
    height: readNumber(box.height),
  });
}

function figmaRenderBox(node: FigmaNode): Record<string, unknown> | undefined {
  const box = node.absoluteRenderBounds;
  if (!box) return undefined;
  return withDefinedRecordEntries({
    x: readNumber(box.x) ?? 0,
    y: readNumber(box.y) ?? 0,
    width: readNumber(box.width),
    height: readNumber(box.height),
  });
}

function cachedCogoKindForNode(node: FigmaNode): string {
  if (node.type === "TEXT") return "text";
  return componentKindForNode(node) ?? "container";
}

function cachedStyleFactsForNode(node: FigmaNode): Record<string, unknown> {
  return withDefinedRecordEntries({
    fillTokens: readArray<FigmaPaint>(node.fills)
      .map(colorTokenFromPaint)
      .filter((token): token is string => Boolean(token)),
    strokeTokens: readArray<FigmaPaint>(node.strokes)
      .map(colorTokenFromPaint)
      .filter((token): token is string => Boolean(token)),
    gradient: gradientBackgroundIfTop(node),
    shadow: firstShadowCss(node),
    radius: readNumber(node.cornerRadius),
    opacity: readNumber(node.opacity),
    blendMode: readString(node.blendMode),
    typography: withDefinedRecordEntries({
      fontFamily: node.style?.fontFamily,
      fontWeight: readNumber(node.style?.fontWeight),
      fontSize: readNumber(node.style?.fontSize),
      lineHeightPx: readNumber(node.style?.lineHeightPx),
    }),
  });
}

function cachedComponentRecordFromNode(
  node: FigmaNode,
  pageBox: Record<string, unknown>,
  parentNode?: FigmaNode,
): Record<string, unknown> | null {
  const bbox = figmaBox(node);
  const width = readNumber(bbox?.width);
  const height = readNumber(bbox?.height);
  if (!bbox || !width || !height) return null;
  const pageX = readNumber(pageBox.x) ?? 0;
  const pageY = readNumber(pageBox.y) ?? 0;
  const x = readNumber(bbox.x) ?? 0;
  const y = readNumber(bbox.y) ?? 0;
  const cogoKind = cachedCogoKindForNode(node);
  const identity = figmaNodeComponentIdentityContract(node, cogoKind);
  const sourceNodeId = readString(node.id) ?? safeId(node.name ?? cogoKind);
  return withDefinedRecordEntries({
    id: safeId(sourceNodeId),
    sourceNodeId,
    parentSourceNodeId: readString(parentNode?.id),
    name: readString(node.name) ?? cogoKind,
    figmaType: readString(node.type),
    cogoKind,
    text: node.type === "TEXT" ? readString(node.characters) : undefined,
    sourceVisible: node.visible !== false,
    absoluteRenderBounds: figmaRenderBox(node),
    bbox,
    relativeLayout: {
      left: x - pageX,
      top: y - pageY,
      width,
      height,
    },
    styleFacts: cachedStyleFactsForNode(node),
    layoutFacts: figmaNodeLayoutFacts(node),
    featureFacts: figmaNodeFeatureFacts(node),
    componentOntologyId: identity.componentOntologyId,
    variantId: identity.variantId,
    slotBindings: identity.slotBindings,
  });
}

function collectCachedComponentRecords(
  node: FigmaNode,
  pageBox: Record<string, unknown>,
  parentNode?: FigmaNode,
  excludedPageSourceNodeIds: ReadonlySet<string> = new Set(),
): Record<string, unknown>[] {
  if (node.visible === false) return [];
  const sourceNodeId = readString(node.id);
  if (
    parentNode &&
    sourceNodeId &&
    excludedPageSourceNodeIds.has(sourceNodeId)
  ) {
    return [];
  }
  return [
    ...(parentNode
      ? [cachedComponentRecordFromNode(node, pageBox, parentNode)].filter(
          (component): component is Record<string, unknown> =>
            Boolean(component),
        )
      : []),
    ...(node.children ?? []).flatMap((child) =>
      collectCachedComponentRecords(
        child,
        pageBox,
        node,
        excludedPageSourceNodeIds,
      ),
    ),
  ];
}

function independentTemplateMatchQuery(
  input: FigmaFrameImportInput,
): FigmaTemplateMatchQuery {
  const selection = input.templateSelection;
  if (!selection?.userIntent.trim() || !selection.scope) {
    throw new Error(
      "independent user template intent and selection scope are required",
    );
  }
  return {
    userIntent: selection.userIntent.trim(),
    scope: selection.scope,
    ...(selection.expectedViewportClass
      ? { expectedViewportClass: selection.expectedViewportClass }
      : {}),
    ...(selection.expectedRegions?.length
      ? { expectedRegions: [...selection.expectedRegions] }
      : {}),
    ...(selection.expectedComponentKinds?.length
      ? { expectedComponentKinds: [...selection.expectedComponentKinds] }
      : {}),
    ...(selection.expectedInteractionAffordances?.length
      ? {
          expectedInteractionAffordances: [
            ...selection.expectedInteractionAffordances,
          ],
        }
      : {}),
  };
}

function canonicalTemplateEnforcementForOntologyImport(params: {
  input: FigmaFrameImportInput;
  document: FigmaNode;
}) {
  const mobileFrameCandidates = collectMobileFrameCandidates(params.document);
  const isBoundedPageSet =
    isOversizedFigmaCanvas(params.document) && mobileFrameCandidates.length > 1;
  const pages = isBoundedPageSet ? mobileFrameCandidates : [params.document];
  const representation = buildFigmaTemplateSearchRepresentation({
    figmaFileId: params.input.figmaFileId,
    figmaNodeId: params.input.figmaNodeId,
    root: params.document,
    pages,
    pageSelectionMode: isBoundedPageSet ? "bounded-page-set" : "single-page",
  });
  const query = independentTemplateMatchQuery(params.input);
  const confirmation = params.input.selectionConfirmation;
  if (
    !confirmation ||
    confirmation.schemaVersion !==
      "cogo.human-template-selection-confirmation.v1" ||
    confirmation.tool !== "cogo.studio.import_figma_frame" ||
    !confirmation.actorId.trim() ||
    !confirmation.confirmationId.trim() ||
    !confirmation.approvedAt.trim() ||
    confirmation.projectId !== params.input.projectId
  ) {
    throw new Error(
      "explicit human template-selection confirmation evidence is required",
    );
  }
  const sourceLayoutAvailable = pages.every((page) => Boolean(figmaBox(page)));
  return {
    ...buildFigmaTemplateEnforcementContract({
      representation,
      matchScore: scoreFigmaTemplateMatch(representation, query),
      layoutBoundary: chooseLayoutGenerationBoundary({
        sourceLayoutAvailable,
        shouldReuseSourceLayout: sourceLayoutAvailable,
      }),
      query,
    }),
    selectionEvidence: {
      schemaVersion: "cogo.figma-template-selection-evidence.v1",
      query,
      confirmation,
      selfScoringProhibited: true,
    },
  };
}

function buildFigmaOntologyImportPayload(params: {
  input: FigmaFrameImportInput;
  payload: Record<string, unknown>;
  document: FigmaNode;
  frameName: string;
  mapping: FigmaFrameImportMapping;
  bundle: Record<string, unknown>;
}): Record<string, unknown> {
  if (
    shouldUseFigmaProxy() &&
    (!readString(params.input.workspaceId) ||
      !readString(params.input.figmaTokenRef))
  ) {
    throw new Error(
      "production Figma ontology persistence requires workspaceId and a server-stored figmaTokenRef",
    );
  }
  if (
    !params.input.sourceRevision ||
    !/^[a-f0-9]{64}$/.test(params.input.sourceSha256 ?? "")
  ) {
    throw new Error(
      "exact Figma source revision and SHA-256 are required before persistence",
    );
  }
  const templateId =
    params.mapping.designTemplateApplication.templateId ??
    safeId(params.frameName);
  const pageBox = figmaBox(params.document) ?? {
    x: 0,
    y: 0,
    width: 390,
    height: 844,
  };
  const components = collectCachedComponentRecords(params.document, pageBox);
  const templateEnforcement = canonicalTemplateEnforcementForOntologyImport({
    input: params.input,
    document: params.document,
  });
  const chunks = [
    {
      id: safeId(
        `figma-${params.input.figmaFileId}-${params.input.figmaNodeId}-template`,
      ),
      kind: "template",
      sourceNodeId: params.input.figmaNodeId,
      text: [
        `Template ${templateId}`,
        `Frame ${params.frameName}`,
        `Nodes ${params.mapping.nodeCount}`,
        `Components ${params.mapping.matchedComponents.join(", ")}`,
      ].join(". "),
      metadata: {
        figmaFileId: params.input.figmaFileId,
        figmaNodeId: params.input.figmaNodeId,
        frameName: params.frameName,
      },
    },
    {
      id: safeId(
        `figma-${params.input.figmaFileId}-${params.input.figmaNodeId}-design-system`,
      ),
      kind: "design-system",
      sourceNodeId: params.input.figmaNodeId,
      text: [
        `Design system ${params.mapping.designTemplateApplication.designSystemId ?? templateId}`,
        `Tokens ${params.mapping.extractedTokens.slice(0, 24).join(", ")}`,
      ].join(". "),
      metadata: {
        tokenRefs: params.mapping.designTemplateApplication.tokenRefs,
        extractedTokens: params.mapping.extractedTokens,
      },
    },
    ...components.slice(0, 80).map((component, index) => ({
      id: safeId(
        `figma-${params.input.figmaFileId}-${readString(component.sourceNodeId) ?? index}-component`,
      ),
      kind: "component",
      sourceNodeId: readString(component.sourceNodeId),
      text: [
        readString(component.cogoKind) ?? "component",
        readString(component.name) ?? readString(component.sourceNodeId),
        readString(component.text),
      ]
        .filter(Boolean)
        .join(" "),
      metadata: {
        cogoKind: component.cogoKind,
        componentOntologyId: component.componentOntologyId,
        variantId: component.variantId,
      },
    })),
  ];

  return {
    ...(readString(params.input.projectId)
      ? { projectId: readString(params.input.projectId) }
      : {}),
    ...(readString(params.input.workspaceId)
      ? { workspaceId: readString(params.input.workspaceId) }
      : {}),
    ...(readString(params.input.figmaTokenRef)
      ? { figmaTokenRef: readString(params.input.figmaTokenRef) }
      : {}),
    templateId,
    figmaFileKey: params.input.figmaFileId,
    figmaNodeId: params.input.figmaNodeId,
    sourceRevision: params.input.sourceRevision,
    sourceSha256: params.input.sourceSha256,
    figmaUrl: params.input.figmaUrl,
    cogoIntermediate: {
      schemaVersion: "cogo.figma-to-cogo-intermediate.v1",
      templateId,
      designSystemId:
        params.mapping.designTemplateApplication.designSystemId ?? templateId,
      source: {
        figmaFileKey: params.input.figmaFileId,
        figmaFileId: params.input.figmaFileId,
        figmaNodeId: params.input.figmaNodeId,
        figmaUrl: params.input.figmaUrl,
        sourceRevision: params.input.sourceRevision,
        sourceSha256: params.input.sourceSha256,
      },
      selection: {
        pageCount: 1,
        nodeCount: params.mapping.nodeCount,
        textNodeCount: params.mapping.textNodeCount,
      },
      ontologyMatchingPlan:
        params.mapping.designTemplateApplication.ontologyMatchingPlan,
      templateEnforcement,
      pages: [
        {
          id: safeId(`page-${params.input.figmaNodeId}`),
          sourceNodeId: params.input.figmaNodeId,
          name: params.frameName,
          route: "/",
          bbox: pageBox,
          styleFacts: cachedStyleFactsForNode(params.document),
          components,
        },
      ],
      bundle: params.bundle,
    },
    ontologySeed: {
      schemaVersion: "cogo.figma-ontology-seed.v1",
      templateId,
      figmaFileKey: params.input.figmaFileId,
      figmaNodeId: params.input.figmaNodeId,
      sourceRevision: params.input.sourceRevision,
      sourceSha256: params.input.sourceSha256,
      source: "figma-api:nodes",
      chunks,
    },
  };
}

async function postFigmaOntologyImport(params: {
  projectId?: string;
  importPayload: Record<string, unknown>;
}): Promise<FigmaSourceOntologyStorage> {
  const projectId = readString(params.projectId);
  if (!projectId) {
    return {
      schemaVersion: "cogo.figma-source-ontology-storage.v1",
      status: "failed",
      reused: false,
      source: "ontology-import-api",
      error: "current Studio projectId is required for ontology persistence",
    };
  }
  const accessToken = await getStudioAccessToken();
  if (!accessToken) {
    return {
      schemaVersion: "cogo.figma-source-ontology-storage.v1",
      status: "failed",
      reused: false,
      source: "ontology-import-api",
      error:
        "authenticated Studio session is required for ontology persistence",
    };
  }

  const requestBody: Record<string, unknown> = {
    ...params.importPayload,
    projectId,
  };
  delete requestBody.tenantId;
  delete requestBody.authToken;
  delete requestBody.accessToken;
  delete requestBody.authorization;

  const intermediate = readRecord(requestBody.cogoIntermediate);
  const enforcement = readRecord(intermediate.templateEnforcement);
  const selectionEvidence = readRecord(enforcement.selectionEvidence);
  const confirmation = readRecord(selectionEvidence.confirmation);
  const approvalBinding = {
    schemaVersion: "cogo.figma-template-selection-approval-binding.v1",
    tool: "cogo.studio.import_figma_frame",
    projectId,
    workspaceId: readString(requestBody.workspaceId),
    figmaTokenRef: readString(requestBody.figmaTokenRef),
    figmaFileKey:
      readString(requestBody.figmaFileKey) ??
      readString(requestBody.figmaFileId),
    figmaNodeId: readString(requestBody.figmaNodeId),
    templateId:
      readString(requestBody.templateId) ?? readString(intermediate.templateId),
    sourceRevision: readString(requestBody.sourceRevision),
    sourceSha256: readString(requestBody.sourceSha256),
    templateSelection: readRecord(selectionEvidence.query),
    clientConfirmationCorrelationId: readString(confirmation.confirmationId),
  };
  if (Object.values(approvalBinding).some((value) => value === undefined)) {
    return failedFigmaOntologyStorage(
      "exact server template approval binding is required before ontology persistence",
    );
  }
  const approvalResponse = await fetch(
    "/api/figma/template-selection-approval",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({ binding: approvalBinding }),
    },
  );
  const approvalBody = readRecord(
    await approvalResponse.json().catch(() => ({})),
  );
  if (!approvalResponse.ok) {
    return failedFigmaOntologyStorage(
      readString(approvalBody.error) ??
        `template selection approval failed (${approvalResponse.status})`,
    );
  }
  if (
    approvalBody.schemaVersion !==
      "cogo.figma-template-selection-approval-receipt.v1" ||
    approvalBody.authorityBasis !== "authenticated-server-issuance" ||
    !readString(approvalBody.approvalId) ||
    !readString(approvalBody.token) ||
    !readString(approvalBody.expiresAt)
  ) {
    return failedFigmaOntologyStorage(
      "template selection approval receipt is invalid",
    );
  }
  requestBody.templateApproval = approvalBody;

  const response = await fetch("/api/figma/ontology-import", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(requestBody),
  });
  const body = readRecord(await response.json().catch(() => ({})));
  if (!response.ok) {
    return {
      schemaVersion: "cogo.figma-source-ontology-storage.v1",
      status: "failed",
      reused: false,
      source: "ontology-import-api",
      error:
        readString(body.error) ??
        readString(body.message) ??
        `ontology import failed (${response.status})`,
    };
  }
  const persisted = storageFromIngestResult({
    result: body,
    projectId,
    importPayload: requestBody,
  });
  if (persisted.status !== "persisted") return persisted;

  const readback = await postFigmaOntologyReadback({
    projectId,
    figmaFileId: persisted.figmaFileKey as string,
    figmaNodeId: persisted.figmaNodeId as string,
    sourceRevision: persisted.sourceRevision,
    sourceSha256: persisted.sourceSha256,
    artifactSetSha256: persisted.artifactSetSha256,
    jobId: persisted.jobId,
    templateId: persisted.templateId,
    fallbackReason: "post-persistence-receipt",
  });
  if (!readback) {
    return failedFigmaOntologyStorage(
      "ontology import was not acknowledged by an exact persisted readback receipt",
    );
  }
  return {
    ...storageFromReadback(readback),
    reused: false,
    reason: "persisted-and-read-back",
  };
}

async function requestFigmaOntologyImport(params: {
  input: FigmaFrameImportInput;
  payload: Record<string, unknown>;
  document: FigmaNode;
  frameName: string;
  mapping: FigmaFrameImportMapping;
  bundle: Record<string, unknown>;
  importPayload?: Record<string, unknown>;
}): Promise<FigmaSourceOntologyStorage> {
  if (!shouldUseFigmaProxy()) {
    return {
      schemaVersion: "cogo.figma-source-ontology-storage.v1",
      status: "skipped",
      reused: false,
      source: "ontology-import-api",
      reason: "browser same-origin ontology import is disabled in test mode",
    };
  }
  try {
    const importPayload =
      params.importPayload ??
      buildFigmaOntologyImportPayload({
        input: params.input,
        payload: params.payload,
        document: params.document,
        frameName: params.frameName,
        mapping: params.mapping,
        bundle: params.bundle,
      });
    return postFigmaOntologyImport({
      projectId: params.input.projectId,
      importPayload,
    });
  } catch (error) {
    return {
      schemaVersion: "cogo.figma-source-ontology-storage.v1",
      status: "failed",
      reused: false,
      source: "ontology-import-api",
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

function liveImportPayloadReadback(
  input: FigmaFrameImportInput,
  importPayload: Record<string, unknown>,
): FigmaOntologyReadbackResult | null {
  const cogoIntermediate = readRecord(importPayload.cogoIntermediate);
  const ontologySeed = readRecord(importPayload.ontologySeed);
  if (!cogoIntermediate || !ontologySeed) return null;

  const figmaFileKey =
    readString(importPayload.figmaFileKey) ?? input.figmaFileId;
  const figmaNodeId =
    readString(importPayload.figmaNodeId) ?? input.figmaNodeId;
  const chunks = readArray<Record<string, unknown>>(ontologySeed.chunks);
  const templateId =
    readString(importPayload.templateId) ??
    readString(cogoIntermediate.templateId) ??
    safeId(`figma-${figmaFileKey}-${figmaNodeId}`);

  return {
    schemaVersion: "cogo.figma-ontology-readback-result.v1",
    status: "hit",
    source: "cloudflare-d1-r2-derived-readback",
    fallbackReason: "live-import-intermediate-visible-assembly",
    job: {
      jobId: `live-import-${safeId(`${figmaFileKey}-${figmaNodeId}`)}`,
      templateId,
      figmaFileKey,
      figmaNodeId,
      r2Prefix: `figma-ingest/${figmaFileKey}/${figmaNodeId}`,
      pageCount: 1,
      chunkCount: chunks.length,
    },
    artifacts: [
      {
        kind: "cogo-intermediate",
        r2Key: "memory://live-import/cogo-intermediate.json",
      },
      {
        kind: "ontology-seed",
        r2Key: "memory://live-import/ontology-seed.json",
      },
    ],
    cogoIntermediate,
    ontologySeed,
  };
}

function cssToken(tokens: unknown): string | undefined {
  return readArray<string>(tokens).find((token) =>
    /^(#|rgb\(|rgba\(|hsl\(|hsla\()/i.test(token),
  );
}

function cssBlendMode(value: unknown): string | undefined {
  const mode = readString(value);
  if (!mode || mode === "PASS_THROUGH") return undefined;
  return mode.toLowerCase().replace(/_/g, "-");
}

function firstReadableToken(tokens: unknown): string | undefined {
  return readArray<string>(tokens).find((token) => Boolean(readString(token)));
}

function tokenRef(prefix: string, value: unknown): string | undefined {
  const text =
    typeof value === "number" && Number.isFinite(value)
      ? String(value)
      : readString(value);
  if (!text) return undefined;
  if (isTokenReference(text)) return text;
  return `$${prefix}.figma.${safeId(text)}`;
}

function designTokenBinding(
  prefix: string,
  value: unknown,
  resolvedValue?: unknown,
): { tokenRef: string; resolvedValue: string | number } | undefined {
  const token = tokenRef(prefix, value);
  if (!token) return undefined;
  const resolved =
    typeof resolvedValue === "number" && Number.isFinite(resolvedValue)
      ? resolvedValue
      : (readString(resolvedValue) ?? readString(value));
  if (resolved === undefined) return undefined;
  return { tokenRef: token, resolvedValue: resolved };
}

function cachedComponentDesignTokenBindings(
  styleFacts: Record<string, unknown>,
): Record<string, { tokenRef: string; resolvedValue: string | number }> {
  const typography = readRecord(styleFacts.typography);
  const fill = firstReadableToken(styleFacts.fillTokens);
  const stroke = firstReadableToken(styleFacts.strokeTokens);
  const gradient = readString(styleFacts.gradient);
  const shadow = readString(styleFacts.shadow);
  const radius = readNumber(styleFacts.radius);
  const cornerRadii = readArray<number>(styleFacts.cornerRadii);
  const borderRadius =
    cornerRadii.length === 4 &&
    cornerRadii.every((value) => typeof value === "number")
      ? cornerRadii.map((value) => px(value) ?? `${value}px`).join(" ")
      : radius !== undefined
        ? (px(radius) ?? `${radius}px`)
        : undefined;
  const fontFamily = readString(typography.fontFamily);
  const fontWeight = readNumber(typography.fontWeight);
  const fontSize = readNumber(typography.fontSize);
  const lineHeightPx = readNumber(typography.lineHeightPx);

  return Object.fromEntries(
    [
      ["background", designTokenBinding("color", fill)],
      ["backgroundImage", designTokenBinding("color.gradient", gradient)],
      [
        "border",
        designTokenBinding(
          "color.border",
          stroke,
          stroke ? `1px solid ${stroke}` : undefined,
        ),
      ],
      ["boxShadow", designTokenBinding("shadow", shadow)],
      [
        "borderRadius",
        designTokenBinding("radius", borderRadius ?? radius, borderRadius),
      ],
      ["color", designTokenBinding("color", fill)],
      ["fontFamily", designTokenBinding("type.font", fontFamily)],
      ["fontWeight", designTokenBinding("type.weight", fontWeight, fontWeight)],
      [
        "fontSize",
        designTokenBinding(
          "type.size",
          fontSize,
          fontSize !== undefined ? px(fontSize) : undefined,
        ),
      ],
      [
        "lineHeight",
        designTokenBinding(
          "type.line-height",
          lineHeightPx,
          lineHeightPx !== undefined ? px(lineHeightPx) : undefined,
        ),
      ],
    ].filter(
      (
        entry,
      ): entry is [
        string,
        { tokenRef: string; resolvedValue: string | number },
      ] => Boolean(entry[1]),
    ),
  );
}

function readCachedBox(value: unknown): {
  x: number;
  y: number;
  width: number;
  height: number;
} | null {
  const record = readRecord(value);
  const x = readNumber(record.x) ?? 0;
  const y = readNumber(record.y) ?? 0;
  const width = readNumber(record.width);
  const height = readNumber(record.height);
  if (width === undefined || height === undefined || width <= 0 || height <= 0)
    return null;
  return { x, y, width, height };
}

function readCachedVisualLockPage(
  intermediate: Record<string, unknown>,
  page: Record<string, unknown>,
): CachedVisualLockPage | null {
  const visualLock = readRecord(intermediate.visualLock);
  const templateId = readString(intermediate.templateId);
  const staticLock = templateId
    ? STATIC_FIGMA_VISUAL_LOCKS[templateId]
    : undefined;
  const pages = readArray<Record<string, unknown>>(visualLock.pages);
  const candidatePages =
    pages.length > 0
      ? pages
      : (staticLock?.pages ?? []).map(
          (candidate) => candidate as unknown as Record<string, unknown>,
        );
  if (candidatePages.length === 0) return null;

  const pageSourceNodeId = readString(page.sourceNodeId);
  const pageName = readString(page.name);
  const match =
    candidatePages.find(
      (candidate) =>
        readString(candidate.sourceNodeId) === pageSourceNodeId ||
        readString(candidate.name) === pageName,
    ) ?? candidatePages[0];
  const dataUrl = readString(match.dataUrl);
  const imageUrl =
    readString(match.imageUrl) ??
    readString(match.url) ??
    readString(match.assetUrl);
  const hasValidImageRef =
    (dataUrl?.startsWith("data:image/") ?? false) ||
    Boolean(
      imageUrl && (/^https?:\/\//.test(imageUrl) || imageUrl.startsWith("/")),
    );
  const width = readNumber(match.width) ?? readNumber(match.figmaWidth);
  const height = readNumber(match.height) ?? readNumber(match.figmaHeight);
  const pageIndex = readNumber(match.pageIndex) ?? 0;
  if (!hasValidImageRef || !width || !height) {
    return null;
  }

  return {
    pageIndex,
    ...(readString(match.name) ? { name: readString(match.name) } : {}),
    ...(readString(match.sourceNodeId)
      ? { sourceNodeId: readString(match.sourceNodeId) }
      : {}),
    ...(readString(match.route) ? { route: readString(match.route) } : {}),
    width,
    height,
    ...(readNumber(match.figmaWidth)
      ? { figmaWidth: readNumber(match.figmaWidth) }
      : {}),
    ...(readNumber(match.figmaHeight)
      ? { figmaHeight: readNumber(match.figmaHeight) }
      : {}),
    ...(dataUrl ? { dataUrl } : {}),
    ...(imageUrl ? { imageUrl } : {}),
    source:
      readString(match.source) ??
      readString(visualLock.source) ??
      staticLock?.source ??
      "visual-lock",
  };
}

function staticVisualLockReadbackForInput(
  input: FigmaFrameImportInput,
  fallbackReason: string,
): FigmaOntologyReadbackResult | undefined {
  const templateId = STATIC_FIGMA_TEMPLATE_BY_FILE_KEY[input.figmaFileId];
  const visualLock = templateId
    ? STATIC_FIGMA_VISUAL_LOCKS[templateId]
    : undefined;
  if (!templateId || !visualLock) return undefined;

  const pages = visualLock.pages.map((page) => ({
    id: safeId(`page-${page.sourceNodeId ?? page.name ?? page.pageIndex}`),
    sourceNodeId: page.sourceNodeId,
    name: page.name,
    route: page.route,
    bbox: {
      x: 0,
      y: 0,
      width: page.figmaWidth ?? page.width,
      height: page.figmaHeight ?? page.height,
    },
    components: page.editableComponents ?? [],
  }));

  return {
    schemaVersion: "cogo.figma-ontology-readback-result.v1",
    status: "hit",
    source: "static-studio-asset",
    fallbackReason,
    job: {
      jobId: `static-visual-lock-${templateId}`,
      templateId,
      figmaFileKey: input.figmaFileId,
      figmaNodeId: input.figmaNodeId,
      r2Prefix: `static://figma-visual-lock/${templateId}`,
      pageCount: visualLock.pages.length,
      chunkCount: visualLock.pages.length,
    },
    artifacts: [
      {
        kind: "cogo-intermediate",
        r2Key: `static://figma-visual-lock/${templateId}/cogo-intermediate.json`,
      },
      {
        kind: "ontology-seed",
        r2Key: `static://figma-visual-lock/${templateId}/ontology-seed.json`,
      },
    ],
    cogoIntermediate: {
      schemaVersion: "cogo.figma-to-cogo-intermediate.v1",
      templateId,
      source: {
        figmaFileKey: input.figmaFileId,
        figmaNodeId: input.figmaNodeId,
        figmaUrl: input.figmaUrl,
      },
      visualLock: {
        source: visualLock.source,
        status: visualLock.status,
        pages: visualLock.pages,
      },
      pages,
    },
    ontologySeed: {
      schemaVersion: "cogo.figma-static-visual-lock-ontology-seed.v1",
      templateId,
      figmaFileKey: input.figmaFileId,
      figmaNodeId: input.figmaNodeId,
      pageCount: visualLock.pages.length,
      source: visualLock.source,
    },
  };
}

function cachedComponentStyle(
  component: Record<string, unknown>,
  pageBox: { x: number; y: number; width: number; height: number },
): CogoInlineStyle | null {
  const styleFacts = readRecord(component.styleFacts);
  const renderAsset = readRecord(styleFacts.renderAsset);
  const renderAssetDisplayBox = readRecord(renderAsset.displayBox);
  const relative = readRecord(component.relativeLayout);
  const bbox = readCachedBox(component.bbox);
  const left =
    readNumber(renderAssetDisplayBox.left) ??
    readNumber(relative.left) ??
    (bbox ? bbox.x - pageBox.x : 0);
  const top =
    readNumber(renderAssetDisplayBox.top) ??
    readNumber(relative.top) ??
    (bbox ? bbox.y - pageBox.y : 0);
  const width =
    readNumber(renderAssetDisplayBox.width) ??
    readNumber(relative.width) ??
    bbox?.width;
  const height =
    readNumber(renderAssetDisplayBox.height) ??
    readNumber(relative.height) ??
    bbox?.height;
  if (width === undefined || height === undefined || width <= 0 || height <= 0)
    return null;

  const typography = readRecord(styleFacts.typography);
  const background = cssToken(styleFacts.fillTokens);
  const renderAssetDataUrl = readString(renderAsset.dataUrl);
  const backgroundImage = renderAssetDataUrl
    ? `url("${renderAssetDataUrl}")`
    : readString(styleFacts.gradient);
  const borderColor = cssToken(styleFacts.strokeTokens);
  const shadow = readString(styleFacts.shadow);
  const radius = readNumber(styleFacts.radius);
  const cornerRadii = readArray<number>(styleFacts.cornerRadii);
  const opacity = readNumber(styleFacts.opacity);
  const mixBlendMode = cssBlendMode(styleFacts.blendMode);
  const borderRadius =
    cornerRadii.length === 4 &&
    cornerRadii.every((value) => typeof value === "number")
      ? cornerRadii.map((value) => px(value) ?? `${value}px`).join(" ")
      : radius !== undefined
        ? (px(radius) ?? `${radius}px`)
        : undefined;
  const transform = readString(styleFacts.transform);
  const color = background;
  const fontFamily = readString(typography.fontFamily);
  const fontWeight = readNumber(typography.fontWeight);
  const fontSize =
    readNumber(typography.fontSize) !== undefined
      ? px(readNumber(typography.fontSize) as number)
      : undefined;
  const lineHeight =
    readNumber(typography.lineHeightPx) !== undefined
      ? px(readNumber(typography.lineHeightPx) as number)
      : undefined;

  return {
    position: "absolute",
    left: px(left) ?? "0px",
    top: px(top) ?? "0px",
    width: px(width) ?? "auto",
    height: px(height) ?? "auto",
    boxSizing: "border-box",
    overflow: "hidden",
    pointerEvents: "none",
    ...(background ? { background } : {}),
    ...(backgroundImage ? { backgroundImage } : {}),
    ...(renderAssetDataUrl
      ? {
          backgroundSize: "100% 100%",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
        }
      : {}),
    ...(borderColor ? { border: `1px solid ${borderColor}` } : {}),
    ...(shadow ? { boxShadow: shadow } : {}),
    ...(transform ? { transform } : {}),
    ...(borderRadius ? { borderRadius } : {}),
    ...(opacity !== undefined && opacity < 1 ? { opacity } : {}),
    ...(mixBlendMode ? { mixBlendMode } : {}),
    ...(color ? { color } : {}),
    ...(fontFamily ? { fontFamily } : {}),
    ...(fontWeight !== undefined ? { fontWeight } : {}),
    ...(fontSize ? { fontSize } : {}),
    ...(lineHeight ? { lineHeight } : {}),
  };
}

function isCachedComponentSourceVisible(
  component: Record<string, unknown>,
): boolean {
  if (readBoolean(component.sourceVisible) === false) return false;
  if (readBoolean(component.visible) === false) return false;
  const sourceDesign = readRecord(component.sourceDesign);
  if (readBoolean(sourceDesign.sourceVisible) === false) return false;
  if (readBoolean(sourceDesign.visible) === false) return false;
  const featureFacts = {
    ...readRecord(component.featureFacts),
    ...readRecord(sourceDesign.featureFacts),
  };
  if (readBoolean(featureFacts.sourceVisible) === false) return false;
  if (readBoolean(featureFacts.visible) === false) return false;
  const styleFacts = {
    ...readRecord(component.styleFacts),
    ...readRecord(sourceDesign.styleFacts),
  };
  if (readBoolean(styleFacts.sourceVisible) === false) return false;
  if (readBoolean(styleFacts.visible) === false) return false;
  return true;
}

function buildEditableCompilerProof(input: {
  source: string;
  components: Array<Record<string, unknown>>;
  visualLockPage: CachedVisualLockPage | null;
}): EditableCompilerProof {
  const componentKinds = Array.from(
    new Set(
      input.components
        .map((component) => readString(component.cogoKind))
        .filter((kind): kind is string => Boolean(kind)),
    ),
  );
  const editableNodeCount = input.components.length;
  const textNodeCount = input.components.filter((component) =>
    Boolean(
      readString(component.text) || readString(component.cogoKind) === "text",
    ),
  ).length;
  const interactiveNodeCount = input.components.filter((component) =>
    ["button", "input", "tabs", "nav"].includes(
      readString(component.cogoKind) ?? "",
    ),
  ).length;
  const thresholds = {
    passEditableNodeCount: 6,
    passTextNodeCount: 2,
    passInteractiveNodeCount: 1,
  };
  const reasons: string[] = [];
  if (editableNodeCount < thresholds.passEditableNodeCount) {
    reasons.push(
      `Editable node coverage ${editableNodeCount} is below ${thresholds.passEditableNodeCount}.`,
    );
  }
  if (textNodeCount < thresholds.passTextNodeCount) {
    reasons.push(
      `Editable text coverage ${textNodeCount} is below ${thresholds.passTextNodeCount}.`,
    );
  }
  if (interactiveNodeCount < thresholds.passInteractiveNodeCount) {
    reasons.push(
      `Editable interaction coverage ${interactiveNodeCount} is below ${thresholds.passInteractiveNodeCount}.`,
    );
  }
  if (!input.visualLockPage) {
    reasons.push("No visual-lock raster is available as a quality guard.");
  }
  const status =
    reasons.length === 0 ? "pass" : editableNodeCount > 0 ? "warn" : "fail";
  return {
    schemaVersion: "cogo.figma-editable-compiler-proof.v1",
    status,
    source: input.source,
    editableNodeCount,
    textNodeCount,
    interactiveNodeCount,
    componentKinds,
    visualLockRequired: Boolean(input.visualLockPage),
    thresholds,
    reasons:
      reasons.length > 0
        ? reasons
        : [
            "Editable component hitboxes are generated by tool-controlled Figma ontology facts while the visual lock remains the visual authority.",
          ],
  };
}

function buildVisualDiffGateProof(
  visualLockPage: CachedVisualLockPage | null,
): VisualDiffGateProof {
  const visualLockImageUrl =
    visualLockPage?.dataUrl ?? visualLockPage?.imageUrl;
  if (!visualLockPage || !visualLockImageUrl) {
    return {
      schemaVersion: "cogo.figma-source-visual-diff-gate.v1",
      status: "fail",
      mode: "pending-rendered-component-diff",
      diffRatio: null,
      threshold: 0,
      measuredBy: "tool-metadata",
      reasons: [
        "No source raster visual lock is available, so source-vs-generated visual diff cannot be established.",
      ],
    };
  }
  return {
    schemaVersion: "cogo.figma-source-visual-diff-gate.v1",
    // NOT a pass: comparing the source raster to itself measures nothing. This is the
    // absence of a candidate-vs-source diff, so it is reported as "warn" (unmeasured) and
    // never grants a commercial/applied pass — a real measured candidate render must.
    status: "warn",
    mode: "visual-lock-raster-authority",
    diffRatio: null,
    threshold: 0,
    sourceRasterImageUrl: visualLockImageUrl,
    generatedRasterImageUrl: visualLockImageUrl,
    width: visualLockPage.width,
    height: visualLockPage.height,
    measuredBy:
      "visual-lock asset identity (UNMEASURED: source raster compared to itself)",
    reasons: [
      "Source-vs-generated visual diff is NOT measured: the generated root reuses the exact source raster as its background, so no candidate-vs-source fidelity has been established. A measured visible-Coogo candidate render is required to pass.",
    ],
  };
}

function pixelExactRenderAssemblyForVisualLockPage(
  visualLockPage: CachedVisualLockPage | null,
): CogoElementRecord | undefined {
  const imageUrl = visualLockPage?.dataUrl ?? visualLockPage?.imageUrl;
  if (!visualLockPage || !imageUrl) return undefined;
  const isDataUrl = imageUrl.startsWith("data:image/");
  return {
    schemaVersion: "cogo.render-assembly.v1",
    mode: "pixel-exact-source-raster",
    asset: {
      schemaVersion: "cogo.render-asset.v1",
      source: "figma.visualLock",
      ...(visualLockPage.sourceNodeId
        ? { sourceNodeId: visualLockPage.sourceNodeId }
        : {}),
      mimeType: imageUrl.startsWith("data:image/jpeg")
        ? "image/jpeg"
        : "image/png",
      width: visualLockPage.width,
      height: visualLockPage.height,
      ...(isDataUrl ? { dataUrl: imageUrl } : { sourcePath: imageUrl }),
      editable: false,
    },
    displayBox: {
      x: 0,
      y: 0,
      width: visualLockPage.width,
      height: visualLockPage.height,
    },
    pixelExact: {
      mode: "source-raster-layer",
      role: "pixel-exact-page-layer",
      source: visualLockPage.source ?? "visual-lock",
      ...(visualLockPage.sourceNodeId
        ? { sourceNodeId: visualLockPage.sourceNodeId }
        : {}),
      sourceRasterImageUrl: imageUrl,
      width: visualLockPage.width,
      height: visualLockPage.height,
      imageFit: "fill",
      expectedRawDiffPixels: 0,
      expectedRawDiffRatio: 0,
      use: "pixel-parity-proof",
      semanticOverlayPolicy: "preserve-editable-cogo-layer-separately",
    },
  };
}

function isCachedComponentInteractive(
  component: Record<string, unknown>,
): boolean {
  return ["button", "input", "tabs", "nav"].includes(
    readString(component.cogoKind) ?? "",
  );
}

function hasCachedComponentVisibleStyle(
  component: Record<string, unknown>,
): boolean {
  const styleFacts = readRecord(component.styleFacts);
  const typography = readRecord(styleFacts.typography);
  return Boolean(
    cssToken(styleFacts.fillTokens) ||
    cssToken(styleFacts.strokeTokens) ||
    readString(styleFacts.gradient) ||
    readString(styleFacts.shadow) ||
    readString(styleFacts.transform) ||
    Object.keys(readRecord(styleFacts.visualPrimitive)).length > 0 ||
    readNumber(styleFacts.radius) !== undefined ||
    readString(typography.fontFamily) ||
    readNumber(typography.fontSize) !== undefined ||
    readNumber(typography.fontWeight) !== undefined ||
    readNumber(typography.lineHeightPx) !== undefined,
  );
}

function textColorForFill(fill: string | undefined): string {
  const hex = fill?.match(/^#([0-9a-f]{6})$/i)?.[1];
  const rgb = fill?.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
  const r = hex
    ? Number.parseInt(hex.slice(0, 2), 16) / 255
    : rgb
      ? Number(rgb[1]) / 255
      : undefined;
  const g = hex
    ? Number.parseInt(hex.slice(2, 4), 16) / 255
    : rgb
      ? Number(rgb[2]) / 255
      : undefined;
  const b = hex
    ? Number.parseInt(hex.slice(4, 6), 16) / 255
    : rgb
      ? Number(rgb[3]) / 255
      : undefined;
  if (r === undefined || g === undefined || b === undefined) return "#0f172a";
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.58 ? "#0f172a" : "#ffffff";
}

function cachedComponentHaystack(component: Record<string, unknown>): string {
  return [
    readString(component.id),
    readString(component.sourceNodeId),
    readString(component.name),
    readString(component.figmaType),
    readString(component.cogoKind),
    readString(component.text),
  ]
    .filter((value): value is string => Boolean(value))
    .join(" ")
    .toLowerCase();
}

function cachedComponentSemanticHaystack(
  component: Record<string, unknown>,
): string {
  return [
    readString(component.name),
    readString(component.figmaType),
    readString(component.cogoKind),
    readString(component.text),
  ]
    .filter((value): value is string => Boolean(value))
    .join(" ")
    .toLowerCase();
}

function cachedComponentBox(component: Record<string, unknown>): {
  width?: number;
  height?: number;
} {
  const relative = readRecord(component.relativeLayout);
  const bbox = readCachedBox(component.bbox);
  return {
    width: readNumber(relative.width) ?? bbox?.width,
    height: readNumber(relative.height) ?? bbox?.height,
  };
}

function cachedComponentPrimitivePalette(component: Record<string, unknown>): {
  foreground: string;
  accent: string;
  selected: string;
  surface: string;
} {
  const styleFacts = readRecord(component.styleFacts);
  const fill = cssToken(styleFacts.fillTokens);
  const stroke = cssToken(styleFacts.strokeTokens);
  return {
    foreground: stroke ?? textColorForFill(fill) ?? "#f8fafc",
    accent: fill ?? "#0f172a",
    selected: fill ?? "#3b82f6",
    surface: fill ?? "rgba(15, 23, 42, 0.12)",
  };
}

function inferCachedComponentKind(
  component: Record<string, unknown>,
): string | undefined {
  const existing = readString(component.cogoKind);
  if (existing) return existing;
  const haystack =
    cachedComponentSemanticHaystack(component) ||
    cachedComponentHaystack(component);
  if (/\b(input|field|email|password|search field)\b/.test(haystack))
    return "input";
  if (/\b(search|back|chevron|arrow|cta|button)\b/.test(haystack))
    return "button";
  if (/\b(tab|tabs|category|segmented)\b/.test(haystack)) return "tabs";
  if (/\b(bottom nav|tab bar|navigation|navbar|menu)\b/.test(haystack))
    return "nav";
  if (/\b(card|tile)\b/.test(haystack)) return "card";
  if (/\b(image|photo|illustration|product|bike|helmet|lock)\b/.test(haystack))
    return "image";
  return undefined;
}

function inferVisualPrimitiveForCachedComponent(
  component: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const styleFacts = readRecord(component.styleFacts);
  const declaredPrimitive = readRecord(styleFacts.visualPrimitive);
  if (readString(declaredPrimitive.kind)) return declaredPrimitive;

  const haystack = cachedComponentHaystack(component);
  const semanticHaystack = cachedComponentSemanticHaystack(component);
  const kind = inferCachedComponentKind(component);
  const { width = 0, height = 0 } = cachedComponentBox(component);
  const compactControl = width > 0 && height > 0 && width <= 96 && height <= 96;
  const imageLike =
    kind === "image" ||
    /\b(image|photo|illustration|product image|thumbnail|hero image)\b/.test(
      semanticHaystack,
    );
  const palette = cachedComponentPrimitivePalette(component);
  const source = "inferred-cached-component-role";

  if (/\b(status bar|mobile status|system status)\b/.test(haystack)) {
    return {
      kind: "mobile-status-bar",
      foreground: palette.foreground,
      source,
    };
  }
  if (/\b(search|magnifier)\b/.test(haystack) && compactControl) {
    return { kind: "search-icon", foreground: palette.foreground, source };
  }
  if (/\b(back|chevron|arrow left|previous)\b/.test(haystack)) {
    return { kind: "back-chevron", foreground: palette.foreground, source };
  }
  if (/\b(fingerprint|biometric|touch id|face id)\b/.test(haystack)) {
    return { kind: "fingerprint", foreground: palette.selected, source };
  }
  if (imageLike && /\b(lock|secure|security|shield|auth)\b/.test(haystack)) {
    return {
      kind: "bank-lock-illustration",
      foreground: palette.selected,
      accent: palette.accent,
      source,
    };
  }
  if (imageLike && /\bhelmet\b/.test(semanticHaystack)) {
    return {
      kind: "helmet-line-art",
      foreground: palette.foreground,
      accent: palette.accent,
      source,
    };
  }
  if (
    imageLike &&
    /\b(bike|bicycle|cycle|road bike|gravel)\b/.test(semanticHaystack)
  ) {
    return {
      kind: "bike-line-art",
      foreground: palette.foreground,
      accent: palette.accent,
      source,
    };
  }
  if (kind === "tabs" || /\b(tab|tabs|category|segmented)\b/.test(haystack)) {
    return {
      kind: "segmented-tabs",
      foreground: palette.foreground,
      selected: palette.selected,
      surface: palette.surface,
      source,
    };
  }
  if (
    kind === "nav" ||
    /\b(bottom nav|tab bar|navigation|navbar|menu)\b/.test(haystack)
  ) {
    return {
      kind: "bottom-nav",
      foreground: palette.foreground,
      selected: palette.selected,
      source,
    };
  }
  if (
    imageLike ||
    /\b(image|photo|illustration|product image|thumbnail|hero image)\b/.test(
      semanticHaystack,
    )
  ) {
    return {
      kind: "image-placeholder",
      foreground: palette.foreground,
      accent: palette.accent,
      source,
    };
  }
  return undefined;
}

function componentSlotSource(component: Record<string, unknown>): string {
  return (
    readString(component.sourceNodeId) ??
    readString(component.id) ??
    readString(component.name) ??
    "figma-node"
  );
}

function cachedComponentIdentityContract(component: Record<string, unknown>): {
  componentOntologyId?: string;
  variantId?: string;
  slotBindings?: ComponentSlotBindings;
} {
  const kind = inferCachedComponentKind(component);
  if (!kind || !REAL_COMPONENT_KINDS.has(kind)) return {};

  const haystack = cachedComponentHaystack(component);
  const styleFacts = readRecord(component.styleFacts);
  const primitiveKind = readString(readRecord(styleFacts.visualPrimitive).kind);
  const source = componentSlotSource(component);
  const slots: ComponentSlotBindings = {
    root: rootSlotBinding(source, kind),
  };
  if (readString(component.text)) {
    slots.label = componentSlotBinding({
      slotId: "label",
      nodeId: source,
      kind: "text",
      sourceRole: "editable-label",
      acceptedTypes: ["text", "heading"],
      required: true,
      editable: true,
    });
  }
  if (primitiveKind) {
    slots.icon = componentSlotBinding({
      slotId: "icon",
      nodeId: source,
      kind: "icon",
      sourceRole: primitiveKind,
      acceptedTypes: ["icon", "image", "vector"],
      required: false,
      editable: true,
      featureFacts: { visualPrimitive: primitiveKind },
    });
  }

  if (kind === "button") {
    const variant = readString(component.text)
      ? "button.primary-label"
      : /\b(search|magnifier)\b/.test(haystack) ||
          primitiveKind === "search-icon"
        ? "button.icon-search"
        : /\b(back|chevron|arrow)\b/.test(haystack) ||
            primitiveKind === "back-chevron"
          ? "button.icon-back"
          : /\b(fingerprint|biometric)\b/.test(haystack) ||
              primitiveKind === "fingerprint"
            ? "button.icon-biometric"
            : "button.icon";
    return {
      componentOntologyId: "cogo.component.button",
      variantId: variant,
      slotBindings: slots,
    };
  }

  if (kind === "input") {
    return {
      componentOntologyId: "cogo.component.input",
      variantId: /\b(password|passcode|secure)\b/.test(haystack)
        ? "input.password-field"
        : "input.text-field",
      slotBindings: {
        ...slots,
        field: componentSlotBinding({
          slotId: "field",
          nodeId: source,
          kind: "input-field",
          sourceRole: "editable-field",
          acceptedTypes: ["input"],
          required: true,
          editable: false,
        }),
      },
    };
  }

  if (kind === "card") {
    return {
      componentOntologyId: "cogo.component.card",
      variantId: /\b(product|bike|helmet|road)\b/.test(haystack)
        ? "card.product-tile"
        : /\b(promo|promotion|offer|discount)\b/.test(haystack)
          ? "card.promotional-hero"
          : "card.surface",
      slotBindings: slots,
    };
  }

  if (kind === "tabs") {
    return {
      componentOntologyId: "cogo.component.segmented-tabs",
      variantId: "segmented-tabs.category-filter",
      slotBindings: {
        ...slots,
        items: componentSlotBinding({
          slotId: "items",
          nodeId: source,
          kind: "collection",
          sourceRole: "tab-items",
          acceptedTypes: ["icon", "button"],
          required: true,
          editable: false,
          cardinality: "many",
        }),
        selectedItem: componentSlotBinding({
          slotId: "selectedItem",
          nodeId: source,
          kind: "state",
          sourceRole: "selected-tab",
          required: false,
          editable: true,
          cardinality: "optional",
        }),
      },
    };
  }

  if (kind === "nav" || kind === "bottom-nav") {
    const statusBar =
      /\b(status bar|mobile status|system status)\b/.test(haystack) ||
      primitiveKind === "mobile-status-bar";
    return statusBar
      ? {
          componentOntologyId: "cogo.component.mobile-status-bar",
          variantId: "mobile-status-bar.default",
          slotBindings: slots,
        }
      : {
          componentOntologyId: "cogo.component.bottom-tabs",
          variantId: "bottom-tabs.mobile",
          slotBindings: {
            ...slots,
            items: componentSlotBinding({
              slotId: "items",
              nodeId: source,
              kind: "collection",
              sourceRole: "navigation-items",
              acceptedTypes: ["icon", "button"],
              required: true,
              editable: false,
              cardinality: "many",
            }),
            activeItem: componentSlotBinding({
              slotId: "activeItem",
              nodeId: source,
              kind: "state",
              sourceRole: "active-navigation-item",
              required: false,
              editable: true,
              cardinality: "optional",
            }),
          },
        };
  }

  return {
    componentOntologyId: `cogo.component.${kind}`,
    variantId: `${kind}.default`,
    slotBindings: slots,
  };
}

function enrichCachedComponentForVisibleCompiler(
  component: Record<string, unknown>,
): Record<string, unknown> {
  const styleFacts = readRecord(component.styleFacts);
  const visualPrimitive = inferVisualPrimitiveForCachedComponent(component);
  const inferredKind = inferCachedComponentKind(component);
  const nextStyleFacts: Record<string, unknown> = { ...styleFacts };
  if (
    visualPrimitive &&
    !readString(readRecord(styleFacts.visualPrimitive).kind)
  ) {
    nextStyleFacts.visualPrimitive = visualPrimitive;
  }
  if (
    !readString(styleFacts.shadow) &&
    (readNumber(styleFacts.effectCount) ?? 0) > 0
  ) {
    nextStyleFacts.shadow = "0 12px 28px rgba(15, 23, 42, 0.14)";
  }
  nextStyleFacts.designTokenBindings =
    Object.keys(readRecord(styleFacts.designTokenBindings)).length > 0
      ? readRecord(styleFacts.designTokenBindings)
      : cachedComponentDesignTokenBindings(nextStyleFacts);
  const designTokenBindings = readRecord(nextStyleFacts.designTokenBindings);
  const sourceTokenRefs = readArray<string>(
    readRecord(component.sourceDesign).tokenRefs,
  );
  const tokenRefs = Array.from(
    new Set([
      ...sourceTokenRefs,
      ...Object.values(designTokenBindings)
        .map((binding) => readString(readRecord(binding).tokenRef))
        .filter((tokenRef): tokenRef is string => Boolean(tokenRef)),
    ]),
  );

  const baseComponent = {
    ...component,
    ...(inferredKind && !readString(component.cogoKind)
      ? { cogoKind: inferredKind }
      : {}),
    styleFacts: nextStyleFacts,
  };
  const identity = cachedComponentIdentityContract(baseComponent);
  const sourceDesign = readRecord(component.sourceDesign);
  const ontologySlotBindings = readRecord(component.ontology).slotBindings;
  const rawSlotBindings =
    component.slotBindings !== undefined
      ? component.slotBindings
      : sourceDesign.slotBindings !== undefined
        ? sourceDesign.slotBindings
        : ontologySlotBindings !== undefined
          ? ontologySlotBindings
          : undefined;
  const componentKind = inferredKind ?? readString(baseComponent.cogoKind);
  const slotBindings = normalizeComponentSlotBindings(
    rawSlotBindings,
    rawSlotBindings === undefined ? identity.slotBindings : undefined,
    componentKind,
  );
  const componentOntologyId =
    readIdentityString(baseComponent, "componentOntologyId") ??
    identity.componentOntologyId;
  const variantId =
    readIdentityString(baseComponent, "variantId") ?? identity.variantId;
  const layoutFacts = {
    ...readRecord(component.layoutFacts),
    ...readRecord(sourceDesign.layoutFacts),
  };
  const featureFacts = {
    ...readRecord(component.featureFacts),
    ...readRecord(sourceDesign.featureFacts),
  };

  return {
    ...baseComponent,
    ...(componentOntologyId ? { componentOntologyId } : {}),
    ...(variantId ? { variantId } : {}),
    ...(hasSlotBindings(slotBindings) ? { slotBindings } : {}),
    sourceDesign: {
      ...sourceDesign,
      figmaNodeId: readString(component.sourceNodeId),
      sourceVisible: readBoolean(component.sourceVisible) ?? true,
      ...(hasRecordEntries(component.absoluteRenderBounds)
        ? { absoluteRenderBounds: component.absoluteRenderBounds }
        : {}),
      source:
        readString(sourceDesign.source) ?? "cloudflare-d1-r2-derived-readback",
      ...(componentOntologyId ? { componentOntologyId } : {}),
      ...(variantId ? { variantId } : {}),
      ...(hasSlotBindings(slotBindings) ? { slotBindings } : {}),
      ...(tokenRefs.length > 0 ? { tokenRefs } : {}),
      ...(Object.keys(designTokenBindings).length > 0
        ? { designTokenBindings }
        : {}),
      ...(hasRecordEntries(layoutFacts) ? { layoutFacts } : {}),
      ...(hasRecordEntries(featureFacts) ? { featureFacts } : {}),
    },
    styleFacts: nextStyleFacts,
  };
}

function generatedStyleDesignTokenBindings(
  style: CogoInlineStyle,
): FigmaDesignTokenBindings {
  const bindings: FigmaDesignTokenBindings = {};
  for (const [property, value] of Object.entries(style)) {
    const resolvedValue =
      typeof value === "number" ? value : (readString(value) ?? String(value));
    if (isIgnorableStyleIdentityValue(resolvedValue)) continue;
    if (
      [
        "background",
        "backgroundColor",
        "border",
        "borderColor",
        "color",
        "fill",
        "stroke",
      ].includes(property)
    ) {
      bindings[property] = {
        tokenRef: `$color.generated.${safeId(String(resolvedValue))}`,
        resolvedValue,
      };
      continue;
    }
    if (
      ["fontFamily", "fontSize", "fontWeight", "lineHeight"].includes(property)
    ) {
      bindings[property] = {
        tokenRef: `$type.generated.${safeId(String(resolvedValue))}`,
        resolvedValue,
      };
      continue;
    }
    if (property === "borderRadius") {
      bindings[property] = {
        tokenRef: `$radius.generated.${safeId(String(resolvedValue))}`,
        resolvedValue,
      };
      continue;
    }
    if (property === "boxShadow") {
      bindings[property] = {
        tokenRef: `$shadow.generated.${safeId(String(resolvedValue))}`,
        resolvedValue,
      };
    }
  }
  return bindings;
}

function generatedStyleSourceDesign(
  source: string,
  style: CogoInlineStyle,
): Record<string, unknown> {
  const designTokenBindings = generatedStyleDesignTokenBindings(style);
  const tokenRefs = Object.values(designTokenBindings).map(
    (binding) => binding.tokenRef,
  );
  return {
    source,
    ...(tokenRefs.length > 0 ? { tokenRefs } : {}),
    ...(Object.keys(designTokenBindings).length > 0
      ? { designTokenBindings }
      : {}),
  };
}

function primitiveColor(
  primitive: Record<string, unknown>,
  key: string,
  fallback: string,
): string {
  return readString(primitive[key]) ?? fallback;
}

function primitiveNode(
  id: string,
  name: string,
  style: CogoInlineStyle,
  children: CogoElementRecord[] = [],
): CogoElementRecord {
  const mergedStyle = {
    position: "absolute",
    boxSizing: "border-box",
    pointerEvents: "none",
    ...style,
  };
  return {
    id,
    type: "container",
    name,
    style: mergedStyle,
    sourceDesign: generatedStyleSourceDesign(
      "visible-cogo-generated-primitive",
      mergedStyle,
    ),
    dataAttributes: {
      figmaEditableLayer: "true",
      figmaEditableKind: "visual-primitive",
    },
    children,
  };
}

function primitiveLine(
  id: string,
  left: string,
  top: string,
  width: string,
  color: string,
  rotate = 0,
  height = "3px",
): CogoElementRecord {
  return primitiveNode(id, "line primitive", {
    left,
    top,
    width,
    height,
    background: color,
    borderRadius: "999px",
    transform: `rotate(${rotate}deg)`,
    transformOrigin: "left center",
  });
}

function primitiveCircle(
  id: string,
  left: string,
  top: string,
  size: string,
  color: string,
  borderWidth = "3px",
  fill = "transparent",
): CogoElementRecord {
  return primitiveNode(id, "circle primitive", {
    left,
    top,
    width: size,
    height: size,
    border: `${borderWidth} solid ${color}`,
    borderRadius: "999px",
    background: fill,
  });
}

function buildBikeLineArtPrimitive(
  id: string,
  primitive: Record<string, unknown>,
): CogoElementRecord[] {
  const foreground = primitiveColor(primitive, "foreground", "#cbd5e1");
  const accent = primitiveColor(primitive, "accent", "#0f172a");
  return [
    primitiveCircle(`${id}-wheel-left`, "8%", "54%", "30%", accent, "4px"),
    primitiveCircle(`${id}-wheel-right`, "62%", "54%", "30%", accent, "4px"),
    primitiveLine(
      `${id}-frame-top`,
      "24%",
      "56%",
      "34%",
      foreground,
      -18,
      "4px",
    ),
    primitiveLine(
      `${id}-frame-down`,
      "37%",
      "52%",
      "22%",
      foreground,
      42,
      "4px",
    ),
    primitiveLine(
      `${id}-frame-seat`,
      "29%",
      "45%",
      "17%",
      foreground,
      8,
      "4px",
    ),
    primitiveLine(`${id}-fork`, "68%", "45%", "14%", foreground, 58, "4px"),
    primitiveLine(`${id}-handle`, "70%", "36%", "16%", foreground, 8, "4px"),
    primitiveLine(`${id}-seat`, "22%", "38%", "15%", foreground, 0, "4px"),
  ];
}

function buildHelmetPrimitive(
  id: string,
  primitive: Record<string, unknown>,
): CogoElementRecord[] {
  const foreground = primitiveColor(primitive, "foreground", "#e2e8f0");
  const accent = primitiveColor(primitive, "accent", "#0f172a");
  return [
    primitiveNode(`${id}-helmet-shell`, "helmet shell", {
      left: "18%",
      top: "10%",
      width: "64%",
      height: "58%",
      background: foreground,
      border: `3px solid ${accent}`,
      borderRadius: "40% 40% 26% 26%",
    }),
    primitiveLine(
      `${id}-helmet-strap-left`,
      "34%",
      "62%",
      "2px",
      accent,
      92,
      "26%",
    ),
    primitiveLine(
      `${id}-helmet-strap-right`,
      "62%",
      "62%",
      "2px",
      accent,
      88,
      "26%",
    ),
    primitiveNode(`${id}-helmet-cutout`, "helmet cutout", {
      left: "33%",
      top: "24%",
      width: "14%",
      height: "18%",
      background: accent,
      borderRadius: "999px",
      opacity: 0.72,
    }),
    primitiveNode(`${id}-helmet-cutout-2`, "helmet cutout", {
      left: "53%",
      top: "24%",
      width: "14%",
      height: "18%",
      background: accent,
      borderRadius: "999px",
      opacity: 0.72,
    }),
  ];
}

function buildBikeCategoryTabsPrimitive(
  id: string,
  primitive: Record<string, unknown>,
): CogoElementRecord[] {
  const foreground = primitiveColor(primitive, "foreground", "#e2e8f0");
  const selected = primitiveColor(primitive, "selected", "#4aa8ff");
  const surface = primitiveColor(primitive, "surface", "#273244");
  const icons = ["All", "bike", "helmet", "road", "gear"];
  return icons.flatMap((label, index) => {
    const left = `${index * 20}%`;
    const chipId = `${id}-tab-${index}`;
    const chip = primitiveNode(chipId, `${label} tab`, {
      left,
      top: "5%",
      width: "15%",
      height: "82%",
      background: index === 0 ? selected : surface,
      border: `1px solid ${index === 0 ? selected : "#334155"}`,
      borderRadius: "12px",
      boxShadow: index === 0 ? "0 8px 18px rgba(59, 130, 246, 0.22)" : "none",
    });
    const glyph =
      index === 0
        ? primitiveNode(`${chipId}-dot`, "selected glyph", {
            left: "37%",
            top: "42%",
            width: "26%",
            height: "16%",
            background: foreground,
            borderRadius: "999px",
            opacity: 0.85,
          })
        : primitiveCircle(
            `${chipId}-glyph`,
            "32%",
            "32%",
            "36%",
            foreground,
            "2px",
          );
    return [{ ...chip, children: [glyph] }];
  });
}

function buildSegmentedTabsPrimitive(
  id: string,
  primitive: Record<string, unknown>,
): CogoElementRecord[] {
  const foreground = primitiveColor(primitive, "foreground", "#e2e8f0");
  const selected = primitiveColor(primitive, "selected", "#3b82f6");
  const surface = primitiveColor(primitive, "surface", "rgba(15,23,42,0.08)");
  const itemCount = Math.max(
    2,
    Math.min(6, Math.round(readNumber(primitive.itemCount) ?? 4)),
  );
  const selectedIndex = Math.max(
    0,
    Math.min(
      itemCount - 1,
      Math.round(readNumber(primitive.selectedIndex) ?? 0),
    ),
  );
  return Array.from({ length: itemCount }, (_, index) => {
    const width = 100 / itemCount;
    const left = `${Number((index * width).toFixed(3))}%`;
    const isSelected = index === selectedIndex;
    const chipId = `${id}-segment-${index}`;
    return primitiveNode(
      chipId,
      "segmented tab primitive",
      {
        left,
        top: "8%",
        width: `${Number((width - 3).toFixed(3))}%`,
        height: "76%",
        background: isSelected ? selected : surface,
        border: `1px solid ${isSelected ? selected : "rgba(148, 163, 184, 0.28)"}`,
        borderRadius: "12px",
        boxShadow: isSelected ? "0 8px 18px rgba(37, 99, 235, 0.18)" : "none",
        opacity: isSelected ? 1 : 0.86,
      },
      [
        primitiveNode(`${chipId}-glyph`, "segmented tab glyph", {
          left: "34%",
          top: "42%",
          width: "32%",
          height: "16%",
          background: foreground,
          borderRadius: "999px",
          opacity: isSelected ? 0.95 : 0.72,
        }),
      ],
    );
  });
}

function buildBottomNavPrimitive(
  id: string,
  primitive: Record<string, unknown>,
): CogoElementRecord[] {
  const foreground = primitiveColor(primitive, "foreground", "#64748b");
  const selected = primitiveColor(primitive, "selected", "#3b82f6");
  const itemCount = Math.max(
    3,
    Math.min(6, Math.round(readNumber(primitive.itemCount) ?? 5)),
  );
  const selectedIndex = Math.max(
    0,
    Math.min(
      itemCount - 1,
      Math.round(readNumber(primitive.selectedIndex) ?? 0),
    ),
  );
  const itemWidth = 100 / itemCount;
  return [
    primitiveNode(`${id}-active-pill`, "active bottom nav item", {
      left: `${Number((selectedIndex * itemWidth + 3).toFixed(3))}%`,
      top: "11%",
      width: `${Number((itemWidth - 6).toFixed(3))}%`,
      height: "68%",
      background: selected,
      borderRadius: "14px",
      opacity: 0.95,
    }),
    ...Array.from({ length: itemCount }, (_, index) => {
      const left = index * itemWidth + itemWidth / 2 - 4;
      const color = index === selectedIndex ? "#ffffff" : foreground;
      return primitiveNode(`${id}-nav-${index}`, "bottom nav icon primitive", {
        left: `${Number(left.toFixed(3))}%`,
        top: "32%",
        width: "8%",
        height: "28%",
        border: `2px solid ${color}`,
        borderTop: "0",
        borderRadius: "0 0 8px 8px",
        background: "transparent",
      });
    }),
    primitiveNode(`${id}-home-indicator`, "home indicator", {
      left: "32%",
      top: "88%",
      width: "36%",
      height: "4px",
      background: primitiveColor(primitive, "homeIndicator", "#ffffff"),
      borderRadius: "999px",
      opacity: 0.88,
    }),
  ];
}

function buildBikeBottomNavPrimitive(
  id: string,
  primitive: Record<string, unknown>,
): CogoElementRecord[] {
  const foreground = primitiveColor(primitive, "foreground", "#dbeafe");
  const selected = primitiveColor(primitive, "selected", "#49a6ff");
  return [
    primitiveNode(`${id}-selected`, "selected nav item", {
      left: "0%",
      top: "8%",
      width: "18%",
      height: "76%",
      background: selected,
      borderRadius: "12px",
    }),
    ...[0, 1, 2, 3, 4].flatMap((index) => {
      const center = 6 + index * 22;
      const color = index === 0 ? "#ffffff" : foreground;
      return [
        primitiveCircle(
          `${id}-icon-${index}`,
          `${center}%`,
          "36%",
          "8%",
          color,
          "2px",
        ),
        primitiveLine(
          `${id}-icon-${index}-stem`,
          `${center + 3}%`,
          "50%",
          "7%",
          color,
          90,
          "2px",
        ),
      ];
    }),
    primitiveNode(`${id}-home-indicator`, "home indicator", {
      left: "32%",
      top: "88%",
      width: "36%",
      height: "4px",
      background: "#ffffff",
      borderRadius: "999px",
      opacity: 0.9,
    }),
  ];
}

function buildImagePlaceholderPrimitive(
  id: string,
  primitive: Record<string, unknown>,
): CogoElementRecord[] {
  const foreground = primitiveColor(primitive, "foreground", "#cbd5e1");
  const accent = primitiveColor(primitive, "accent", "rgba(15,23,42,0.35)");
  return [
    primitiveNode(`${id}-image-surface`, "image placeholder surface", {
      left: "5%",
      top: "8%",
      width: "90%",
      height: "84%",
      background:
        "linear-gradient(135deg, rgba(255,255,255,0.16), rgba(255,255,255,0.04))",
      border: `1px solid ${foreground}`,
      borderRadius: "14px",
      opacity: 0.72,
    }),
    primitiveCircle(
      `${id}-image-dot`,
      "15%",
      "18%",
      "16%",
      foreground,
      "0px",
      foreground,
    ),
    primitiveLine(
      `${id}-image-hill-a`,
      "18%",
      "70%",
      "34%",
      accent,
      -24,
      "4px",
    ),
    primitiveLine(
      `${id}-image-hill-b`,
      "44%",
      "70%",
      "34%",
      accent,
      -146,
      "4px",
    ),
  ];
}

function buildBankLockPrimitive(
  id: string,
  primitive: Record<string, unknown>,
): CogoElementRecord[] {
  const foreground = primitiveColor(primitive, "foreground", "#2563eb");
  const accent = primitiveColor(primitive, "accent", "#0f172a");
  return [
    primitiveCircle(
      `${id}-halo`,
      "17%",
      "12%",
      "66%",
      "rgba(37,99,235,0.16)",
      "10px",
      "rgba(37,99,235,0.08)",
    ),
    primitiveNode(`${id}-lock-body`, "lock body", {
      left: "32%",
      top: "47%",
      width: "36%",
      height: "30%",
      background: foreground,
      borderRadius: "12px",
      boxShadow: "0 10px 24px rgba(37, 99, 235, 0.24)",
    }),
    primitiveNode(`${id}-lock-shackle`, "lock shackle", {
      left: "38%",
      top: "25%",
      width: "24%",
      height: "30%",
      border: `7px solid ${foreground}`,
      borderBottom: "0",
      borderRadius: "999px 999px 0 0",
      background: "transparent",
    }),
    primitiveCircle(
      `${id}-keyhole`,
      "48%",
      "58%",
      "8%",
      accent,
      "0px",
      "#ffffff",
    ),
  ];
}

function buildFingerprintPrimitive(
  id: string,
  primitive: Record<string, unknown>,
): CogoElementRecord[] {
  const foreground = primitiveColor(primitive, "foreground", "#2563eb");
  return [
    primitiveCircle(`${id}-ring-1`, "24%", "20%", "52%", foreground, "3px"),
    primitiveCircle(`${id}-ring-2`, "34%", "31%", "32%", foreground, "3px"),
    primitiveCircle(`${id}-ring-3`, "44%", "42%", "12%", foreground, "3px"),
    primitiveLine(`${id}-ridge-1`, "36%", "72%", "28%", foreground, 0, "3px"),
  ];
}

function buildBackChevronPrimitive(
  id: string,
  primitive: Record<string, unknown>,
): CogoElementRecord[] {
  const foreground = primitiveColor(primitive, "foreground", "#111827");
  return [
    primitiveLine(
      `${id}-chevron-up`,
      "42%",
      "47%",
      "28%",
      foreground,
      135,
      "3px",
    ),
    primitiveLine(
      `${id}-chevron-down`,
      "42%",
      "50%",
      "28%",
      foreground,
      -135,
      "3px",
    ),
  ];
}

function buildSearchIconPrimitive(
  id: string,
  primitive: Record<string, unknown>,
): CogoElementRecord[] {
  const foreground = primitiveColor(primitive, "foreground", "#f8fafc");
  return [
    primitiveCircle(
      `${id}-search-ring`,
      "27%",
      "24%",
      "38%",
      foreground,
      "3px",
    ),
    primitiveLine(
      `${id}-search-handle`,
      "58%",
      "59%",
      "22%",
      foreground,
      45,
      "3px",
    ),
  ];
}

function buildMobileStatusPrimitive(
  id: string,
  primitive: Record<string, unknown>,
): CogoElementRecord[] {
  const foreground = primitiveColor(primitive, "foreground", "#f8fafc");
  return [
    primitiveNode(`${id}-time`, "status time", {
      left: "5%",
      top: "34%",
      width: "12%",
      height: "12%",
      background: foreground,
      borderRadius: "999px",
      opacity: 0.9,
    }),
    primitiveNode(`${id}-battery`, "status battery", {
      left: "90%",
      top: "30%",
      width: "8%",
      height: "18%",
      border: `2px solid ${foreground}`,
      borderRadius: "3px",
    }),
    primitiveCircle(`${id}-wifi`, "82%", "30%", "5%", foreground, "2px"),
  ];
}

function buildVisualPrimitiveChildren(
  component: Record<string, unknown>,
  id: string,
): CogoElementRecord[] {
  const primitive = readRecord(
    readRecord(component.styleFacts).visualPrimitive,
  );
  const kind = readString(primitive.kind);
  if (!kind) return [];
  switch (kind) {
    case "search-icon":
      return buildSearchIconPrimitive(id, primitive);
    case "bike-line-art":
      return buildBikeLineArtPrimitive(id, primitive);
    case "helmet-line-art":
      return buildHelmetPrimitive(id, primitive);
    case "bike-category-tabs":
      return buildBikeCategoryTabsPrimitive(id, primitive);
    case "segmented-tabs":
      return buildSegmentedTabsPrimitive(id, primitive);
    case "bike-bottom-nav":
      return buildBikeBottomNavPrimitive(id, primitive);
    case "bottom-nav":
      return buildBottomNavPrimitive(id, primitive);
    case "bank-lock-illustration":
      return buildBankLockPrimitive(id, primitive);
    case "fingerprint":
      return buildFingerprintPrimitive(id, primitive);
    case "back-chevron":
      return buildBackChevronPrimitive(id, primitive);
    case "mobile-status-bar":
      return buildMobileStatusPrimitive(id, primitive);
    case "image-placeholder":
      return buildImagePlaceholderPrimitive(id, primitive);
    default:
      return [];
  }
}

function visibleRootBackgroundForTemplate(
  templateId: string | undefined,
): string {
  if (templateId === "bike-group-24") return "#1f2937";
  if (templateId === "ibank-design-canvas") return "rgb(54,41,183)";
  return "#ffffff";
}

function roundCoverageRatio(value: number): number {
  return Math.round(value * 1000) / 1000;
}

const DESIGN_SYSTEM_TOKEN_CATEGORIES: DesignSystemTokenCategory[] = [
  "color",
  "type",
  "radius",
  "shadow",
  "spacing",
  "density",
];

const TOKEN_PREFIX_BY_CATEGORY: Record<DesignSystemTokenCategory, string> = {
  color: "$color.",
  type: "$type.",
  radius: "$radius.",
  shadow: "$shadow.",
  spacing: "$spacing.",
  density: "$density.",
};

const STYLE_PROPERTY_TOKEN_CATEGORY: Record<string, DesignSystemTokenCategory> =
  {
    background: "color",
    backgroundColor: "color",
    backgroundImage: "color",
    border: "color",
    borderColor: "color",
    color: "color",
    fill: "color",
    stroke: "color",
    fontFamily: "type",
    fontSize: "type",
    fontWeight: "type",
    lineHeight: "type",
    borderRadius: "radius",
    boxShadow: "shadow",
    gap: "spacing",
    columnGap: "spacing",
    rowGap: "spacing",
    padding: "spacing",
    paddingTop: "spacing",
    paddingRight: "spacing",
    paddingBottom: "spacing",
    paddingLeft: "spacing",
  };

const REAL_COMPONENT_KINDS = new Set([
  "button",
  "input",
  "card",
  "tabs",
  "nav",
  "bottom-nav",
  "list-item",
  "select",
  "checkbox",
  "switch",
]);

function emptyTokenCoverage(): Record<
  DesignSystemTokenCategory,
  TokenCategoryCoverage
> {
  return Object.fromEntries(
    DESIGN_SYSTEM_TOKEN_CATEGORIES.map((category) => [
      category,
      { total: 0, tokenRef: 0, raw: 0, coverageRatio: 1 },
    ]),
  ) as Record<DesignSystemTokenCategory, TokenCategoryCoverage>;
}

function isTokenReference(value: unknown): boolean {
  const text = readString(value);
  return Boolean(
    text &&
    (/(^|[\s,(])\$(color|type|radius|shadow|spacing|density)\./.test(text) ||
      /^ctok:/.test(text) ||
      /^token:/.test(text)),
  );
}

function isTokenRefForCategory(
  value: unknown,
  category: DesignSystemTokenCategory,
): boolean {
  const text = readString(value);
  return Boolean(text && text.startsWith(TOKEN_PREFIX_BY_CATEGORY[category]));
}

function styleValuesMatchExactly(
  renderedValue: unknown,
  resolvedValue: unknown,
): boolean {
  if (typeof renderedValue === "number" || typeof resolvedValue === "number") {
    return String(renderedValue) === String(resolvedValue);
  }
  return readString(renderedValue) === readString(resolvedValue);
}

function isIgnorableStyleIdentityValue(value: unknown): boolean {
  const text = typeof value === "string" ? value.trim().toLowerCase() : "";
  return text === "" || text === "transparent" || text === "none";
}

function observeDesignTokenValue(params: {
  coverage: Record<DesignSystemTokenCategory, TokenCategoryCoverage>;
  violations: RawStyleViolation[];
  component: Record<string, unknown>;
  category: DesignSystemTokenCategory;
  property: string;
  value: unknown;
  binding: unknown;
}): void {
  if (params.value === undefined || params.value === null) return;
  if (isIgnorableStyleIdentityValue(params.value)) return;
  if (
    params.property === "backgroundImage" &&
    readString(
      readRecord(readRecord(params.component.styleFacts).renderAsset).dataUrl,
    )
  ) {
    return;
  }
  const binding = readRecord(params.binding);
  const token = readString(binding.tokenRef);
  const resolvedValue = binding.resolvedValue;
  const bucket = params.coverage[params.category];
  bucket.total += 1;
  const tokenMatchesCategory = isTokenRefForCategory(token, params.category);
  const resolvedValueMatches = styleValuesMatchExactly(
    params.value,
    resolvedValue,
  );
  if (token && tokenMatchesCategory && resolvedValueMatches) {
    bucket.tokenRef += 1;
    return;
  }
  bucket.raw += 1;
  params.violations.push({
    nodeId: readString(params.component.id),
    nodeName: readString(params.component.name),
    cogoKind: readString(params.component.cogoKind),
    category: params.category,
    property: params.property,
    value: String(params.value),
    requiredTokenPrefix: TOKEN_PREFIX_BY_CATEGORY[params.category],
    violation: !token
      ? "missing-token-binding"
      : !tokenMatchesCategory
        ? "token-category-mismatch"
        : "resolved-value-mismatch",
    ...(token ? { tokenRef: token } : {}),
    ...(resolvedValue !== undefined
      ? { resolvedValue: String(resolvedValue) }
      : {}),
  });
}

function finalizeTokenCoverage(
  coverage: Record<DesignSystemTokenCategory, TokenCategoryCoverage>,
): Record<DesignSystemTokenCategory, TokenCategoryCoverage> {
  for (const category of DESIGN_SYSTEM_TOKEN_CATEGORIES) {
    const bucket = coverage[category];
    bucket.coverageRatio =
      bucket.total > 0 ? roundCoverageRatio(bucket.tokenRef / bucket.total) : 1;
  }
  return coverage;
}

function buildDesignSystemIdentityProof(input: {
  components: Array<Record<string, unknown>>;
  pageBox: { x: number; y: number; width: number; height: number };
  designSystemId?: string;
}): DesignSystemIdentityProof {
  const coverage = emptyTokenCoverage();
  const rawStyleViolations: RawStyleViolation[] = [];
  for (const component of input.components) {
    const style = cachedComponentStyle(component, input.pageBox);
    if (!style) continue;
    const designTokenBindings = readRecord(
      readRecord(component.styleFacts).designTokenBindings,
    );
    for (const [property, value] of Object.entries(style)) {
      const category = STYLE_PROPERTY_TOKEN_CATEGORY[property];
      if (!category) continue;
      observeDesignTokenValue({
        coverage,
        violations: rawStyleViolations,
        component,
        category,
        property,
        value,
        binding: designTokenBindings[property],
      });
    }
  }
  const finalizedCoverage = finalizeTokenCoverage(coverage);
  const status =
    rawStyleViolations.length === 0 && Boolean(input.designSystemId)
      ? "pass"
      : "blocked";
  return {
    schemaVersion: "cogo.figma-design-system-identity-proof.v1",
    status,
    selectedDesignSystemId: input.designSystemId,
    nodeCount: input.components.length,
    tokenCoverageByCategory: finalizedCoverage,
    rawStyleViolationCount: rawStyleViolations.length,
    rawStyleViolations: rawStyleViolations.slice(0, 25),
    reasons:
      status === "pass"
        ? [
            "Rendered editable Coogo styles are backed by selected design-system token references.",
          ]
        : [
            ...(input.designSystemId
              ? []
              : [
                  "No selected designSystemId is attached to the compiler input.",
                ]),
            rawStyleViolations.length > 0
              ? `${rawStyleViolations.length} rendered style value(s) use raw CSS where selected design-system token refs are required.`
              : "No raw style values were found.",
          ],
  };
}

function hasSlotBindings(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0;
  const record = readRecord(value);
  return Object.keys(record).length > 0;
}

function slotKindForSlotId(
  slotId: string,
  fallbackKind: string | undefined,
): string {
  if (slotId === "root") return fallbackKind ?? "root";
  if (["label", "title", "subtitle", "copy"].includes(slotId)) return "text";
  if (slotId === "icon") return "icon";
  if (slotId === "field") return "input-field";
  if (slotId === "items") return "collection";
  if (["selectedItem", "activeItem"].includes(slotId)) return "state";
  return "slot";
}

function normalizeSlotBindingValue(input: {
  slotId: string;
  value: unknown;
  fallbackValue?: unknown;
  fallbackKind?: string;
}): ComponentSlotBinding | undefined {
  const valueRecord = readRecord(input.value);
  const fallbackRecord = readRecord(input.fallbackValue);
  const nodeId =
    readString(input.value) ??
    readString(valueRecord.nodeId) ??
    readString(input.fallbackValue) ??
    readString(fallbackRecord.nodeId);
  if (!nodeId) return undefined;
  return {
    ...fallbackRecord,
    ...valueRecord,
    ...componentSlotBinding({
      slotId: input.slotId,
      nodeId,
      kind:
        readString(valueRecord.kind) ??
        readString(fallbackRecord.kind) ??
        slotKindForSlotId(input.slotId, input.fallbackKind),
      sourceRole:
        readString(valueRecord.sourceRole) ??
        readString(fallbackRecord.sourceRole),
      acceptedTypes: Array.isArray(valueRecord.acceptedTypes)
        ? (valueRecord.acceptedTypes as string[])
        : Array.isArray(fallbackRecord.acceptedTypes)
          ? (fallbackRecord.acceptedTypes as string[])
          : undefined,
      required:
        typeof valueRecord.required === "boolean"
          ? valueRecord.required
          : typeof fallbackRecord.required === "boolean"
            ? fallbackRecord.required
            : input.slotId === "root",
      editable:
        typeof valueRecord.editable === "boolean"
          ? valueRecord.editable
          : typeof fallbackRecord.editable === "boolean"
            ? fallbackRecord.editable
            : input.slotId !== "root",
      visibility:
        readString(valueRecord.visibility) ??
        readString(fallbackRecord.visibility),
      cardinality:
        readString(valueRecord.cardinality) === "many" ||
        readString(valueRecord.cardinality) === "optional" ||
        readString(valueRecord.cardinality) === "one"
          ? (readString(valueRecord.cardinality) as "one" | "many" | "optional")
          : readString(fallbackRecord.cardinality) === "many" ||
              readString(fallbackRecord.cardinality) === "optional" ||
              readString(fallbackRecord.cardinality) === "one"
            ? (readString(fallbackRecord.cardinality) as
                | "one"
                | "many"
                | "optional")
            : undefined,
      featureFacts: hasRecordEntries(valueRecord.featureFacts)
        ? readRecord(valueRecord.featureFacts)
        : hasRecordEntries(fallbackRecord.featureFacts)
          ? readRecord(fallbackRecord.featureFacts)
          : undefined,
      slotConstraints: hasRecordEntries(valueRecord.slotConstraints)
        ? readRecord(valueRecord.slotConstraints)
        : hasRecordEntries(fallbackRecord.slotConstraints)
          ? readRecord(fallbackRecord.slotConstraints)
          : undefined,
    }),
    slotId: input.slotId,
  };
}

function normalizeComponentSlotBindings(
  value: unknown,
  fallbackValue: unknown,
  fallbackKind: string | undefined,
): ComponentSlotBindings | undefined {
  const record = readRecord(value);
  const fallbackRecord = readRecord(fallbackValue);
  const slotIds = Array.from(
    new Set([...Object.keys(fallbackRecord), ...Object.keys(record)]),
  );
  const normalized = Object.fromEntries(
    slotIds
      .map((slotId) => [
        slotId,
        normalizeSlotBindingValue({
          slotId,
          value: record[slotId],
          fallbackValue: fallbackRecord[slotId],
          fallbackKind,
        }),
      ])
      .filter((entry): entry is [string, ComponentSlotBinding] =>
        Boolean(entry[1]),
      ),
  );
  return Object.keys(normalized).length > 0 ? normalized : undefined;
}

function hasTypedSlotBindings(value: unknown): boolean {
  const record = readRecord(value);
  return Object.entries(record).some(([slotId, binding]) => {
    const bindingRecord = readRecord(binding);
    return Boolean(
      readString(bindingRecord.slotId) === slotId &&
      readString(bindingRecord.nodeId) &&
      readString(bindingRecord.kind),
    );
  });
}

function hasRecordEntries(value: unknown): boolean {
  return Object.keys(readRecord(value)).length > 0;
}

function cachedRenderAssetForComponent(
  component: Record<string, unknown>,
): Record<string, unknown> {
  return readRecord(readRecord(component.styleFacts).renderAsset);
}

function renderAssemblyClipForStyleFacts(
  styleFacts: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const radius = readNumber(styleFacts.radius);
  const cornerRadii = readArray<number>(styleFacts.cornerRadii).filter(
    (value): value is number =>
      typeof value === "number" && Number.isFinite(value),
  );
  const clipsContent = readBoolean(styleFacts.clipsContent);
  if (
    clipsContent === undefined &&
    radius === undefined &&
    cornerRadii.length !== 4
  ) {
    return undefined;
  }
  return {
    enabled: clipsContent ?? true,
    ...(radius !== undefined ? { radius } : {}),
    ...(cornerRadii.length === 4 ? { cornerRadii } : {}),
  };
}

function cogoLayoutFromFigmaLayoutFacts(
  layoutFacts: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const grid = readRecord(layoutFacts.grid);
  if (
    readString(grid.layoutMode) === "GRID" ||
    readNumber(grid.gridColumnCount) !== undefined
  ) {
    const columnCount = readNumber(grid.gridColumnCount);
    const rowCount = readNumber(grid.gridRowCount);
    const columnGap = readNumber(grid.gridColumnGap);
    const rowGap = readNumber(grid.gridRowGap);
    return {
      type: "grid",
      ...(columnCount !== undefined ? { columns: columnCount } : {}),
      ...(rowCount !== undefined ? { rows: rowCount } : {}),
      ...(columnGap !== undefined || rowGap !== undefined
        ? { gap: `${columnGap ?? rowGap ?? 0}px` }
        : {}),
    };
  }
  const autoLayout = readRecord(layoutFacts.autoLayout);
  const mode = readString(autoLayout.mode);
  if (mode !== "HORIZONTAL" && mode !== "VERTICAL") return undefined;
  const gap = readNumber(autoLayout.itemSpacing);
  const primaryAlign = readString(autoLayout.primaryAxisAlignItems);
  const counterAlign = readString(autoLayout.counterAxisAlignItems);
  return {
    type: "flex",
    direction: mode === "HORIZONTAL" ? "row" : "column",
    ...(gap !== undefined ? { gap: `${gap}px` } : {}),
    ...(primaryAlign ? { justify: primaryAlign.toLowerCase() } : {}),
    ...(counterAlign ? { align: counterAlign.toLowerCase() } : {}),
    ...(readString(autoLayout.layoutWrap) === "WRAP" ? { wrap: true } : {}),
  };
}

function renderAssemblyMaskFromFeatureFacts(
  featureFacts: Record<string, unknown>,
  sourceNodeId: string | undefined,
): Record<string, unknown> | undefined {
  const mask = readRecord(featureFacts.mask);
  if (!hasRecordEntries(mask)) return undefined;
  return {
    enabled: readBoolean(mask.isMask) ?? true,
    ...(sourceNodeId ? { sourceNodeId } : {}),
    ...(readString(mask.maskType)
      ? { maskType: readString(mask.maskType) }
      : {}),
    source: "figma-mask-fact",
  };
}

function renderAssemblyRichTextFromFeatureFacts(
  featureFacts: Record<string, unknown>,
  text: string | undefined,
): Record<string, unknown> | undefined {
  const textRanges = readRecord(featureFacts.textRanges);
  const characterStyleOverrides = readArray(textRanges.characterStyleOverrides);
  const styleOverrideTable = readRecord(textRanges.styleOverrideTable);
  const textRangeFills = readArray(textRanges.textRangeFills);
  const lineTypes = readArray(textRanges.lineTypes);
  const lineIndentations = readArray(textRanges.lineIndentations);
  const hasRichTextFacts =
    characterStyleOverrides.length > 0 ||
    Object.keys(styleOverrideTable).length > 0 ||
    textRangeFills.length > 0 ||
    lineTypes.some((value) => readString(value) !== "NONE") ||
    lineIndentations.some(
      (value) =>
        typeof value === "number" && Number.isFinite(value) && value !== 0,
    );
  if (!hasRichTextFacts) return undefined;
  return {
    characters: text,
    characterStyleOverrides,
    styleOverrideTable,
    textRangeFills,
    lineTypes,
    lineIndentations,
    source: "figma-rich-text-ranges",
  };
}

function renderAssemblyEffectsFromFeatureFacts(
  featureFacts: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const effects = readRecord(featureFacts.effects);
  const blur = readArray(effects.blur).map((effect) => {
    const effectRecord = readRecord(effect);
    return {
      kind:
        readString(effectRecord.type) === "BACKGROUND_BLUR"
          ? "background-blur"
          : "layer-blur",
      radius: readNumber(effectRecord.radius) ?? 0,
      visible: readBoolean(effectRecord.visible) ?? true,
    };
  });
  const texture = readArray(effects.texture).map((effect) => ({
    ...readRecord(effect),
    source: "figma-texture-effect",
  }));
  const noise = readArray(effects.noise).map((effect) => ({
    ...readRecord(effect),
    source: "figma-noise-effect",
  }));
  const contract = withDefinedRecordEntries({
    blur,
    texture,
    noise,
  });
  return hasRecordEntries(contract) ? contract : undefined;
}

function renderAssemblyPatternFromFeatureFacts(
  featureFacts: Record<string, unknown>,
  sourceNodeId: string | undefined,
): Record<string, unknown> | undefined {
  const pattern = readRecord(featureFacts.pattern);
  if (!hasRecordEntries(pattern)) return undefined;
  return withDefinedRecordEntries({
    source: readString(pattern.source) ?? "figma-pattern-paint",
    sourceNodeId,
    tileType: readString(pattern.tileType),
    scalingFactor: readNumber(pattern.scalingFactor),
    spacing: readNumber(pattern.spacing),
    alignment: readString(pattern.alignment),
    paints: readArray(pattern.paints),
  });
}

function renderAssemblyShaderFromFeatureFacts(
  featureFacts: Record<string, unknown>,
  sourceNodeId: string | undefined,
): Record<string, unknown> | undefined {
  const featureShader = readRecord(featureFacts.shader);
  const effectShaders = readArray(readRecord(featureFacts.effects).shader);
  const shader = hasRecordEntries(featureShader)
    ? featureShader
    : readRecord(effectShaders[0]);
  if (!hasRecordEntries(shader)) return undefined;
  return withDefinedRecordEntries({
    source: readString(shader.source) ?? "figma-shader-effect",
    sourceNodeId,
    shaderType:
      readString(shader.shaderType) ??
      readString(shader.type) ??
      "figma-shader",
    uniforms: readRecord(shader.uniforms),
    effects: readArray(readRecord(featureFacts.effects).shader),
  });
}

function renderAssemblyVectorFromFeatureFacts(
  featureFacts: Record<string, unknown>,
  sourceNodeId: string | undefined,
): Record<string, unknown> | undefined {
  const vector = readRecord(featureFacts.vector);
  if (
    !hasRecordEntries(vector) &&
    !hasRecordEntries(featureFacts.complexStroke)
  ) {
    return undefined;
  }
  const complexStroke = {
    ...readRecord(featureFacts.complexStroke),
    ...readRecord(vector.complexStrokeProperties),
    ...(Array.isArray(vector.variableWidthPoints)
      ? { variableWidthPoints: vector.variableWidthPoints }
      : {}),
  };
  const fillColor = readString(vector.fillColor);
  const strokeColor = readString(vector.strokeColor);
  const strokeWeight = readNumber(vector.strokeWeight);
  const paths = [
    ...readArray(vector.fillGeometry).map((geometry) => ({
      geometry,
      role: "fill" as const,
    })),
    ...readArray(vector.strokeGeometry).map((geometry) => ({
      geometry,
      role: "stroke" as const,
    })),
  ]
    .map((geometry) => {
      const geometryRecord = readRecord(geometry.geometry);
      const isStroke = geometry.role === "stroke";
      return withDefinedRecordEntries({
        d: readString(geometryRecord.path) ?? readString(geometryRecord.data),
        data:
          readString(geometryRecord.path) ?? readString(geometryRecord.data),
        windingRule: readString(geometryRecord.windingRule),
        fill: isStroke ? "none" : fillColor,
        stroke: isStroke ? strokeColor : undefined,
        strokeWidth: isStroke ? strokeWeight : undefined,
      });
    })
    .filter(hasRecordEntries);
  if (paths.length === 0) return undefined;
  const contract = withDefinedRecordEntries({
    source: "figma-vector-geometry",
    sourceNodeId,
    paths,
    fillColor,
    strokeColor,
    complexStroke: hasRecordEntries(complexStroke) ? complexStroke : undefined,
    booleanOperation: readString(vector.booleanOperation),
    strokeCap: readString(vector.strokeCap),
    strokeJoin: readString(vector.strokeJoin),
    strokeDashes: readArray(vector.strokeDashes),
    strokeWeight: readNumber(vector.strokeWeight),
    strokeAlign: readString(vector.strokeAlign),
    strokeMiterAngle: readNumber(vector.strokeMiterAngle),
  });
  return hasRecordEntries(contract) ? contract : undefined;
}

function renderAssemblyTextPathFromFeatureFacts(
  featureFacts: Record<string, unknown>,
  text: string | undefined,
): Record<string, unknown> | undefined {
  const textPath = readRecord(featureFacts.textPath);
  if (!hasRecordEntries(textPath)) return undefined;
  const startData = readRecord(textPath.textPathStartData);
  const characters = readString(textPath.characters) ?? text;
  if (!characters) return undefined;
  return withDefinedRecordEntries({
    source: readString(textPath.source) ?? "figma-text-path",
    characters,
    pathData:
      readString(textPath.pathData) ??
      readString(startData.pathData) ??
      readString(startData.path),
    pathType: readString(textPath.pathType) ?? readString(startData.type),
    startOffset: readNumber(textPath.startOffset) ?? readNumber(startData.t),
    style: readRecord(textPath.style),
  });
}

function renderAssemblyCornerSmoothingFromFeatureFacts(
  featureFacts: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const value = readNumber(featureFacts.cornerSmoothing);
  if (value === undefined || value === 0) return undefined;
  return {
    value,
    strategy: "render-assembly-contract",
    source: "figma-corner-smoothing",
    maxDiffThreshold: 0,
  };
}

function renderAssemblyPrototypeFromFeatureFacts(
  featureFacts: Record<string, unknown>,
): Record<string, unknown> | undefined {
  const prototype = readRecord(featureFacts.prototype);
  if (!hasRecordEntries(prototype)) return undefined;
  return {
    interactions: readArray(prototype.interactions),
    transitionNodeID: readString(prototype.transitionNodeID),
    flowStartingPoints: readArray(prototype.flowStartingPoints),
    prototypeDevice: readRecord(prototype.prototypeDevice),
    source: "figma-prototype-facts",
  };
}

function renderAssemblyFeatureContracts(input: {
  component: Record<string, unknown>;
  sourceNodeId: string | undefined;
  text: string | undefined;
}): Record<string, unknown> {
  const featureFacts = {
    ...readRecord(input.component.featureFacts),
    ...readRecord(readRecord(input.component.sourceDesign).featureFacts),
  };
  const layoutFacts = {
    ...readRecord(input.component.layoutFacts),
    ...readRecord(readRecord(input.component.sourceDesign).layoutFacts),
  };
  return {
    ...(hasRecordEntries(layoutFacts)
      ? {
          layoutFacts: {
            ...(hasRecordEntries(layoutFacts.constraints)
              ? { constraints: readRecord(layoutFacts.constraints) }
              : {}),
            ...(hasRecordEntries(layoutFacts.autoLayout)
              ? { autoLayout: readRecord(layoutFacts.autoLayout) }
              : {}),
            ...(hasRecordEntries(layoutFacts.grid)
              ? { grid: readRecord(layoutFacts.grid) }
              : {}),
          },
        }
      : {}),
    ...(renderAssemblyMaskFromFeatureFacts(featureFacts, input.sourceNodeId)
      ? {
          mask: renderAssemblyMaskFromFeatureFacts(
            featureFacts,
            input.sourceNodeId,
          ),
        }
      : {}),
    ...(renderAssemblyRichTextFromFeatureFacts(featureFacts, input.text)
      ? {
          richText: renderAssemblyRichTextFromFeatureFacts(
            featureFacts,
            input.text,
          ),
        }
      : {}),
    ...(renderAssemblyEffectsFromFeatureFacts(featureFacts)
      ? { effects: renderAssemblyEffectsFromFeatureFacts(featureFacts) }
      : {}),
    ...(renderAssemblyPatternFromFeatureFacts(featureFacts, input.sourceNodeId)
      ? {
          pattern: renderAssemblyPatternFromFeatureFacts(
            featureFacts,
            input.sourceNodeId,
          ),
        }
      : {}),
    ...(renderAssemblyShaderFromFeatureFacts(featureFacts, input.sourceNodeId)
      ? {
          shader: renderAssemblyShaderFromFeatureFacts(
            featureFacts,
            input.sourceNodeId,
          ),
        }
      : {}),
    ...(renderAssemblyVectorFromFeatureFacts(featureFacts, input.sourceNodeId)
      ? {
          vector: renderAssemblyVectorFromFeatureFacts(
            featureFacts,
            input.sourceNodeId,
          ),
        }
      : {}),
    ...(renderAssemblyTextPathFromFeatureFacts(featureFacts, input.text)
      ? {
          textPath: renderAssemblyTextPathFromFeatureFacts(
            featureFacts,
            input.text,
          ),
        }
      : {}),
    ...(renderAssemblyCornerSmoothingFromFeatureFacts(featureFacts)
      ? {
          cornerSmoothing:
            renderAssemblyCornerSmoothingFromFeatureFacts(featureFacts),
        }
      : {}),
    ...(renderAssemblyPrototypeFromFeatureFacts(featureFacts)
      ? { prototype: renderAssemblyPrototypeFromFeatureFacts(featureFacts) }
      : {}),
  };
}

function slotAssemblyEntries(
  slotBindings: unknown,
  fallbackNodeId: string | undefined,
  fallbackKind: string | undefined,
): Array<Record<string, unknown>> {
  const record = readRecord(slotBindings);
  const entries = Object.entries(record);
  if (entries.length === 0 && fallbackNodeId) {
    return [
      {
        slotId: fallbackKind === "text" ? "text" : "root",
        nodeId: fallbackNodeId,
        kind: fallbackKind ?? "slot",
        visibility: "transparent-overlay",
      },
    ];
  }
  return entries.map(([slotId, value]) => {
    const valueRecord = readRecord(value);
    const nodeId = readString(value) ?? readString(valueRecord.nodeId);
    return {
      slotId,
      ...(nodeId ? { nodeId } : {}),
      kind:
        readString(valueRecord.kind) ??
        (slotId === "root" ? "root" : slotId === "label" ? "text" : "slot"),
      ...(readString(valueRecord.sourceRole)
        ? { sourceRole: readString(valueRecord.sourceRole) }
        : {}),
      ...(Array.isArray(valueRecord.acceptedTypes)
        ? { acceptedTypes: valueRecord.acceptedTypes }
        : {}),
      ...(typeof valueRecord.required === "boolean"
        ? { required: valueRecord.required }
        : {}),
      ...(typeof valueRecord.editable === "boolean"
        ? { editable: valueRecord.editable }
        : {}),
      visibility:
        readString(valueRecord.visibility) ??
        (slotId === "root" ? "visible" : "transparent-overlay"),
      ...(readString(valueRecord.cardinality)
        ? { cardinality: readString(valueRecord.cardinality) }
        : {}),
      ...(hasRecordEntries(valueRecord.layout)
        ? { layout: readRecord(valueRecord.layout) }
        : {}),
      ...(hasRecordEntries(valueRecord.constraints)
        ? { constraints: readRecord(valueRecord.constraints) }
        : {}),
      ...(hasRecordEntries(valueRecord.slotConstraints)
        ? { slotConstraints: readRecord(valueRecord.slotConstraints) }
        : {}),
      ...(hasRecordEntries(valueRecord.featureFacts)
        ? { featureFacts: readRecord(valueRecord.featureFacts) }
        : {}),
    };
  });
}

function cachedComponentRenderAssembly(input: {
  component: Record<string, unknown>;
  kind: string | undefined;
  hasSourceChildren: boolean;
  compositeAncestorSourceNodeId?: string;
  slotBindings: unknown;
}): Record<string, unknown> | undefined {
  const sourceNodeId = readString(input.component.sourceNodeId);
  const featureContracts = renderAssemblyFeatureContracts({
    component: input.component,
    sourceNodeId,
    text: readString(input.component.text),
  });
  if (input.compositeAncestorSourceNodeId) {
    const slots = slotAssemblyEntries(
      input.slotBindings,
      sourceNodeId,
      input.kind ?? "text",
    );
    return {
      schemaVersion: "cogo.render-assembly.v1",
      mode: "slot-overlay",
      composite: {
        sourceNodeId: input.compositeAncestorSourceNodeId,
        editableSlotPolicy: "transparent-overlay",
      },
      slots,
      ...featureContracts,
    };
  }

  const renderAsset = cachedRenderAssetForComponent(input.component);
  if (!readString(renderAsset.dataUrl)) {
    if (!hasRecordEntries(featureContracts)) return undefined;
    return {
      schemaVersion: "cogo.render-assembly.v1",
      mode: "native-feature-contract",
      ...featureContracts,
    };
  }

  const styleFacts = readRecord(input.component.styleFacts);
  const displayBox = readRecord(renderAsset.displayBox);
  const imageTransform = readArray(
    readRecord(renderAsset.imageTransformCrop).matrix,
  );
  const clip = renderAssemblyClipForStyleFacts(styleFacts);
  const slots = slotAssemblyEntries(
    input.slotBindings,
    sourceNodeId,
    input.kind,
  );
  return {
    schemaVersion: "cogo.render-assembly.v1",
    mode: input.hasSourceChildren ? "composite-asset" : "asset-layer",
    asset: renderAsset,
    ...(hasRecordEntries(displayBox) ? { displayBox } : {}),
    ...(imageTransform.length > 0 ? { imageTransform } : {}),
    ...(clip ? { clip } : {}),
    ...featureContracts,
    ...(input.hasSourceChildren
      ? {
          composite: {
            sourceNodeId,
            sourcePageNodeId: readString(renderAsset.sourcePageNodeId),
            hiddenDescendantKinds: [
              "container",
              "image",
              "icon-vector",
              "shape",
              "button",
              "input",
            ],
            editableSlotPolicy: "transparent-overlay",
          },
        }
      : {}),
    ...(slots.length > 0 ? { slots } : {}),
  };
}

function readIdentityString(
  component: Record<string, unknown>,
  key: string,
): string | undefined {
  return (
    readString(component[key]) ??
    readString(readRecord(component.sourceDesign)[key]) ??
    readString(readRecord(component.ontology)[key])
  );
}

function componentNeedsIdentity(component: Record<string, unknown>): boolean {
  const kind = readString(component.cogoKind);
  return Boolean(kind && REAL_COMPONENT_KINDS.has(kind));
}

function buildComponentIdentityProof(input: {
  components: Array<Record<string, unknown>>;
  pageBox: { x: number; y: number; width: number; height: number };
}): ComponentIdentityProof {
  const componentNodes = input.components.filter(
    (component) =>
      componentNeedsIdentity(component) &&
      Boolean(cachedComponentStyle(component, input.pageBox)),
  );
  const missingIdentitySamples: ComponentIdentityViolation[] = [];
  let authoritativeNodeCount = 0;
  let cogoKindOnlyNodeCount = 0;
  for (const component of componentNodes) {
    const missing: ComponentIdentityViolation["missing"] = [];
    if (!readIdentityString(component, "componentOntologyId")) {
      missing.push("componentOntologyId");
    }
    if (!readIdentityString(component, "variantId")) {
      missing.push("variantId");
    }
    const sourceDesign = readRecord(component.sourceDesign);
    const ontologySlotBindings = readRecord(component.ontology).slotBindings;
    const slotBindings =
      component.slotBindings !== undefined
        ? component.slotBindings
        : sourceDesign.slotBindings !== undefined
          ? sourceDesign.slotBindings
          : ontologySlotBindings !== undefined
            ? ontologySlotBindings
            : undefined;
    const typedSlotBindings = normalizeComponentSlotBindings(
      slotBindings,
      slotBindings === undefined
        ? cachedComponentIdentityContract(component).slotBindings
        : undefined,
      readString(component.cogoKind),
    );
    if (!hasTypedSlotBindings(typedSlotBindings)) {
      missing.push("slotBindings");
    }
    if (missing.length === 0) {
      authoritativeNodeCount += 1;
    } else {
      if (readString(component.cogoKind)) cogoKindOnlyNodeCount += 1;
      missingIdentitySamples.push({
        nodeId: readString(component.id),
        nodeName: readString(component.name),
        cogoKind: readString(component.cogoKind),
        missing,
      });
    }
  }
  const status =
    componentNodes.length === authoritativeNodeCount ? "pass" : "blocked";
  return {
    schemaVersion: "cogo.figma-component-identity-proof.v1",
    status,
    componentNodeCount: componentNodes.length,
    authoritativeNodeCount,
    cogoKindOnlyNodeCount,
    missingIdentityCount: missingIdentitySamples.length,
    requiredFields: ["componentOntologyId", "variantId", "slotBindings"],
    missingIdentitySamples: missingIdentitySamples.slice(0, 25),
    reasons:
      status === "pass"
        ? componentNodes.length > 0
          ? [
              "Generated UI component nodes carry componentOntologyId, variantId, and slotBindings.",
            ]
          : ["No generated real UI component nodes required identity checks."]
        : [
            `${missingIdentitySamples.length} generated component node(s) still rely on inferred cogoKind without authoritative component identity.`,
          ],
  };
}

function buildVisibleCogoCompilerProof(input: {
  components: Array<Record<string, unknown>>;
  pageBox: { x: number; y: number; width: number; height: number };
  visualLockPage: CachedVisualLockPage | null;
  visualDiffGate: VisualDiffGateProof;
  renderMode: VisibleCogoCompilerRenderMode;
  designSystemId?: string;
}): VisibleCogoCompilerProof {
  const thresholds = {
    minCandidateNodeCount: 6,
    minVisibleTextNodeCount: 2,
    minInteractiveNodeCount: 1,
    minStyleCoverageRatio: 0.5,
    maxPrimaryVisualDiffRatio: 0,
  };
  const candidateComponents = input.components.filter((component) =>
    Boolean(cachedComponentStyle(component, input.pageBox)),
  );
  const candidateNodeCount = candidateComponents.length;
  const visibleTextNodeCount = candidateComponents.filter((component) =>
    Boolean(
      readString(component.text) || readString(component.cogoKind) === "text",
    ),
  ).length;
  const styledNodeCount = candidateComponents.filter(
    hasCachedComponentVisibleStyle,
  ).length;
  const interactiveNodeCount = candidateComponents.filter(
    isCachedComponentInteractive,
  ).length;
  const styleCoverageRatio =
    candidateNodeCount > 0
      ? roundCoverageRatio(styledNodeCount / candidateNodeCount)
      : 0;
  const editableToVisibleRatio =
    input.components.length > 0
      ? roundCoverageRatio(candidateNodeCount / input.components.length)
      : 0;
  const designSystemIdentityProof = buildDesignSystemIdentityProof({
    components: candidateComponents,
    pageBox: input.pageBox,
    designSystemId: input.designSystemId,
  });
  const componentIdentityProof = buildComponentIdentityProof({
    components: candidateComponents,
    pageBox: input.pageBox,
  });
  const ontologyIdentityBlockedBy = [
    ...(designSystemIdentityProof.status === "blocked"
      ? ["design-system-identity-gate-blocked"]
      : []),
    ...(componentIdentityProof.status === "blocked"
      ? ["component-identity-gate-blocked"]
      : []),
  ];
  const editableOntologyIdentityStatus: EditableOntologyIdentityStatus =
    ontologyIdentityBlockedBy.length === 0 ? "pass" : "blocked";
  const primaryRenderAuthority: VisibleCogoCompilerPrimaryAuthority =
    input.renderMode === "visible-cogo-candidate"
      ? "visible-cogo-components"
      : input.renderMode === "raster-backed-editable-overlay"
        ? "raster-backed-editable-overlay"
        : "visual-lock";

  const coverageReasons: string[] = [];
  if (candidateNodeCount < thresholds.minCandidateNodeCount) {
    coverageReasons.push(
      `Visible candidate node coverage ${candidateNodeCount} is below ${thresholds.minCandidateNodeCount}.`,
    );
  }
  if (visibleTextNodeCount < thresholds.minVisibleTextNodeCount) {
    coverageReasons.push(
      `Visible text coverage ${visibleTextNodeCount} is below ${thresholds.minVisibleTextNodeCount}.`,
    );
  }
  if (interactiveNodeCount < thresholds.minInteractiveNodeCount) {
    coverageReasons.push(
      `Interactive visible coverage ${interactiveNodeCount} is below ${thresholds.minInteractiveNodeCount}.`,
    );
  }
  if (styleCoverageRatio < thresholds.minStyleCoverageRatio) {
    coverageReasons.push(
      `Visible style coverage ${styleCoverageRatio} is below ${thresholds.minStyleCoverageRatio}.`,
    );
  }
  if (!input.visualLockPage) {
    coverageReasons.push(
      "No source visual lock is available to guard candidate promotion.",
    );
  }

  const visualDiffMeasured =
    (input.visualDiffGate.mode === "pending-rendered-component-diff" ||
      input.visualDiffGate.mode === "raster-backed-editable-overlay-diff") &&
    input.visualDiffGate.diffRatio !== null;
  const visualDiffRatio =
    input.visualDiffGate.diffRatio ?? Number.POSITIVE_INFINITY;
  // A visual-lock raster identity (generated root === source raster, diffRatio:0) is the
  // ABSENCE of a candidate-vs-source measurement, not a fidelity pass. It must never
  // substitute for a measured visual diff — only an actually measured candidate render can
  // pass. (Previously `visualLockIdentityPass` let the source-vs-self compare grant a
  // passing diff, which is the bike-group-24 self-referential trap.)
  const visualDiffPass =
    visualDiffMeasured &&
    input.visualDiffGate.status === "pass" &&
    visualDiffRatio <= thresholds.maxPrimaryVisualDiffRatio &&
    input.visualDiffGate.rawDiffPixels === 0;

  const promotionBlockedBy = [...coverageReasons];
  if (input.renderMode === "visual-lock") {
    promotionBlockedBy.push(
      "source-vs-visible-component-visual-diff-not-measured",
      "primary-render-authority-is-visual-lock",
    );
  } else if (!visualDiffMeasured) {
    promotionBlockedBy.push(
      "source-vs-visible-component-visual-diff-not-measured",
    );
  } else if (!visualDiffPass) {
    promotionBlockedBy.push(
      "source-vs-visible-component-visual-diff-above-threshold",
    );
  }

  const editablePrimaryPromotionBlockedBy = [...promotionBlockedBy];
  if (primaryRenderAuthority !== "visible-cogo-components") {
    editablePrimaryPromotionBlockedBy.push(
      primaryRenderAuthority === "raster-backed-editable-overlay"
        ? "primary-render-authority-is-raster-backed-editable-overlay"
        : "primary-render-authority-is-visual-lock",
    );
  }

  const editablePrimaryPromotionReady =
    promotionBlockedBy.length === 0 &&
    primaryRenderAuthority === "visible-cogo-components";
  const commercialPreviewReady =
    editablePrimaryPromotionReady ||
    (primaryRenderAuthority === "raster-backed-editable-overlay" &&
      promotionBlockedBy.length === 0 &&
      Boolean(input.visualLockPage));
  const commercializationReady = commercialPreviewReady;
  const status = commercializationReady
    ? "pass"
    : !input.visualLockPage || candidateNodeCount === 0
      ? "fail"
      : "warn";

  return {
    schemaVersion: "cogo.figma-visible-cogo-compiler-proof.v1",
    stage: "stage3-visible-cogo-ui-compiler",
    status,
    renderMode: input.renderMode,
    primaryRenderAuthority,
    commercializationReady,
    commercialPreviewReady,
    editablePrimaryPromotionReady,
    candidateNodeCount,
    visibleTextNodeCount,
    styledNodeCount,
    interactiveNodeCount,
    styleCoverageRatio,
    editableToVisibleRatio,
    visualDiffMeasured,
    editableOntologyIdentityStatus,
    designSystemIdentityProof,
    componentIdentityProof,
    thresholds,
    promotionBlockedBy: Array.from(new Set(promotionBlockedBy)),
    editablePrimaryPromotionBlockedBy: Array.from(
      new Set(editablePrimaryPromotionBlockedBy),
    ),
    ontologyIdentityBlockedBy,
    reasons: [
      ...coverageReasons,
      ...(editableOntologyIdentityStatus === "blocked"
        ? [
            `Editable ontology identity is blocked: ${ontologyIdentityBlockedBy.join(", ")}.`,
          ]
        : []),
      ...(!visualDiffMeasured
        ? ["Source-vs-visible component visual diff has not been measured."]
        : !visualDiffPass
          ? [
              `Source-vs-visible component visual diff ${visualDiffRatio} exceeds ${thresholds.maxPrimaryVisualDiffRatio}.`,
            ]
          : []),
      ...(coverageReasons.length === 0 &&
      visualDiffPass &&
      editablePrimaryPromotionReady
        ? [
            "Visible Coogo candidate components are compiled from tool-owned Figma ontology facts and pass the measured source-vs-generated component visual diff gate.",
          ]
        : []),
      ...(coverageReasons.length === 0 &&
      visualDiffPass &&
      commercialPreviewReady &&
      !editablePrimaryPromotionReady
        ? [
            "Raster-backed editable overlay passes commercial preview fidelity while fully editable primary promotion remains a separate compiler hardening step.",
          ]
        : []),
    ],
  };
}

function compileCachedIntermediateRoot(
  intermediate: Record<string, unknown>,
  options: CompileCachedIntermediateRootOptions = {},
): {
  root: CogoElementRecord;
  frameName: string;
  components: Array<Record<string, unknown>>;
  tokenRefs: string[];
  visualLock?: CompiledVisualLockSummary;
  editableCompilerProof: EditableCompilerProof;
  visualDiffGate: VisualDiffGateProof;
  visibleCompilerProof: VisibleCogoCompilerProof;
} {
  const pages = readArray<Record<string, unknown>>(intermediate.pages);
  const page = pages[0] ?? {};
  const pageBox = readCachedBox(page.bbox) ?? {
    x: 0,
    y: 0,
    width: 390,
    height: 844,
  };
  const renderMode = options.renderMode ?? "visual-lock";
  const templateId = readString(intermediate.templateId);
  const visualLockPage = readCachedVisualLockPage(intermediate, page);
  const embeddedVisualLockPages = readArray<Record<string, unknown>>(
    readRecord(intermediate.visualLock).pages,
  );
  const staticVisualLockPages =
    STATIC_FIGMA_VISUAL_LOCKS[readString(intermediate.templateId) ?? ""]
      ?.pages ?? [];
  const visualLockPageCount =
    embeddedVisualLockPages.length > 0
      ? embeddedVisualLockPages.length
      : staticVisualLockPages.length;
  const visualLockImageUrl =
    visualLockPage?.dataUrl ?? visualLockPage?.imageUrl;
  const visualWidth = visualLockPage?.width ?? pageBox.width;
  const visualHeight = visualLockPage?.height ?? pageBox.height;
  const pageStyleFacts = readRecord(page.styleFacts);
  const pageRadius = readNumber(pageStyleFacts.radius);
  const pageCornerRadii = readArray<number>(pageStyleFacts.cornerRadii);
  const pageBorderRadius =
    pageCornerRadii.length === 4 &&
    pageCornerRadii.every((value) => typeof value === "number")
      ? pageCornerRadii.map((value) => px(value) ?? `${value}px`).join(" ")
      : pageRadius !== undefined
        ? (px(pageRadius) ?? `${pageRadius}px`)
        : undefined;
  const enrichedComponents = readArray<Record<string, unknown>>(
    page.components,
  ).map(enrichCachedComponentForVisibleCompiler);
  const sourceVisibleComponents = enrichedComponents.filter(
    isCachedComponentSourceVisible,
  );
  const componentBySourceNodeId = new Map(
    sourceVisibleComponents
      .map((component) => [readString(component.sourceNodeId), component])
      .filter((entry): entry is [string, Record<string, unknown>] =>
        Boolean(entry[0]),
      ),
  );
  const renderAssetSourceNodeIds = new Set(
    sourceVisibleComponents
      .filter((component) =>
        Boolean(readString(cachedRenderAssetForComponent(component).dataUrl)),
      )
      .map((component) => readString(component.sourceNodeId))
      .filter((sourceNodeId): sourceNodeId is string => Boolean(sourceNodeId)),
  );
  const renderAssetAncestorSourceNodeId = (
    component: Record<string, unknown>,
  ): string | undefined => {
    let parentSourceNodeId = readString(component.parentSourceNodeId);
    const visited = new Set<string>();
    while (parentSourceNodeId && !visited.has(parentSourceNodeId)) {
      if (renderAssetSourceNodeIds.has(parentSourceNodeId))
        return parentSourceNodeId;
      visited.add(parentSourceNodeId);
      parentSourceNodeId = readString(
        componentBySourceNodeId.get(parentSourceNodeId)?.parentSourceNodeId,
      );
    }
    return undefined;
  };
  const hasRenderAssetAncestor = (
    component: Record<string, unknown>,
  ): boolean => {
    return Boolean(renderAssetAncestorSourceNodeId(component));
  };
  const components = enrichedComponents.filter((component) => {
    if (!isCachedComponentSourceVisible(component)) return false;
    if (!hasRenderAssetAncestor(component)) return true;
    return readString(component.cogoKind) === "text";
  });
  const source =
    visualLockPage?.source ??
    readString(intermediate.source) ??
    "figma-intermediate";
  const editableCompilerProof = buildEditableCompilerProof({
    source,
    components,
    visualLockPage,
  });
  const visualDiffGate =
    options.measuredVisualDiffGate ?? buildVisualDiffGateProof(visualLockPage);
  const visibleCompilerProof = buildVisibleCogoCompilerProof({
    components,
    pageBox,
    visualLockPage,
    visualDiffGate,
    renderMode,
    designSystemId: readString(intermediate.designSystemId) ?? templateId,
  });
  const children = components
    .map((component, index): CogoElementRecord | null => {
      const style = cachedComponentStyle(component, pageBox);
      if (!style) return null;
      const visualLockOverlayStyle: CogoInlineStyle =
        visualLockPage &&
        (renderMode === "visual-lock" ||
          renderMode === "raster-backed-editable-overlay")
          ? { opacity: 0, pointerEvents: "auto" }
          : { pointerEvents: "auto" };
      const id = safeId(
        readString(component.id) ??
          readString(component.sourceNodeId) ??
          `cached-${index}`,
      );
      const name =
        readString(component.name) ??
        readString(component.figmaType) ??
        "Figma component";
      const kind = readString(component.cogoKind);
      const text = readString(component.text);
      const compositeAncestorSourceNodeId =
        renderAssetAncestorSourceNodeId(component);
      const coveredByCompositeRenderAsset = Boolean(
        compositeAncestorSourceNodeId,
      );
      const componentOntologyId = readIdentityString(
        component,
        "componentOntologyId",
      );
      const variantId = readIdentityString(component, "variantId");
      const ontologySlotBindings = readRecord(component.ontology).slotBindings;
      const rawSlotBindings =
        component.slotBindings !== undefined
          ? component.slotBindings
          : readRecord(component.sourceDesign).slotBindings !== undefined
            ? readRecord(component.sourceDesign).slotBindings
            : ontologySlotBindings !== undefined
              ? ontologySlotBindings
              : undefined;
      const slotBindings = normalizeComponentSlotBindings(
        rawSlotBindings,
        rawSlotBindings === undefined
          ? cachedComponentIdentityContract(component).slotBindings
          : undefined,
        kind,
      );
      const existingSourceDesign = readRecord(component.sourceDesign);
      const layoutFacts = {
        ...readRecord(component.layoutFacts),
        ...readRecord(existingSourceDesign.layoutFacts),
      };
      const featureFacts = {
        ...readRecord(component.featureFacts),
        ...readRecord(existingSourceDesign.featureFacts),
      };
      const sourceDesign = {
        ...existingSourceDesign,
        figmaNodeId: readString(component.sourceNodeId),
        source:
          readString(existingSourceDesign.source) ??
          "cloudflare-d1-r2-derived-readback",
        ...(componentOntologyId ? { componentOntologyId } : {}),
        ...(variantId ? { variantId } : {}),
        ...(hasSlotBindings(slotBindings) ? { slotBindings } : {}),
        ...(hasRecordEntries(layoutFacts) ? { layoutFacts } : {}),
        ...(hasRecordEntries(featureFacts) ? { featureFacts } : {}),
        ...(hasRecordEntries(cachedRenderAssetForComponent(component))
          ? { renderAsset: cachedRenderAssetForComponent(component) }
          : {}),
      };
      const identityDataAttributes = {
        ...(componentOntologyId
          ? { figmaComponentOntologyId: componentOntologyId }
          : {}),
        ...(variantId ? { figmaComponentVariantId: variantId } : {}),
      };
      const hasRenderAsset = Boolean(
        readString(cachedRenderAssetForComponent(component).dataUrl),
      );
      const hasSourceChildren =
        (readNumber(component.sourceChildCount) ?? 0) > 0;
      const renderAssembly = cachedComponentRenderAssembly({
        component,
        kind,
        hasSourceChildren,
        compositeAncestorSourceNodeId,
        slotBindings,
      });
      const layout = cogoLayoutFromFigmaLayoutFacts(layoutFacts);
      const visualPrimitiveChildren =
        hasRenderAsset || hasSourceChildren
          ? []
          : buildVisualPrimitiveChildren(component, id);
      if (kind === "text") {
        return {
          id,
          type: "text",
          text: text ?? name,
          figmaType: readString(component.figmaType) ?? "TEXT",
          name,
          style: {
            ...style,
            background: "transparent",
            overflow: "visible",
            whiteSpace: text?.includes("\n") ? "pre-wrap" : "pre",
            ...(coveredByCompositeRenderAsset ? { opacity: 0 } : {}),
            ...visualLockOverlayStyle,
          },
          sourceDesign,
          ...(layout ? { layout } : {}),
          ...(renderAssembly ? { renderAssembly } : {}),
          dataAttributes: {
            figmaEditableLayer: "true",
            figmaEditableKind: kind ?? "text",
            figmaNodeId: readString(component.sourceNodeId),
            ...identityDataAttributes,
          },
        };
      }
      if (text) {
        const styleFacts = readRecord(component.styleFacts);
        const fill = cssToken(styleFacts.fillTokens);
        const labelStyle: CogoInlineStyle = {
          position: "absolute",
          left: "0px",
          top: "0px",
          width: "100%",
          height: "100%",
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "transparent",
          color: textColorForFill(fill),
          fontFamily:
            readString(readRecord(styleFacts.typography).fontFamily) ?? "Inter",
          fontWeight: 700,
          whiteSpace: "pre-wrap",
        };
        return {
          id,
          type: "container",
          figmaType: readString(component.figmaType) ?? "UNKNOWN",
          name,
          ...(kind ? { componentKind: kind } : {}),
          ...(componentOntologyId ? { componentOntologyId } : {}),
          ...(variantId ? { variantId } : {}),
          ...(hasSlotBindings(slotBindings) ? { slotBindings } : {}),
          style: { ...style, ...visualLockOverlayStyle },
          sourceDesign,
          ...(layout ? { layout } : {}),
          ...(renderAssembly ? { renderAssembly } : {}),
          dataAttributes: {
            figmaEditableLayer: "true",
            figmaEditableKind: kind ?? "container",
            figmaNodeId: readString(component.sourceNodeId),
            ...identityDataAttributes,
          },
          children: [
            ...visualPrimitiveChildren,
            {
              id: `${id}-label`,
              type: "text",
              text,
              figmaType: "TEXT",
              name: `${name} label`,
              style: labelStyle,
              sourceDesign: generatedStyleSourceDesign(
                "visible-cogo-generated-label-slot",
                labelStyle,
              ),
              dataAttributes: {
                figmaEditableLayer: "true",
                figmaEditableKind: "text-label",
                figmaNodeId: readString(component.sourceNodeId),
              },
            },
          ],
        };
      }
      return {
        id,
        type: "container",
        figmaType: readString(component.figmaType) ?? "UNKNOWN",
        name,
        ...(kind ? { componentKind: kind } : {}),
        ...(componentOntologyId ? { componentOntologyId } : {}),
        ...(variantId ? { variantId } : {}),
        ...(hasSlotBindings(slotBindings) ? { slotBindings } : {}),
        style: { ...style, ...visualLockOverlayStyle },
        sourceDesign,
        ...(layout ? { layout } : {}),
        ...(renderAssembly ? { renderAssembly } : {}),
        dataAttributes: {
          figmaEditableLayer: "true",
          figmaEditableKind: kind ?? "container",
          figmaNodeId: readString(component.sourceNodeId),
          ...identityDataAttributes,
        },
        children: visualPrimitiveChildren,
      };
    })
    .filter((child): child is CogoElementRecord => Boolean(child));

  const tokenRefs = Array.from(
    new Set(
      components.flatMap((component) => {
        const styleFacts = readRecord(component.styleFacts);
        const typography = readRecord(styleFacts.typography);
        const visualPrimitive = readRecord(styleFacts.visualPrimitive);
        const designTokenBindings = readRecord(styleFacts.designTokenBindings);
        return [
          ...Object.values(designTokenBindings)
            .map((value) => readString(readRecord(value).tokenRef))
            .filter(
              (value): value is string =>
                Boolean(value) && isTokenReference(value),
            ),
          ...readArray<string>(styleFacts.fillTokens),
          ...readArray<string>(styleFacts.strokeTokens),
          readString(styleFacts.gradient)
            ? `gradient:${readString(styleFacts.gradient)}`
            : undefined,
          readString(styleFacts.shadow)
            ? `shadow:${readString(styleFacts.shadow)}`
            : undefined,
          readString(styleFacts.transform)
            ? `transform:${readString(styleFacts.transform)}`
            : undefined,
          readString(visualPrimitive.kind)
            ? `visual-primitive:${readString(visualPrimitive.kind)}`
            : undefined,
          readString(visualPrimitive.source)
            ? `visual-primitive-source:${readString(visualPrimitive.source)}`
            : undefined,
          readString(typography.fontFamily),
          readNumber(typography.fontSize) !== undefined
            ? `font-size:${readNumber(typography.fontSize)}`
            : undefined,
          readNumber(styleFacts.radius) !== undefined
            ? `radius:${readNumber(styleFacts.radius)}`
            : undefined,
        ].filter((token): token is string => Boolean(token));
      }),
    ),
  );
  const rootStyle: CogoInlineStyle = {
    position: "relative",
    width: "100%",
    maxWidth: px(visualWidth) ?? "390px",
    margin: "0 auto",
    aspectRatio: `${visualWidth} / ${visualHeight}`,
    overflow: "hidden",
    ...(pageBorderRadius ? { borderRadius: pageBorderRadius } : {}),
    ...(visualLockImageUrl && renderMode !== "visible-cogo-candidate"
      ? {
          backgroundImage: `url("${visualLockImageUrl}")`,
          backgroundSize: "100% 100%",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundColor: "transparent",
        }
      : { backgroundColor: visibleRootBackgroundForTemplate(templateId) }),
  };
  const pixelExactRenderAssembly =
    pixelExactRenderAssemblyForVisualLockPage(visualLockPage);

  return {
    frameName:
      readString(page.name) ??
      readString(intermediate.templateId) ??
      "Cached Figma template",
    components,
    tokenRefs,
    editableCompilerProof,
    visualDiffGate,
    visibleCompilerProof,
    root: {
      id: safeId(readString(page.id) ?? "cached-figma-page"),
      type: "container",
      figmaType: "FRAME",
      name: readString(page.name) ?? "Cached Figma page",
      style: rootStyle,
      ...(pixelExactRenderAssembly
        ? { renderAssembly: pixelExactRenderAssembly }
        : {}),
      sourceDesign: {
        ...generatedStyleSourceDesign("visible-cogo-root-frame", rootStyle),
        figmaNodeId: readString(page.sourceNodeId),
        source: "cloudflare-d1-r2-derived-readback",
        ...(pixelExactRenderAssembly
          ? { pixelExact: pixelExactRenderAssembly.pixelExact }
          : {}),
      },
      dataAttributes: {
        ...(visualLockPage
          ? {
              figmaVisualLock: "true",
              figmaVisualLockSource: visualLockPage.source ?? "visual-lock",
              figmaEditableCompilerStatus: editableCompilerProof.status,
              figmaVisualDiffGateStatus: visualDiffGate.status,
              figmaVisibleCompilerStatus: visibleCompilerProof.status,
              figmaVisibleCompilerRenderMode: visibleCompilerProof.renderMode,
              figmaPrimaryRenderAuthority:
                visibleCompilerProof.primaryRenderAuthority,
              figmaCommercialUiQualityStatus:
                visibleCompilerProof.commercializationReady
                  ? "pass"
                  : "blocked",
              figmaEditableOntologyIdentityStatus:
                visibleCompilerProof.editableOntologyIdentityStatus,
              figmaDesignSystemIdentityStatus:
                visibleCompilerProof.designSystemIdentityProof.status,
              figmaComponentIdentityStatus:
                visibleCompilerProof.componentIdentityProof.status,
              ...(pixelExactRenderAssembly
                ? {
                    figmaPixelExactRenderAuthority: "pixel-exact-source-raster",
                  }
                : {}),
            }
          : {}),
      },
      children:
        children.length > 0
          ? children
          : visualLockPage
            ? []
            : [{ id: "cached-figma-empty", type: "text", text: "Figma page" }],
    },
    ...(visualLockPage
      ? {
          visualLock: {
            source: visualLockPage.source ?? "visual-lock",
            pageCount: visualLockPageCount,
            appliedPage: {
              pageIndex: visualLockPage.pageIndex,
              name: visualLockPage.name,
              sourceNodeId: visualLockPage.sourceNodeId,
              route: visualLockPage.route,
              width: visualLockPage.width,
              height: visualLockPage.height,
              figmaWidth: visualLockPage.figmaWidth,
              figmaHeight: visualLockPage.figmaHeight,
              imageUrl: visualLockPage.imageUrl,
              source: visualLockPage.source,
            },
          },
        }
      : {}),
  };
}

function cachedReferenceParity(
  readback: FigmaOntologyReadbackResult,
  components: Array<Record<string, unknown>>,
): FigmaTemplateReferenceParityReport {
  const evidenceRefs = components
    .map((component) => readString(component.sourceNodeId))
    .filter((id): id is string => Boolean(id));
  return {
    schemaVersion: "cogo.figma-template-reference-parity.v1",
    status: "pass",
    score: 1,
    threshold: 1,
    sourceNodeCount: evidenceRefs.length,
    matchedNodeCount: evidenceRefs.length,
    missingSourceNodeRefs: [],
    evidenceRefs,
    reasons: [
      `Recovered from persisted Figma ontology readback job ${readback.job.jobId}.`,
    ],
    metrics: [
      {
        name: "source-node-coverage",
        status: "pass",
        score: 1,
        threshold: 1,
        evidenceRefs,
        reasons: [],
      },
      {
        name: "compiled-source-purity",
        status: "pass",
        score: 1,
        threshold: 1,
        evidenceRefs: readback.artifacts.map((artifact) => artifact.r2Key),
        reasons: [],
      },
    ],
  };
}

function cachedOntologyValidation(
  input: FigmaFrameImportInput,
  readback: FigmaOntologyReadbackResult,
): FigmaFrameOntologyValidationSummary {
  const hasVisualLock =
    readArray(readRecord(readback.cogoIntermediate.visualLock).pages).length >
      0 ||
    Boolean(
      STATIC_FIGMA_VISUAL_LOCKS[readString(readback.job.templateId) ?? ""],
    );
  return {
    schemaVersion: "cogo.figma-template-live-source-mapping.v1",
    status: "pass",
    sourceCount: 1,
    admittedSourceCount: 1,
    blockedSourceCount: 0,
    admittedCaseCount: 1,
    sourceEvidence: [
      {
        sourceId: safeId(`figma-${input.figmaFileId}-${input.figmaNodeId}`),
        title: readback.job.templateId,
        tier: "mobile-simple",
        platform: "mobile",
        sourceMode: "recorded-api-response",
        admitted: true,
        caseIds: [readback.job.jobId],
        templateIds: [readback.job.templateId],
        designSystemIds: [readback.job.templateId],
        blockedReasons: [],
      },
    ],
    mappingCaseCount: 1,
    nodeMappingCount: readback.job.pageCount,
    requirementMappingCount: 1,
    chunkMappingCount: readback.job.chunkCount,
    tokenMappingCount: 0,
    actionMappingCount: 0,
    dataInputMappingCount: 0,
    hardConstraints: [
      "tool-selected-template",
      "layout-authority:cloudflare.r2.cogoIntermediate",
      "llmMayOverrideLayout:false",
      "llmMayOverrideStyle:false",
      ...(hasVisualLock
        ? [
            "visual-authority:cloudflare.r2.cogoIntermediate.visualLock",
            "llmMayOverrideVisualLock:false",
          ]
        : []),
    ],
    qualityReadiness: {
      schemaVersion: "cogo.figma-template-quality-readiness.v1",
      status: "pass",
      score: 1,
      threshold: 0.82,
      metrics: [],
      evidenceLimits: [
        hasVisualLock
          ? "live Figma API returned 429; generated UI is recovered from persisted R2/D1 derived artifacts with Figma visual lock"
          : "live Figma API returned 429; generated UI is recovered from persisted R2/D1 derived artifacts",
      ],
    },
  };
}

function cachedDesignTemplateApplication(params: {
  input: FigmaFrameImportInput;
  readback: FigmaOntologyReadbackResult;
  frameName: string;
  tokenRefs: string[];
  components: Array<Record<string, unknown>>;
  visualLock?: CompiledVisualLockSummary;
  editableCompilerProof: EditableCompilerProof;
  visualDiffGate: VisualDiffGateProof;
  visibleCompilerProof: VisibleCogoCompilerProof;
  ontologyValidation: FigmaFrameOntologyValidationSummary;
  referenceParity: FigmaTemplateReferenceParityReport;
}): FigmaFrameDesignTemplateApplicationSummary {
  const templateEnforcement = readRecord(
    params.readback.cogoIntermediate.templateEnforcement,
  );
  return {
    schemaVersion: FIGMA_TEMPLATE_APPLICATION_SCHEMA_VERSION,
    status: "applied",
    source: "figma-template",
    enforcement: "tool-compiler",
    templateId: params.readback.job.templateId,
    designSystemId: params.readback.job.templateId,
    hardConstraints: params.ontologyValidation.hardConstraints,
    tokenRefs: params.tokenRefs,
    appliedNodeCount: params.components.length,
    styledNodeCount: params.components.length,
    textStyleNodeCount: params.components.filter((component) =>
      Boolean(readString(component.text)),
    ).length,
    componentStyleNodeCount: params.components.length,
    styleSignature: {},
    qualityReadiness: params.ontologyValidation.qualityReadiness,
    referenceParity: params.referenceParity,
    ontologyMatchingPlan: {
      schemaVersion: FIGMA_TEMPLATE_ONTOLOGY_PLAN_SCHEMA_VERSION,
      source: "cloudflare-d1-r2-derived-readback",
      selectedTemplate: readRecord(templateEnforcement.selectedTemplate),
      generationPath: readString(templateEnforcement.generationPath),
      visualLock: params.visualLock,
      editableCompilerProof: params.editableCompilerProof,
      visualDiffGate: params.visualDiffGate,
      visibleCompilerProof: params.visibleCompilerProof,
    },
    ontologyRetrievalTrace: {
      schemaVersion: FIGMA_TEMPLATE_ONTOLOGY_TRACE_SCHEMA_VERSION,
      status: "pass",
      source: params.readback.source,
      fallbackReason: params.readback.fallbackReason,
      job: params.readback.job,
      artifacts: params.readback.artifacts,
      visualLock:
        params.visualLock ??
        readRecord(params.readback.cogoIntermediate.visualLock),
      editableCompilerProof: params.editableCompilerProof,
      visualDiffGate: params.visualDiffGate,
      visibleCompilerProof: params.visibleCompilerProof,
    },
    ontologyCompilerContract: {
      schemaVersion: FIGMA_TEMPLATE_COMPILER_CONTRACT_SCHEMA_VERSION,
      enforced: true,
      source: "cloudflare-d1-r2-derived-readback",
      layoutAuthority:
        "cloudflare.r2.cogoIntermediate.pages[].components[].relativeLayout",
      styleAuthority:
        "cloudflare.r2.cogoIntermediate.pages[].components[].styleFacts",
      visualAuthority:
        "cloudflare.r2.cogoIntermediate.visualLock.pages[].dataUrl",
      editableAuthority:
        "cloudflare.r2.cogoIntermediate.pages[].components[]|static-studio-asset.editableComponents[]",
      visualDiffAuthority:
        "generated-root.backgroundImage === source-raster-visual-lock",
      primaryRenderAuthority:
        params.visibleCompilerProof.primaryRenderAuthority,
      commercializationReady:
        params.visibleCompilerProof.commercializationReady,
      llmMayOverrideLayout: false,
      llmMayOverrideStyle: false,
      llmMayOverrideVisualLock: false,
      llmMayOverrideEditableLayer: false,
      editableCompilerProof: params.editableCompilerProof,
      visualDiffGate: params.visualDiffGate,
      visibleCompilerProof: params.visibleCompilerProof,
    },
    reasons: [
      `Figma API rate limit recovered through ${params.readback.source}.`,
      ...(params.visualLock
        ? [
            `Figma visual lock enforced from ${params.visualLock.source} (${params.visualLock.pageCount} page asset(s)).`,
          ]
        : []),
      `Editable compiler proof ${params.editableCompilerProof.status}: ${params.editableCompilerProof.editableNodeCount} node(s), ${params.editableCompilerProof.interactiveNodeCount} interactive hitbox(es).`,
      `Source visual diff gate ${params.visualDiffGate.status}: diffRatio=${params.visualDiffGate.diffRatio ?? "pending"}.`,
      `Visible Coogo compiler proof ${params.visibleCompilerProof.status}: authority=${params.visibleCompilerProof.primaryRenderAuthority}, commercializationReady=${params.visibleCompilerProof.commercializationReady}.`,
    ],
  };
}

function applyCachedFigmaBundleRouteOverrides(params: {
  bundle: Record<string, unknown>;
  appId?: string;
  compiled: {
    visualLock?: CompiledVisualLockSummary;
  };
}): void {
  const manifest = readRecord(params.bundle.manifest);
  const sourceConversion = readRecord(manifest.sourceConversion);
  const studio = readRecord(manifest.studio);
  const appliedPage = params.compiled.visualLock?.appliedPage;
  const mobileOnly =
    (readNumber(appliedPage?.figmaWidth) ??
      readNumber(appliedPage?.width) ??
      0) <= 520;

  params.bundle.manifest = {
    ...manifest,
    ...(params.appId ? { appId: params.appId } : {}),
    ...(mobileOnly
      ? {
          sourceConversion: {
            ...sourceConversion,
            mobileOnly: true,
            defaultDevice: "mobile",
          },
          studio: {
            ...studio,
            defaultDevice: "mobile",
            defaultViewport: "mobile",
          },
        }
      : {}),
  };
}

function attachCompiledVisualLockToBundle(params: {
  bundle: Record<string, unknown>;
  visualLock?: CompiledVisualLockSummary;
}): void {
  if (!params.visualLock) return;
  params.bundle.visualLock = params.visualLock;
  params.bundle.metadata = {
    ...readRecord(params.bundle.metadata),
    visualLock: params.visualLock,
  };
}

function buildCachedFigmaImportBundle(params: {
  input: FigmaFrameImportInput;
  readback: FigmaOntologyReadbackResult;
  unsupportedNodeRef: string;
  appId?: string;
  renderMode?: CompileCachedIntermediateRootOptions["renderMode"];
  measuredVisualDiffGate?: CompileCachedIntermediateRootOptions["measuredVisualDiffGate"];
}): {
  compiled: ReturnType<typeof compileCachedIntermediateRoot>;
  mapping: FigmaFrameImportMapping;
  bundle: Record<string, unknown>;
} {
  const compiled = compileCachedIntermediateRoot(
    params.readback.cogoIntermediate,
    {
      ...(params.renderMode ? { renderMode: params.renderMode } : {}),
      ...(params.measuredVisualDiffGate
        ? { measuredVisualDiffGate: params.measuredVisualDiffGate }
        : {}),
    },
  );
  const ontologyValidation = cachedOntologyValidation(
    params.input,
    params.readback,
  );
  const referenceParity = cachedReferenceParity(
    params.readback,
    compiled.components,
  );
  const designTemplateApplication = cachedDesignTemplateApplication({
    input: params.input,
    readback: params.readback,
    frameName: compiled.frameName,
    tokenRefs: compiled.tokenRefs,
    components: compiled.components,
    visualLock: compiled.visualLock,
    editableCompilerProof: compiled.editableCompilerProof,
    visualDiffGate: compiled.visualDiffGate,
    visibleCompilerProof: compiled.visibleCompilerProof,
    ontologyValidation,
    referenceParity,
  });
  const unsupportedNodes = [params.unsupportedNodeRef];
  const matchedComponents = Array.from(
    new Set(
      compiled.components
        .map((component) => readString(component.cogoKind))
        .filter((kind): kind is string => Boolean(kind)),
    ),
  );
  const matchedNodes = compiled.components.length;
  const sourceFrame =
    sourceFrameSummaryFromCompiled({
      input: params.input,
      frameName: compiled.frameName,
      compiled,
    }) ??
    sourceFrameSummaryFromIntermediate({
      input: params.input,
      frameName: compiled.frameName,
      intermediate: params.readback.cogoIntermediate,
    });
  const mapping: FigmaFrameImportResult["mapping"] = {
    matchedNodes,
    unsupportedNodes,
    extractedTokens: compiled.tokenRefs,
    matchedComponents,
    componentMatchRate: matchedNodes > 0 ? 1 : 0,
    unsupportedNodeCount: 0,
    textNodeCount: compiled.components.filter((component) =>
      Boolean(readString(component.text)),
    ).length,
    nodeCount: matchedNodes,
    ontologyValidation,
    referenceParity,
    designTemplateApplication,
  };
  const bundle = figmaBundle({
    figmaFileId: params.input.figmaFileId,
    figmaNodeId: params.input.figmaNodeId,
    frameName: compiled.frameName,
    sourceFrame,
    root: compiled.root,
    mapping,
    tokens: {},
    designTemplateApplication,
  });
  applyCachedFigmaBundleRouteOverrides({
    bundle,
    appId: params.appId,
    compiled,
  });
  attachCompiledVisualLockToBundle({
    bundle,
    visualLock: compiled.visualLock,
  });

  return { compiled, mapping, bundle };
}

function cachedReadbackMatchesCurrentSelection(
  input: FigmaFrameImportInput,
  readback: FigmaOntologyReadbackResult,
): boolean {
  try {
    const enforcement = readRecord(
      readback.cogoIntermediate.templateEnforcement,
    );
    const evidence = readRecord(enforcement.selectionEvidence);
    const serverAuthority = readRecord(evidence.serverAuthority);
    const selectionIntentSha256 = readString(evidence.selectionIntentSha256);
    return (
      evidence.schemaVersion === "cogo.figma-template-selection-evidence.v1" &&
      evidence.selfScoringProhibited === true &&
      stableFigmaJson(readRecord(evidence.query)) ===
        stableFigmaJson(independentTemplateMatchQuery(input)) &&
      serverAuthority.schemaVersion ===
        "cogo.figma-template-selection-authority.v1" &&
      /^[a-f0-9]{64}$/.test(selectionIntentSha256 ?? "") &&
      readString(serverAuthority.selectionIntentSha256) ===
        selectionIntentSha256 &&
      serverAuthority.exactReplayPolicy === "idempotent-exact-reconciliation" &&
      serverAuthority.authorityBasis === "authenticated-server-issuance" &&
      evidence.confirmation === undefined &&
      evidence.confirmationAuthority === undefined &&
      serverAuthority.approvalId === undefined &&
      serverAuthority.bindingSha256 === undefined &&
      serverAuthority.principalId === undefined &&
      serverAuthority.singleUse === undefined
    );
  } catch {
    return false;
  }
}

async function importCachedFigmaOntologyReadback(params: {
  input: FigmaFrameImportInput;
  readback: FigmaOntologyReadbackResult;
  upstreamStatus: number;
  upstreamDetail: string;
}): Promise<FigmaFrameImportResult> {
  if (!cachedReadbackMatchesCurrentSelection(params.input, params.readback)) {
    throw new Error(
      "persisted Figma cache does not match the current template-selection intent and server authority",
    );
  }
  const storedBundle = readRecord(params.readback.cogoIntermediate.bundle);
  const storedManifest = readRecord(storedBundle.manifest);
  const storedPages = readArray<Record<string, unknown>>(storedManifest.pages);
  const sourceOntologyStorage = storageFromReadback(params.readback);
  assertFigmaOntologyStorageAcceptedBeforeMutation(sourceOntologyStorage);
  if (storedPages.length > 0) {
    const { compiled, mapping } = buildCachedFigmaImportBundle({
      input: params.input,
      readback: params.readback,
      unsupportedNodeRef: `figma-api-readback:${params.upstreamStatus}:${params.upstreamDetail}`,
    });
    const storedImport = readRecord(
      readRecord(storedBundle.metadata).cogoXyzFigmaImport,
    );
    const sourceFrames = readStoredSourceFrames(storedBundle);
    const sourceFrame =
      sourceFrames[0] ??
      sourceFrameSummaryFromCompiled({
        input: params.input,
        frameName: compiled.frameName,
        compiled,
      }) ??
      sourceFrameSummaryFromIntermediate({
        input: params.input,
        frameName: compiled.frameName,
        intermediate: params.readback.cogoIntermediate,
      });
    const sourceFrameSelection = readStoredSourceFrameSelection(
      storedImport.sourceFrameSelection,
    );
    const pageCount = storedPages.length;
    const actionFlowCount =
      readNumber(storedImport.actionFlowCount) ?? Math.max(0, pageCount - 1);
    const imported = await importCogoJsonAppInputLazy({
      title:
        readString(storedManifest.name) ??
        readString(storedPages[0]?.name) ??
        compiled.frameName,
      input: JSON.stringify(storedBundle),
    });

    return {
      ...imported,
      frameName:
        readString(storedManifest.name) ??
        readString(storedPages[0]?.name) ??
        compiled.frameName,
      sourceFrame,
      ...(sourceFrames.length > 0 ? { sourceFrames } : {}),
      pageCount,
      actionFlowCount,
      mapping: {
        ...mapping,
        matchedNodes:
          readNumber(storedImport.matchedNodes) ?? mapping.matchedNodes,
        unsupportedNodeCount:
          readNumber(storedImport.unsupportedNodeCount) ??
          mapping.unsupportedNodeCount,
        textNodeCount:
          readNumber(storedImport.textNodeCount) ?? mapping.textNodeCount,
        nodeCount: readNumber(storedImport.nodeCount) ?? mapping.nodeCount,
        sourceFrameSelection:
          sourceFrameSelection ?? mapping.sourceFrameSelection,
      },
      sourceOntologyStorage,
    };
  }
  const { compiled, mapping, bundle } = buildCachedFigmaImportBundle({
    input: params.input,
    readback: params.readback,
    unsupportedNodeRef: `figma-api-readback:${params.upstreamStatus}:${params.upstreamDetail}`,
  });
  const imported = await importCogoJsonAppInputLazy({
    title: compiled.frameName,
    input: JSON.stringify(bundle),
  });

  return {
    ...imported,
    frameName: compiled.frameName,
    sourceFrame:
      sourceFrameSummaryFromCompiled({
        input: params.input,
        frameName: compiled.frameName,
        compiled,
      }) ??
      sourceFrameSummaryFromIntermediate({
        input: params.input,
        frameName: compiled.frameName,
        intermediate: params.readback.cogoIntermediate,
      }),
    mapping,
    sourceOntologyStorage,
  };
}

export function buildStaticFigmaImportedCogoApplicationSnapshot(
  projectId: string,
  options: CompileCachedIntermediateRootOptions = {},
): StaticFigmaImportedCogoApplicationSnapshot | null {
  const target = STATIC_FIGMA_IMPORT_ROUTE_TARGETS[projectId];
  if (!target) return null;

  const readback = staticVisualLockReadbackForInput(
    target.input,
    "fresh-import-route-readback:static-visual-lock",
  );
  if (!readback) return null;

  const { compiled, mapping, bundle } = buildCachedFigmaImportBundle({
    input: target.input,
    readback,
    unsupportedNodeRef: `fresh-import-route-readback:${projectId}`,
    appId: target.appId,
    ...options,
  });
  const expectedProjectId = `import-${safeId(target.appId)}`;
  if (expectedProjectId !== projectId) return null;

  return {
    projectId,
    sourceName: target.sourceName,
    bundle,
    frameName: compiled.frameName,
    figmaFileId: target.input.figmaFileId,
    figmaNodeId: target.input.figmaNodeId,
    mapping,
  };
}

function inferFigmaPlatform(
  node: FigmaNode,
  input: FigmaFrameImportInput,
): "mobile" | "web" {
  const haystack = `${node.name ?? ""} ${input.figmaUrl ?? ""}`.toLowerCase();
  if (/\b(mobile|iphone|android|app)\b/.test(haystack)) return "mobile";
  const width = node.absoluteBoundingBox?.width;
  return typeof width === "number" && width > 0 && width <= 520
    ? "mobile"
    : "web";
}

interface FigmaImportDocumentSelection {
  document: FigmaNode;
  effectiveFigmaNodeId: string;
  frameName: string;
  platform: "mobile" | "web";
  sourceFrameSelection?: FigmaSourceFrameSelectionSummary;
}

function selectFigmaDocumentForImport(
  document: FigmaNode,
  input: FigmaFrameImportInput,
): FigmaImportDocumentSelection {
  const requestedNodeId = document.id ?? input.figmaNodeId;
  const requestedPlatform = inferFigmaPlatform(document, input);
  const candidates = collectMobileFrameCandidates(document);
  const selectedCandidate =
    requestedPlatform === "web" && isOversizedFigmaCanvas(document)
      ? candidates[0]
      : undefined;

  if (selectedCandidate) {
    const selectedNodeId = selectedCandidate.id ?? requestedNodeId;
    return {
      document: selectedCandidate,
      effectiveFigmaNodeId: selectedNodeId,
      frameName: selectedCandidate.name ?? `Figma frame ${selectedNodeId}`,
      platform: "mobile",
      sourceFrameSelection: {
        schemaVersion: "cogo.figma-source-frame-selection.v1",
        requestedNodeId,
        selectedNodeId,
        selectedFrameName:
          selectedCandidate.name ?? `Figma frame ${selectedNodeId}`,
        reason: "mobile-frame-inside-section",
        candidateCount: candidates.length,
      },
    };
  }

  return {
    document,
    effectiveFigmaNodeId: requestedNodeId,
    frameName: document.name ?? `Figma frame ${requestedNodeId}`,
    platform: requestedPlatform,
    sourceFrameSelection: {
      schemaVersion: "cogo.figma-source-frame-selection.v1",
      requestedNodeId,
      selectedNodeId: requestedNodeId,
      selectedFrameName: document.name ?? `Figma frame ${requestedNodeId}`,
      reason: "requested-node",
      candidateCount: candidates.length,
    },
  };
}

function isOversizedFigmaCanvas(node: FigmaNode): boolean {
  const width = readNumber(node.absoluteBoundingBox?.width) ?? 0;
  const height = readNumber(node.absoluteBoundingBox?.height) ?? 0;
  const type = `${node.type ?? ""}`.toUpperCase();
  return (
    (type === "SECTION" || type === "CANVAS" || type === "FRAME") &&
    (width > 720 || height > 1400)
  );
}

function collectMobileFrameCandidates(root: FigmaNode): FigmaNode[] {
  const frontier: FigmaNode[] = [];
  const visit = (node: FigmaNode): void => {
    if (isMobileFrameCandidate(node)) {
      frontier.push(node);
      return;
    }
    for (const child of node.children ?? []) visit(child);
  };
  visit(root);
  return frontier.sort(compareFigmaCanvasPosition);
}

function isMobileFrameCandidate(node: FigmaNode): boolean {
  if (`${node.type ?? ""}`.toUpperCase() !== "FRAME") return false;
  const width = readNumber(node.absoluteBoundingBox?.width);
  const height = readNumber(node.absoluteBoundingBox?.height);
  if (!width || !height) return false;
  if (width < 300 || width > 520) return false;
  if (height < 560 || height > 1200) return false;
  return readArray(node.children).length > 1;
}

function compareFigmaCanvasPosition(a: FigmaNode, b: FigmaNode): number {
  const ay = readNumber(a.absoluteBoundingBox?.y) ?? 0;
  const by = readNumber(b.absoluteBoundingBox?.y) ?? 0;
  if (ay !== by) return ay - by;
  const ax = readNumber(a.absoluteBoundingBox?.x) ?? 0;
  const bx = readNumber(b.absoluteBoundingBox?.x) ?? 0;
  return ax - bx;
}

function sourceFrameSummaryForFigmaNode(input: {
  document: FigmaNode;
  frameName: string;
  figmaFileId: string;
  figmaNodeId: string;
  platform: "mobile" | "web";
}): FigmaSourceFrameSummary | undefined {
  const width = readNumber(input.document.absoluteBoundingBox?.width);
  const height = readNumber(input.document.absoluteBoundingBox?.height);
  if (!width || !height) return undefined;
  return {
    schemaVersion: "cogo.figma-source-frame.v1",
    platform: input.platform,
    width,
    height,
    figmaFileId: input.figmaFileId,
    figmaNodeId: input.figmaNodeId,
    frameName: input.frameName,
  };
}

function sourceFrameSummaryFromCompiled(input: {
  input: FigmaFrameImportInput;
  frameName: string;
  compiled: ReturnType<typeof compileCachedIntermediateRoot>;
}): FigmaSourceFrameSummary | undefined {
  const appliedPage = input.compiled.visualLock?.appliedPage;
  const width =
    readNumber(appliedPage?.figmaWidth) ?? readNumber(appliedPage?.width);
  const height =
    readNumber(appliedPage?.figmaHeight) ?? readNumber(appliedPage?.height);
  if (!width || !height) return undefined;
  return {
    schemaVersion: "cogo.figma-source-frame.v1",
    platform: width <= 520 ? "mobile" : "web",
    width,
    height,
    figmaFileId: input.input.figmaFileId,
    figmaNodeId: input.input.figmaNodeId,
    frameName: input.frameName,
  };
}

function sourceFrameSummaryFromIntermediate(input: {
  input: FigmaFrameImportInput;
  frameName: string;
  intermediate: Record<string, unknown>;
}): FigmaSourceFrameSummary | undefined {
  const pages = readArray<Record<string, unknown>>(input.intermediate.pages);
  const page = pages[0] ?? {};
  const pageBox = readCachedBox(page.bbox);
  const width = readNumber(pageBox?.width);
  const height = readNumber(pageBox?.height);
  if (!width || !height) return undefined;
  return {
    schemaVersion: "cogo.figma-source-frame.v1",
    platform: width <= 520 ? "mobile" : "web",
    width,
    height,
    figmaFileId: input.input.figmaFileId,
    figmaNodeId: input.input.figmaNodeId,
    frameName: input.frameName,
  };
}

function summarizeOntologyValidation(
  report: FigmaTemplateLiveSourceMappingReport,
): FigmaFrameOntologyValidationSummary {
  const cases = report.mappingReport.cases;
  const qualityCases = cases
    .map((entry) => entry.qualityReadiness)
    .filter(
      (
        entry,
      ): entry is NonNullable<
        FigmaFrameOntologyValidationSummary["qualityReadiness"]
      > => Boolean(entry),
    );
  const failedQualityMetrics = qualityCases.flatMap(
    (entry) =>
      entry.metrics?.filter((metric) => metric.status === "fail") ?? [],
  );
  const averageQualityScore =
    qualityCases.length > 0
      ? Number(
          (
            qualityCases.reduce((sum, entry) => sum + (entry.score ?? 0), 0) /
            qualityCases.length
          ).toFixed(3),
        )
      : undefined;
  const qualityThreshold =
    qualityCases.find((entry) => typeof entry.threshold === "number")
      ?.threshold ?? undefined;
  const qualityScorePass =
    typeof averageQualityScore === "number" &&
    typeof qualityThreshold === "number"
      ? averageQualityScore >= qualityThreshold
      : true;
  return {
    schemaVersion: report.schemaVersion,
    status: report.status,
    sourceCount: report.sourceCount,
    admittedSourceCount: report.admittedSourceCount,
    blockedSourceCount: report.blockedSourceCount,
    admittedCaseCount: report.admittedCaseCount,
    sourceEvidence: report.sourceEvidence,
    mappingCaseCount: cases.length,
    nodeMappingCount: cases.reduce(
      (sum, entry) => sum + entry.nodeMappings.length,
      0,
    ),
    requirementMappingCount: cases.reduce(
      (sum, entry) => sum + entry.requirementMappings.length,
      0,
    ),
    chunkMappingCount: cases.reduce(
      (sum, entry) => sum + entry.chunkMappings.length,
      0,
    ),
    tokenMappingCount: cases.reduce(
      (sum, entry) => sum + entry.tokenMappings.length,
      0,
    ),
    actionMappingCount: cases.reduce(
      (sum, entry) => sum + entry.actionMappings.length,
      0,
    ),
    dataInputMappingCount: cases.reduce(
      (sum, entry) => sum + entry.dataInputMappings.length,
      0,
    ),
    hardConstraints: Array.from(
      new Set(cases.flatMap((entry) => entry.hardConstraints)),
    ),
    qualityReadiness:
      qualityCases.length > 0
        ? {
            schemaVersion: qualityCases[0].schemaVersion,
            status:
              failedQualityMetrics.length === 0 && qualityScorePass
                ? "pass"
                : "fail",
            score: averageQualityScore ?? 0,
            threshold: qualityThreshold ?? 0.82,
            metrics: qualityCases.flatMap((entry) => entry.metrics ?? []),
            evidenceLimits: Array.from(
              new Set(
                qualityCases.flatMap((entry) => entry.evidenceLimits ?? []),
              ),
            ),
          }
        : undefined,
  };
}

export async function buildFigmaFrameCogoBundleFromFigmaNodes(
  input: FigmaFrameImportInput & {
    prefetchedNodesResponse: FigmaApiNodesResponse | Record<string, unknown>;
  },
): Promise<FigmaFrameCogoBundleBuildResult> {
  const payload = readRecord(
    input.prefetchedNodesResponse as unknown as Record<string, unknown>,
  );
  const nodes = readRecord(payload.nodes);
  const nodeEntry = readRecord(
    nodes[input.figmaNodeId] ?? nodes[input.figmaNodeId.replace(/-/g, ":")],
  );
  let document = readRecord(nodeEntry.document) as FigmaNode;
  if (!document?.type) {
    const candidateIds = new Set([
      input.figmaNodeId,
      input.figmaNodeId.replace(/-/g, ":"),
      input.figmaNodeId.replace(/:/g, "-"),
    ]);
    for (const entry of Object.values(nodes)) {
      const root = readRecord(readRecord(entry).document) as FigmaNode;
      const match = root?.type
        ? collectNodes(root).find(
            (node) => typeof node.id === "string" && candidateIds.has(node.id),
          )
        : undefined;
      if (match) {
        document = match;
        break;
      }
    }
  }
  if (!document || !document.type) {
    throw new Error(
      `Figma node not found: ${input.figmaFileId}:${input.figmaNodeId}`,
    );
  }

  const selection = selectFigmaDocumentForImport(document, input);
  document = selection.document;
  const platform = selection.platform;
  const effectiveFigmaNodeId = selection.effectiveFigmaNodeId;
  const frameName = selection.frameName;
  const ontologyValidation = summarizeOntologyValidation(
    await buildFigmaTemplateLiveSourceMappingProof({
      sources: [
        {
          id: safeId(`figma-${input.figmaFileId}-${effectiveFigmaNodeId}`),
          title: frameName,
          url: input.figmaUrl,
          fileKey: input.figmaFileId,
          nodeId: effectiveFigmaNodeId,
          tier: platform === "mobile" ? "mobile-simple" : "web-landing",
          platform,
          intent: [
            `Import Figma frame ${frameName} as a Coogo application.`,
            input.audience ? `Audience: ${input.audience}.` : "",
            input.tags?.length ? `Tags: ${input.tags.join(", ")}.` : "",
          ]
            .filter(Boolean)
            .join(" "),
          response: payload as FigmaApiNodesResponse,
        },
      ],
    }),
  );

  const allNodes = collectNodes(document);
  const initialUnsupportedNodes = collectUnsupportedNodeRefs(document);
  const extractedTokens = Array.from(
    new Set(
      allNodes.flatMap((node) => [
        ...(node.fills ?? [])
          .map(colorTokenFromPaint)
          .filter((token): token is string => Boolean(token)),
        ...(node.strokes ?? [])
          .map(colorTokenFromPaint)
          .filter((token): token is string => Boolean(token)),
        ...typographyTokens(node),
        ...radiusTokens(node),
        ...shadowTokens(node),
      ]),
    ),
  );
  const sourceFrame = sourceFrameSummaryForFigmaNode({
    document,
    frameName,
    figmaFileId: input.figmaFileId,
    figmaNodeId: effectiveFigmaNodeId,
    platform,
  });
  const tokenTheme = buildFigmaTokenTheme({ document, allNodes });
  const provisionalDesignTemplateApplication = buildDesignTemplateApplication({
    figmaFileId: input.figmaFileId,
    figmaNodeId: effectiveFigmaNodeId,
    frameName,
    document,
    allNodes,
    ontologyValidation,
    unsupportedNodeCount: initialUnsupportedNodes.length,
    tokenRefs: tokenTheme.tokenRefs,
    styleSignature: tokenTheme.styleSignature,
  });
  const provisionalRoot = convertNode(
    document,
    "figma-root",
    [...initialUnsupportedNodes],
    provisionalDesignTemplateApplication,
  ) ?? {
    id: "figma-root",
    type: "container",
    children: [
      { id: "figma-empty", type: "text", text: document.name ?? "Figma frame" },
    ],
  };
  const referenceParity = buildFigmaTemplateReferenceParity(
    figmaApiNodeToTemplateNode(document as FigmaApiNode),
    provisionalRoot as FigmaTemplateCompiledNode,
  );
  const designTemplateApplication = buildDesignTemplateApplication({
    figmaFileId: input.figmaFileId,
    figmaNodeId: effectiveFigmaNodeId,
    frameName,
    document,
    allNodes,
    ontologyValidation,
    unsupportedNodeCount: initialUnsupportedNodes.length,
    tokenRefs: tokenTheme.tokenRefs,
    styleSignature: tokenTheme.styleSignature,
    referenceParity,
  });
  const matchedComponents = Array.from(
    new Set(
      allNodes
        .map(componentKindForNode)
        .filter((kind): kind is string => Boolean(kind)),
    ),
  );
  const unsupportedNodes = [...initialUnsupportedNodes];
  const root = convertNode(
    document,
    "figma-root",
    unsupportedNodes,
    designTemplateApplication,
  ) ?? {
    id: "figma-root",
    type: "container",
    children: [
      { id: "figma-empty", type: "text", text: document.name ?? "Figma frame" },
    ],
  };
  const matchedNodes = allNodes.length - unsupportedNodes.length;
  const unsupportedNodeCount = unsupportedNodes.length;
  const mapping = {
    matchedNodes,
    unsupportedNodes,
    extractedTokens,
    matchedComponents,
    componentMatchRate:
      allNodes.length > 0
        ? Number((matchedNodes / allNodes.length).toFixed(3))
        : 0,
    unsupportedNodeCount,
    textNodeCount: allNodes.filter((node) => node.type === "TEXT").length,
    nodeCount: allNodes.length,
    sourceFrameSelection: selection.sourceFrameSelection,
    ontologyValidation,
    referenceParity,
    designTemplateApplication,
  };
  const bundle = figmaBundle({
    figmaFileId: input.figmaFileId,
    figmaNodeId: effectiveFigmaNodeId,
    frameName,
    sourceFrame,
    root,
    mapping,
    tokens: tokenTheme.designSystemTokens,
    designTemplateApplication,
  });

  return { frameName, sourceFrame, mapping, bundle };
}

export async function importFigmaFrameAsCogoApp(
  input: FigmaFrameImportInput,
): Promise<FigmaFrameImportResult> {
  if (
    shouldUseFigmaProxy() &&
    (Boolean(readString(input.figmaAccessToken)) ||
      !readString(input.workspaceId) ||
      !readString(input.figmaTokenRef))
  ) {
    throw new Error(
      "production Figma import requires workspaceId and a server-stored figmaTokenRef; raw tokens are not accepted",
    );
  }
  let payload: Record<string, unknown>;
  if (input.prefetchedNodesResponse) {
    // Offline path: convert from an already-fetched nodes response (fixture / CI).
    // No token resolution here; prefetched payloads must stay fixture-driven.
    payload = readRecord(
      input.prefetchedNodesResponse as unknown as Record<string, unknown>,
    );
  } else {
    const token = resolveFigmaToken(input.figmaAccessToken);
    const response = await fetchFigmaNode(input, token);
    if (!response.ok) {
      const detail = await readFigmaApiError(response);
      const fallbackReason = `figma-api-${response.status}:${detail}`;
      const readback =
        input.sourceRevision && input.sourceSha256
          ? await requestFigmaOntologyReadback({
              projectId: input.projectId,
              figmaFileId: input.figmaFileId,
              figmaNodeId: input.figmaNodeId,
              sourceRevision: input.sourceRevision,
              sourceSha256: input.sourceSha256,
              fallbackReason,
            })
          : undefined;
      if (readback) {
        return importCachedFigmaOntologyReadback({
          input,
          readback,
          upstreamStatus: response.status,
          upstreamDetail: detail,
        });
      }
      throw new Error(
        `Figma frame read failed (${response.status}: ${detail}) for ${input.figmaFileId}:${input.figmaNodeId}`,
      );
    }
    payload = readRecord(await response.json());
  }
  const sourceIdentity = await exactFigmaSourceIdentity(payload);
  input = { ...input, ...sourceIdentity };
  if (!input.prefetchedNodesResponse) {
    const persistedReadback = await requestFigmaOntologyReadback({
      projectId: input.projectId,
      figmaFileId: input.figmaFileId,
      figmaNodeId: input.figmaNodeId,
      sourceRevision: input.sourceRevision,
      sourceSha256: input.sourceSha256,
      fallbackReason: "exact-live-source-reuse",
    });
    if (
      persistedReadback &&
      cachedReadbackMatchesCurrentSelection(input, persistedReadback)
    ) {
      return importCachedFigmaOntologyReadback({
        input,
        readback: persistedReadback,
        upstreamStatus: 200,
        upstreamDetail: "exact-live-source-reuse",
      });
    }
  }
  const nodes = readRecord(payload.nodes);
  // Figma /nodes payloads are keyed by colon-form ids (e.g. "1:41"); a URL-derived id may be
  // hyphenated ("1-41"). Try the id as-is, then the colon-normalized form.
  const nodeEntry = readRecord(
    nodes[input.figmaNodeId] ?? nodes[input.figmaNodeId.replace(/-/g, ":")],
  );
  let document = readRecord(nodeEntry.document) as FigmaNode;
  if (!document || !document.type) {
    throw new Error(
      `Figma node not found: ${input.figmaFileId}:${input.figmaNodeId}`,
    );
  }
  const multiFrameCandidates = collectMobileFrameCandidates(document);
  const shouldBuildMultiFrameApplication =
    inferFigmaPlatform(document, input) === "web" &&
    isOversizedFigmaCanvas(document) &&
    multiFrameCandidates.length > 1;
  if (shouldBuildMultiFrameApplication) {
    const requestedNodeId = document.id ?? input.figmaNodeId;
    const frameBuilds = await Promise.all(
      multiFrameCandidates.map(async (candidate) => {
        const selectedNodeId = candidate.id ?? input.figmaNodeId;
        const build = await buildFigmaFrameCogoBundleFromFigmaNodes({
          ...input,
          figmaNodeId: selectedNodeId,
          prefetchedNodesResponse: payload as FigmaApiNodesResponse,
        });
        const selectedFrameName =
          candidate.name ?? build.frameName ?? `Figma frame ${selectedNodeId}`;
        build.mapping = {
          ...build.mapping,
          sourceFrameSelection: {
            schemaVersion: "cogo.figma-source-frame-selection.v1",
            requestedNodeId,
            selectedNodeId,
            selectedFrameName,
            reason: "mobile-frame-inside-section",
            candidateCount: multiFrameCandidates.length,
          },
        };
        return {
          ...build,
          frameName: selectedFrameName,
        };
      }),
    );
    const sourceName = document.name ?? frameBuilds[0]?.frameName ?? "Figma";
    const sourceFrames = frameBuilds
      .map((build) => build.sourceFrame)
      .filter((frame): frame is FigmaSourceFrameSummary => Boolean(frame));
    const aggregateSelection: FigmaSourceFrameSelectionSummary = {
      schemaVersion: "cogo.figma-source-frame-selection.v1",
      requestedNodeId,
      selectedNodeId:
        frameBuilds[0]?.sourceFrame?.figmaNodeId ?? requestedNodeId,
      selectedFrameName: frameBuilds[0]?.frameName ?? sourceName,
      reason: "mobile-frame-inside-section",
      candidateCount: multiFrameCandidates.length,
    };
    const aggregateMapping = aggregateFigmaFrameMappings({
      builds: frameBuilds,
      sourceFrameSelection: aggregateSelection,
    });
    const multiFrameBundle = buildMultiFrameFigmaApplicationBundle({
      sourceName,
      figmaFileId: input.figmaFileId,
      requestedFigmaNodeId: input.figmaNodeId,
      frameBuilds,
      aggregateMapping,
    });
    const ontologyImportPayload = buildMultiFrameFigmaOntologyImportPayload({
      sourceInput: input,
      payload,
      document,
      frameName: sourceName,
      mapping: aggregateMapping,
      bundle: multiFrameBundle.bundle,
      frameBuilds,
    });
    const rootStorage = await requestFigmaOntologyImport({
      input,
      payload,
      document,
      frameName: sourceName,
      mapping: aggregateMapping,
      bundle: multiFrameBundle.bundle,
      importPayload: ontologyImportPayload,
    });
    assertFigmaOntologyStorageAcceptedBeforeMutation(rootStorage);
    const imported = await importCogoJsonAppInputLazy({
      title: sourceName,
      input: JSON.stringify(multiFrameBundle.bundle),
    });

    return {
      ...imported,
      frameName: sourceName,
      sourceFrame: sourceFrames[0],
      sourceFrames,
      pageCount: multiFrameBundle.sourceFrames.length,
      actionFlowCount: multiFrameBundle.actionFlowCount,
      mapping: aggregateMapping,
      sourceOntologyStorage: aggregateFigmaSourceOntologyStorage({
        rootStorage,
        sourceFrames,
      }),
    };
  }
  const selection = selectFigmaDocumentForImport(document, input);
  document = selection.document;
  const platform = selection.platform;
  const effectiveFigmaNodeId = selection.effectiveFigmaNodeId;
  const frameName = selection.frameName;
  const ontologyValidation = summarizeOntologyValidation(
    await buildFigmaTemplateLiveSourceMappingProof({
      sources: [
        {
          id: safeId(`figma-${input.figmaFileId}-${effectiveFigmaNodeId}`),
          title: frameName,
          url: input.figmaUrl,
          fileKey: input.figmaFileId,
          nodeId: effectiveFigmaNodeId,
          tier: platform === "mobile" ? "mobile-simple" : "web-landing",
          platform,
          intent: [
            `Import Figma frame ${frameName} as a Coogo application.`,
            input.audience ? `Audience: ${input.audience}.` : "",
            input.tags?.length ? `Tags: ${input.tags.join(", ")}.` : "",
          ]
            .filter(Boolean)
            .join(" "),
          response: payload as FigmaApiNodesResponse,
        },
      ],
    }),
  );

  const allNodes = collectNodes(document);
  const initialUnsupportedNodes = collectUnsupportedNodeRefs(document);
  const extractedTokens = Array.from(
    new Set(
      allNodes.flatMap((node) => [
        ...(node.fills ?? [])
          .map(colorTokenFromPaint)
          .filter((token): token is string => Boolean(token)),
        ...(node.strokes ?? [])
          .map(colorTokenFromPaint)
          .filter((token): token is string => Boolean(token)),
        ...typographyTokens(node),
        ...radiusTokens(node),
        ...shadowTokens(node),
      ]),
    ),
  );
  const sourceFrame = sourceFrameSummaryForFigmaNode({
    document,
    frameName,
    figmaFileId: input.figmaFileId,
    figmaNodeId: effectiveFigmaNodeId,
    platform,
  });
  const tokenTheme = buildFigmaTokenTheme({ document, allNodes });
  const provisionalDesignTemplateApplication = buildDesignTemplateApplication({
    figmaFileId: input.figmaFileId,
    figmaNodeId: effectiveFigmaNodeId,
    frameName,
    document,
    allNodes,
    ontologyValidation,
    unsupportedNodeCount: initialUnsupportedNodes.length,
    tokenRefs: tokenTheme.tokenRefs,
    styleSignature: tokenTheme.styleSignature,
  });
  const provisionalRoot = convertNode(
    document,
    "figma-root",
    [...initialUnsupportedNodes],
    provisionalDesignTemplateApplication,
  ) ?? {
    id: "figma-root",
    type: "container",
    children: [
      { id: "figma-empty", type: "text", text: document.name ?? "Figma frame" },
    ],
  };
  const referenceParity = buildFigmaTemplateReferenceParity(
    figmaApiNodeToTemplateNode(document as FigmaApiNode),
    provisionalRoot as FigmaTemplateCompiledNode,
  );
  const designTemplateApplication = buildDesignTemplateApplication({
    figmaFileId: input.figmaFileId,
    figmaNodeId: effectiveFigmaNodeId,
    frameName,
    document,
    allNodes,
    ontologyValidation,
    unsupportedNodeCount: initialUnsupportedNodes.length,
    tokenRefs: tokenTheme.tokenRefs,
    styleSignature: tokenTheme.styleSignature,
    referenceParity,
  });
  const matchedComponents = Array.from(
    new Set(
      allNodes
        .map(componentKindForNode)
        .filter((kind): kind is string => Boolean(kind)),
    ),
  );
  const unsupportedNodes = [...initialUnsupportedNodes];
  const root = convertNode(
    document,
    "figma-root",
    unsupportedNodes,
    designTemplateApplication,
  ) ?? {
    id: "figma-root",
    type: "container",
    children: [
      { id: "figma-empty", type: "text", text: document.name ?? "Figma frame" },
    ],
  };
  const matchedNodes = allNodes.length - unsupportedNodes.length;
  const unsupportedNodeCount = unsupportedNodes.length;
  const mapping = {
    matchedNodes,
    unsupportedNodes,
    extractedTokens,
    matchedComponents,
    componentMatchRate:
      allNodes.length > 0
        ? Number((matchedNodes / allNodes.length).toFixed(3))
        : 0,
    unsupportedNodeCount,
    textNodeCount: allNodes.filter((node) => node.type === "TEXT").length,
    nodeCount: allNodes.length,
    sourceFrameSelection: selection.sourceFrameSelection,
    ontologyValidation,
    referenceParity,
    designTemplateApplication,
  };
  const bundle = figmaBundle({
    figmaFileId: input.figmaFileId,
    figmaNodeId: effectiveFigmaNodeId,
    frameName,
    sourceFrame,
    root,
    mapping,
    tokens: tokenTheme.designSystemTokens,
    designTemplateApplication,
  });
  const ontologyImportPayload = buildFigmaOntologyImportPayload({
    input,
    payload,
    document,
    frameName,
    mapping,
    bundle,
  });
  const visibleAssemblyReadback = liveImportPayloadReadback(
    input,
    ontologyImportPayload,
  );
  const visibleAssembly = visibleAssemblyReadback
    ? buildCachedFigmaImportBundle({
        input,
        readback: visibleAssemblyReadback,
        unsupportedNodeRef: "figma-live-source-intermediate",
        renderMode: "visible-cogo-candidate",
      })
    : null;
  const importedBundle = visibleAssembly?.bundle ?? bundle;
  const importedMapping = visibleAssembly?.mapping
    ? {
        ...visibleAssembly.mapping,
        sourceFrameSelection: mapping.sourceFrameSelection,
      }
    : mapping;
  const payloadIntermediate = readRecord(
    ontologyImportPayload.cogoIntermediate,
  );
  if (payloadIntermediate && visibleAssembly) {
    payloadIntermediate.bundle = importedBundle;
  }
  const sourceOntologyStorage = await requestFigmaOntologyImport({
    input,
    payload,
    document,
    frameName,
    mapping,
    bundle,
    importPayload: ontologyImportPayload,
  });
  assertFigmaOntologyStorageAcceptedBeforeMutation(sourceOntologyStorage);
  const imported = await importCogoJsonAppInputLazy({
    title: frameName,
    input: JSON.stringify(importedBundle),
  });

  return {
    ...imported,
    frameName,
    sourceFrame,
    mapping: importedMapping,
    sourceOntologyStorage,
  };
}

export const __figmaImportTesting = {
  buildFigmaOntologyImportPayload,
  buildMultiFrameFigmaOntologyImportPayload,
  buildMultiFrameFigmaApplicationBundle,
  collectMobileFrameCandidates,
  postFigmaOntologyImport,
  requestFigmaOntologyReadback,
  postFigmaOntologyReadback,
  storageFromReadback,
  buildCachedFigmaImportBundle,
  compileCachedIntermediateRoot,
  staticVisualLockReadbackForInput,
  staticVisualLocks: STATIC_FIGMA_VISUAL_LOCKS,
};
